/**
 * ui.js — NexChat UI Renderer
 *
 * Builds and updates DOM. Stateless rendering functions — state lives in nexchat.js.
 * All DOM manipulation is centralised here.
 */

import {
  getRooms, getRoom, getRoomName, isDmRoom, getDmPartner,
  isEncryptedRoom, getUnreadCount, getTimeline, formatMessage,
  getMyUserId, getDisplayName, mxcToHttp, getUserAvatarUrl, getRoomAvatarUrl, decryptAndBlobUrl,
} from './matrix.js';

/* ──────────────────────────────────────────────
   SVG icon library (inline, zero deps)
─────────────────────────────────────────────── */
export const Icons = {
  chat: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>`,
  hash: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/>
    <line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
  </svg>`,
  lock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>`,
  plus: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>`,
  send: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>`,
  search: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>`,
  settings: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>`,
  logout: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>`,
  users: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>`,
  check: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>`,
  checkDouble: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="17 1 9 17 4 12"/><polyline points="23 1 15 17 10 12"/>
  </svg>`,
  wifi_off: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="1" y1="1" x2="23" y2="23"/>
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
    <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/>
  </svg>`,
  nexchat_logo: `<svg viewBox="0 0 22 22" fill="none">
    <path d="M3 4h16v9a2 2 0 01-2 2H7l-4 3V4z" fill="currentColor" opacity="0.15"/>
    <path d="M3 4h16v9a2 2 0 01-2 2H7l-4 3V4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <circle cx="8" cy="9" r="1.2" fill="currentColor"/>
    <circle cx="11" cy="9" r="1.2" fill="currentColor"/>
    <circle cx="14" cy="9" r="1.2" fill="currentColor"/>
  </svg>`,
  nexai: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg>`,
  reply: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>`,
};

/* ──────────────────────────────────────────────
   Avatar helpers
─────────────────────────────────────────────── */
const AVATAR_COLORS = [
  '#6c3ef4', '#0ea5e9', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#8b5cf6', '#14b8a6',
];

function getAvatarColor(text) {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function createAvatarElement(name, size = 36, isDm = false, imageUrl = null) {
  const el = document.createElement('div');
  el.className = `room-avatar${isDm ? ' dm' : ''}`;
  el.style.cssText = `width:${size}px;height:${size}px;min-width:${size}px;background:${getAvatarColor(name || '?')};font-size:${Math.max(10, size * 0.36)}px;`;
  if (imageUrl) {
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.cssText = `width:100%;height:100%;object-fit:cover;border-radius:inherit;`;
    img.onerror = () => { img.remove(); };
    el.appendChild(img);
  } else {
    const span = document.createElement('span');
    span.className = 'room-avatar-text';
    span.textContent = getInitials(name);
    el.appendChild(span);
  }
  return el;
}


/* ──────────────────────────────────────────────
   Async avatar loader for message groups
─────────────────────────────────────────────── */
async function loadMsgGroupAvatar(avatarEl, userId) {
  if (!avatarEl) return;
  try {
    const mxc = await getUserAvatarUrl(userId);
    if (!mxc) return;
    const url = mxcToHttp(mxc);
    if (!url) return;
    // Remove initials text
    avatarEl.innerHTML = '';
    // Add image
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:inherit;display:block;';
    img.onerror = () => { img.remove(); };
    avatarEl.appendChild(img);
  } catch (_) {}
}

/* ──────────────────────────────────────────────
   Auth Screen
─────────────────────────────────────────────── */
export function renderAuthScreen(onLoginClick) {
  const root = document.getElementById('app');
  root.innerHTML = '';

  const screen = document.createElement('div');
  screen.className = 'auth-screen';
  screen.innerHTML = `
    <div class="auth-card">
      <div class="auth-logo">
        <div class="auth-logo-icon">${Icons.nexchat_logo}</div>
        <span class="auth-logo-text">NexChat</span>
        <span class="auth-logo-badge">Beta</span>
      </div>
      <h1 class="auth-title">${t('auth.welcome')}</h1>
      <p class="auth-subtitle">${t('auth.subtitle')}</p>
      <div id="auth-error" class="auth-error"></div>
      <button class="btn btn-primary" id="login-btn">
        <span id="login-btn-content">${t('auth.continue')}</span>
      </button>
      <div class="auth-server-hint">
        Connected to <span>nexchat.ethosserver.com</span>
      </div>
    </div>
  `;

  root.appendChild(screen);

  document.getElementById('login-btn').addEventListener('click', async () => {
    const btn = document.getElementById('login-btn');
    const content = document.getElementById('login-btn-content');
    btn.disabled = true;
    content.innerHTML = `<div class="spinner"></div> Connecting...`;
    try {
      await onLoginClick();
    } catch (err) {
      btn.disabled = false;
      content.textContent = t('auth.continue');
      showAuthError(err.message);
    }
  });
}

export function showAuthError(msg) {
  const el = document.getElementById('auth-error');
  if (el) { el.textContent = msg; el.classList.add('visible'); }
}

/* ──────────────────────────────────────────────
   Loading Screen
─────────────────────────────────────────────── */
export function renderLoadingScreen(message = 'Connecting…') {
  const root = document.getElementById('app');
  root.innerHTML = `
    <div class="auth-screen">
      <div style="display:flex;flex-direction:column;align-items:center;gap:16px;">
        <div class="auth-logo-icon" style="width:56px;height:56px;border-radius:16px;">
          <div style="transform:scale(1.4)">${Icons.nexchat_logo}</div>
        </div>
        <div style="display:flex;align-items:center;gap:10px;color:var(--text-secondary);font-size:14px;">
          <div class="spinner" style="border-color:rgba(108,62,244,0.3);border-top-color:#6c3ef4;"></div>
          <span class="pulse">${message}</span>
        </div>
      </div>
    </div>
  `;
}

/* ──────────────────────────────────────────────
   Main App Shell
─────────────────────────────────────────────── */
export function renderAppShell(userName, userId, onNewRoom, onLogout, onProfileClick, onEditProfile, onShowScheduled) {
  const root = document.getElementById('app');
  root.innerHTML = `
    <div class="app-layout">
      <!-- Sidebar -->
      <aside class="sidebar">
        <div class="sidebar-header">
          <div class="sidebar-brand">
            <div class="sidebar-brand-name">
              <div class="sidebar-brand-icon">${Icons.nexchat_logo}</div>
              NexChat
            </div>
            <div class="sidebar-actions">
              <button class="icon-btn" id="new-room-btn" title="${t('sidebar.new_conversation')}">${Icons.plus}</button>
            </div>
          </div>
          <div class="search-box">
            ${Icons.search}
            <input type="text" class="search-input" id="room-search" placeholder="${t('sidebar.search')}" />
          </div>
        </div>

        <!-- Stories Bar -->
        <div class="stories-bar" id="stories-bar">
          <div class="stories-scroll" id="stories-scroll">
            <div class="story-add-btn" id="story-add-btn" title="Add story">
              <div class="story-avatar-ring story-own-ring">
                <div class="story-avatar-inner" id="my-story-avatar">+</div>
              </div>
              <span class="story-name">My Story</span>
            </div>
          </div>
        </div>

        <!-- Room List -->
        <div class="room-list" id="room-list">
          <div class="loading-state">
            <div class="spinner" style="border-color:rgba(108,62,244,0.3);border-top-color:#6c3ef4;width:18px;height:18px;"></div>
            <span class="pulse">${t('sidebar.loading')}</span>
          </div>
        </div>

        <!-- User Profile -->
        <div class="sidebar-footer">
          <div style="position:relative;">
            <div id="profile-dropdown" class="profile-dropdown">
              <div class="dropdown-item" onclick="setPresence('online',this)">
                <span style="width:10px;height:10px;border-radius:50%;background:#10b981;flex-shrink:0;display:inline-block;"></span>
                <span>Online</span>
              </div>
              <div class="dropdown-item" onclick="setPresence('unavailable',this)">
                <span style="width:10px;height:10px;border-radius:50%;background:#f59e0b;flex-shrink:0;display:inline-block;"></span>
                <span>Away</span>
              </div>
              <div class="dropdown-item" onclick="setPresence('busy',this)">
                <span style="width:10px;height:10px;border-radius:50%;background:#ef4444;flex-shrink:0;display:inline-block;"></span>
                <span>Busy</span>
              </div>
              <div class="dropdown-item" onclick="setPresence('offline',this)">
                <span style="width:10px;height:10px;border-radius:50%;background:#6b7280;flex-shrink:0;display:inline-block;"></span>
                <span>Appear Offline</span>
              </div>
              <div class="dropdown-divider" style="height:1px;background:var(--border);margin:4px 8px;"></div>
              <div class="dropdown-item" id="emoji-status-btn">
                <span id="current-emoji-status-icon">💬</span>
                <span id="current-emoji-status-text" style="color:var(--text-muted);font-size:12px;">Set emoji status…</span>
              </div>
              <div class="dropdown-item" id="notif-settings-btn">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
                Notification Settings
              </div>
              <div class="dropdown-item" id="edit-profile-btn">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                Edit Profile
              </div>
              <div class="dropdown-item" id="theme-picker-btn">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 1 0 20"/><path d="M12 2v20M2 12h20"/></svg>
                Theme
              </div>
              <div class="dropdown-item" id="language-picker-btn">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/></svg>
                Language
              </div>
              <div class="dropdown-item" id="scheduled-msgs-btn">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Scheduled Messages
              </div>
              <div class="dropdown-item" id="working-hours-btn">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                <span>Working Hours</span>
                <span id="dnd-active-badge" style="display:none;margin-left:auto;font-size:10px;background:var(--accent);color:#fff;padding:1px 6px;border-radius:10px;">DND</span>
              </div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-item danger" id="logout-btn">
                ${Icons.logout} Sign out
              </div>
            </div>
            <div class="user-profile" id="user-profile-btn">
              <div class="user-avatar" id="user-avatar-el">
                ${getInitials(userName)}
                <div class="user-avatar-presence"></div>
              </div>
              <div class="user-info">
                <div class="user-name" id="user-display-name">${escHtml(userName)}</div>
                <div class="user-status">${escHtml(userId)}</div>
                <div id="user-status-emoji" style="font-size:11px;color:var(--accent-text);margin-top:1px;"></div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- Chat Area -->
      <main class="chat-area" id="chat-area">
        <div class="no-room-selected">
          <div class="no-room-selected-icon">${Icons.chat}</div>
          <h2>${t('sidebar.conversations')}</h2>
          <p>${t('sidebar.select')}</p>
          <div class="empty-state-actions">
            <button class="btn btn-ghost" id="welcome-new-room-btn">
              ${Icons.plus} New conversation
            </button>
          </div>
        </div>
      </main>

      <!-- Toast container -->
      <div class="toast-container" id="toast-container"></div>
    </div>
  `;

  // Wire up user avatar color
  const avatarEl = document.getElementById('user-avatar-el');
  if (avatarEl) {
    avatarEl.style.background = getAvatarColor(userName);
    const initSpan = avatarEl.childNodes[0];
    if (initSpan?.nodeType === Node.TEXT_NODE) initSpan.textContent = getInitials(userName);
  }

  // Profile dropdown toggle
  const profileBtn = document.getElementById('user-profile-btn');
  const dropdown   = document.getElementById('profile-dropdown');


  profileBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('visible');
  });
  document.addEventListener('click', () => dropdown?.classList.remove('visible'));

  // Edit profile
  document.getElementById('notif-settings-btn')?.addEventListener('click', () => {
    dropdown?.classList.remove('visible');
    const getS = () => { try { return JSON.parse(localStorage.getItem('nx_notif_settings') || '{}'); } catch(_) { return {}; } };
    const saveS = (s) => localStorage.setItem('nx_notif_settings', JSON.stringify(s));
    const reqPerm = async () => {
      if (!('Notification' in window)) return false;
      if (Notification.permission === 'granted') return true;
      if (Notification.permission === 'denied') return false;
      return (await Notification.requestPermission()) === 'granted';
    };
    showNotifSettings(getS, saveS, reqPerm);
  });

  document.getElementById('edit-profile-btn')?.addEventListener('click', () => {
    dropdown?.classList.remove('visible');
    if (onEditProfile) onEditProfile();
  });

  // Theme switcher button
  document.getElementById('theme-picker-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.remove('visible');
    showThemePicker();
  });

  document.getElementById('language-picker-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.remove('visible');
    showLanguagePicker();
  });

  document.getElementById('emoji-status-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.remove('visible');
    showEmojiStatusModal((status) => {
      _updateEmojiStatusDisplay(status);
      // Dispatch event so nexchat.js can call setEmojiStatus + setPresence
      window.dispatchEvent(new CustomEvent('nx:set-emoji-status', { detail: { status } }));
    });
  });

  // Logout

  // Scheduled messages
  document.getElementById('scheduled-msgs-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.remove('visible');
    if (onShowScheduled) onShowScheduled();
  });
  document.getElementById('logout-btn')?.addEventListener('click', onLogout);

  // New room
  document.getElementById('new-room-btn')?.addEventListener('click', onNewRoom);
  document.getElementById('welcome-new-room-btn')?.addEventListener('click', onNewRoom);

  // Room search
  document.getElementById('room-search')?.addEventListener('input', (e) => {
    filterRoomList(e.target.value.toLowerCase());
  });
}

/* ──────────────────────────────────────────────
   Room List Rendering
─────────────────────────────────────────────── */
export function renderRoomList(rooms, activeRoomId, onRoomSelect) {
  const container = document.getElementById('room-list');
  if (!container) return;

  if (!rooms.length) {
    container.innerHTML = `
      <div class="empty-state" style="padding:32px 16px;">
        <div class="empty-state-icon" style="width:48px;height:48px;">${Icons.chat}</div>
        <p style="font-size:13px;">${t('sidebar.empty')}</p>
      </div>
    `;
    return;
  }

  // Separate DMs from channels
  const dms       = rooms.filter(r => isDmRoom(r));
  const channels  = rooms.filter(r => !isDmRoom(r));

  container.innerHTML = '';

  if (dms.length) {
    container.appendChild(buildRoomSection('Direct Messages', dms, activeRoomId, onRoomSelect));
  }
  if (channels.length) {
    container.appendChild(buildRoomSection('Channels', channels, activeRoomId, onRoomSelect));
  }
}

function buildRoomSection(title, rooms, activeRoomId, onRoomSelect) {
  const section = document.createElement('div');
  section.className = 'room-list-section';

  const header = document.createElement('div');
  header.className = 'room-list-section-header';
  header.textContent = title;
  section.appendChild(header);

  for (const room of rooms) {
    section.appendChild(buildRoomItem(room, room.roomId === activeRoomId, onRoomSelect));
  }

  return section;
}

function buildRoomItem(room, isActive, onRoomSelect) {
  const isDm     = isDmRoom(room);
  const partner  = isDm ? getDmPartner(room) : null;
  const name     = isDm && partner ? (partner.name || getDisplayName(partner.userId)) : getRoomName(room);
  const unread   = getUnreadCount(room);
  const encrypted = isEncryptedRoom(room);

  // Get last message preview
  const timeline = room.getLiveTimeline().getEvents();
  const lastMsg  = [...timeline].reverse().find(e => e.getType() === 'm.room.message');
  const preview  = lastMsg ? formatPreview(lastMsg) : '';
  const lastTs   = lastMsg ? formatTime(lastMsg.getTs()) : '';

  const item = document.createElement('div');
  item.className = `room-item${isActive ? ' active' : ''}${unread ? ' unread' : ''}`;
  item.dataset.roomId = room.roomId;

  const avatar = createAvatarElement(name, 36, isDm);
  item.appendChild(avatar);

  // For DMs load partner's user avatar, for rooms load room avatar
  const loadAvatar = async () => {
    let mxc = null;
    if (isDm) {
      const partner = getDmPartner(room);
      if (partner?.userId) mxc = await getUserAvatarUrl(partner.userId);
    } else {
      mxc = await getRoomAvatarUrl(room);
    }
    if (!mxc) return;
    const url = mxcToHttp(mxc);
    if (!url) return;
    const img = document.createElement('img');
    img.src = url;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:inherit;';
    img.onerror = () => img.remove();
    img.onload = () => {
      avatar.innerHTML = '';
      avatar.style.overflow = 'hidden';
      avatar.appendChild(img);
    };
  };
  loadAvatar().catch(() => {});

  const info = document.createElement('div');
  info.className = 'room-info';
  info.innerHTML = `
    <div class="room-name-row">
      <span class="room-name">${escHtml(name)}</span>
      <span class="room-time">${escHtml(lastTs)}</span>
    </div>
    <div style="display:flex;align-items:center;justify-content:space-between;gap:6px;">
      <span class="room-preview">${escHtml(preview)}</span>
      <div style="display:flex;align-items:center;gap:4px;flex-shrink:0;">
        ${encrypted ? `<span class="room-encrypt-icon">${Icons.lock}</span>` : ''}
        ${unread ? `<span class="unread-badge">${unread > 99 ? '99+' : unread}</span>` : ''}
      </div>
    </div>
  `;
  item.appendChild(info);

  item.addEventListener('click', () => onRoomSelect(room.roomId));
  return item;
}

function formatPreview(event) {
  const content = event.getContent();
  const body = content.body || '';
  const senderId = event.getSender();
  const myId = getMyUserId();
  const prefix = senderId === myId ? 'You: ' : '';
  const text = body.replace(/\n/g, ' ').slice(0, 60);
  return prefix + text + (body.length > 60 ? '…' : '');
}

function filterRoomList(query) {
  const items = document.querySelectorAll('.room-item');
  items.forEach(item => {
    const name = item.querySelector('.room-name')?.textContent.toLowerCase() || '';
    item.style.display = (!query || name.includes(query)) ? '' : 'none';
  });
}

/* ──────────────────────────────────────────────
   Chat Panel Rendering
─────────────────────────────────────────────── */
export function renderChatPanel(room, onSendMessage, onTyping, onNexAI, onSearch) {
  const chatArea = document.getElementById('chat-area');
  if (!chatArea) return;

  const isDm     = isDmRoom(room);
  const partner  = isDm ? getDmPartner(room) : null;
  const name     = isDm && partner ? (partner.name || getDisplayName(partner.userId)) : getRoomName(room);
  const encrypted = isEncryptedRoom(room);
  const memberCount = room.getJoinedMembers().length;

  chatArea.innerHTML = `
    ${encrypted ? `
      <div class="encrypt-info-bar">
        ${Icons.lock}
        <span>${t('chat.encrypted')}</span>
      </div>
    ` : ''}
    <div id="connection-banner" class="connection-banner hidden">
      <div class="spinner" style="width:14px;height:14px;"></div>
      Reconnecting…
    </div>
    <div class="chat-header">
      <div class="chat-header-left">
        <button class="mobile-menu-btn" id="mobile-menu-btn" title="${t('btn.menu') || 'Menu'}">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <div class="chat-header-avatar${isDm ? ' dm' : ''}" id="chat-header-avatar"></div>
        <div class="chat-header-info">
          <div class="chat-header-name">
            ${escHtml(name)}
            ${encrypted ? `
              <span class="encrypt-badge">
                ${Icons.lock} Encrypted
              </span>
            ` : ''}
          </div>
          <div class="chat-header-meta">
            ${isDm
              ? `<span id="partner-presence">● Online</span><span id="partner-status-msg" style="margin-left:6px;font-size:11px;color:var(--text-muted);"></span>`
              : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> <span>${memberCount} members</span>`
            }
          </div>
          ${!isDm ? `<div class="chat-header-topic" id="chat-header-topic">
            <span class="chat-topic-text" id="chat-topic-text"></span>
            <button class="chat-topic-edit" id="chat-topic-edit" title="Edit topic">
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
          </div>` : ''}
        </div>
      </div>
      <div class="chat-header-actions">
        <button class="call-btn" id="voice-call-btn" title="${t('chat.voice_call')}">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.65 3.35 2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9c1.91 3.28 4.63 6 7.91 7.91l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
        </button>
        <button class="call-btn video" id="video-call-btn" title="${t('chat.video_call')}">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
        </button>
        <button class="icon-btn" id="pins-btn" title="${t('chat.pins')}" style="position:relative;"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg></button>
          <button class="icon-btn" id="bookmarks-btn" title="Bookmarks">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
          </button>
          <button class="icon-btn" id="starred-btn" title="${t('chat.starred')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></button>
          <button class="icon-btn" title="${t('chat.search')}">${Icons.search}</button>
      </div>
    </div>

    <div class="messages-container" id="messages-container">
      <div class="loading-state">
        <div class="spinner" style="border-color:rgba(108,62,244,0.3);border-top-color:#6c3ef4;width:18px;height:18px;"></div>
        <span class="pulse">${t('chat.loading')}</span>
      </div>
    </div>

    <div class="typing-indicator" id="typing-indicator"></div>
    <div class="message-input-area">
      <div class="message-input-wrapper">
        <textarea
          class="message-textarea"
          id="message-input"
          placeholder="Message ${escHtml(isDm ? name : '#' + name)}…"
          rows="1"
        ></textarea>
        <div class="message-input-actions">
          <input type="file" id="file-input" style="display:none" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar" />
          <button class="icon-btn" id="post-btn" title="Create post" style="color:var(--text-secondary);">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          </button>
          <input type="file" id="sticker-file-input" style="display:none" accept="image/*" multiple />
          <button class="attach-btn" id="poll-btn" title="${t('input.poll')}" style="font-size:13px;font-weight:700;">📊</button>
          <button class="attach-btn" id="gif-btn" title="${t('input.gif')}" style="font-size:13px;font-weight:700;">GIF</button>
          <button class="attach-btn" id="attach-btn" title="${t('input.attach')}">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <button class="schedule-btn icon-btn" id="schedule-btn" title="${t('input.schedule')}">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </button>
          <button class="voice-record-btn" id="voice-btn" title="${t('input.record')}">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="2" width="6" height="11" rx="3"/>
              <path d="M19 10a7 7 0 0 1-14 0"/><line x1="12" y1="19" x2="12" y2="22"/>
              <line x1="8" y1="22" x2="16" y2="22"/>
            </svg>
          </button>
          <button class="nexai-btn" id="nexai-btn" title="${t('input.nexai')}">${Icons.nexai}</button>
          <button class="send-btn" id="send-btn" disabled>${Icons.send}</button>
        </div>
      </div>
      <div class="message-input-hint">
        Press <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for new line
      </div>
    </div>
  `;

  // Set avatar in header — create initials first, then load real photo
  const headerAvatarOld = document.getElementById('chat-header-avatar');
  if (headerAvatarOld) {
    const av = createAvatarElement(name, 36, isDm);
    av.id = 'chat-header-avatar';
    av.style.cssText = 'width:36px;height:36px;min-width:36px;overflow:hidden;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:white;';
    av.style.background = getAvatarColor(name);
    headerAvatarOld.replaceWith(av);

    // Load real avatar asynchronously
    (async () => {
      let mxc = null;
      if (isDmRoom(room)) {
        const partner = getDmPartner(room);
        if (partner?.userId) mxc = await getUserAvatarUrl(partner.userId);
      } else {
        mxc = await getRoomAvatarUrl(room);
      }
      if (!mxc) return;
      const url = mxcToHttp(mxc);
      if (!url) return;
      const img = document.createElement('img');
      img.src = url;
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:inherit;';
      img.onload = () => { av.innerHTML = ''; av.appendChild(img); };
      img.onerror = () => {};
    })().catch(() => {});
  }

  // Wire textarea
  const textarea = document.getElementById('message-input');
  const sendBtn  = document.getElementById('send-btn');

  textarea?.addEventListener('input', () => {
    autoResizeTextarea(textarea);
    const hasText = textarea.value.trim().length > 0;
    sendBtn.disabled = !hasText;
    if (onTyping) onTyping(hasText);
    // Slash command menu
    const val = textarea.value;
    if (val.startsWith('/') && !val.includes(' ')) {
      removeMentionMenu();
      showSlashMenu(val, textarea, (cmd) => {
        textarea.value = cmd + ' ';
        textarea.focus();
        sendBtn.disabled = false;
      });
    } else {
      removeSlashMenu();
    }

    // @mention autocomplete
    const cursorPos = textarea.selectionStart;
    const textBefore = val.slice(0, cursorPos);
    const mentionMatch = textBefore.match(/@([^\s@]*)$/);
    if (mentionMatch) {
      const query = mentionMatch[1];
      const members = window._getRoomMembers?.() || [];
      showMentionMenu(members, query, textarea, (userId, name) => {
        // Replace @query with @name
        const before = textBefore.slice(0, textBefore.lastIndexOf('@'));
        const after = val.slice(cursorPos);
        textarea.value = before + '@' + name + ' ' + after;
        const newPos = before.length + name.length + 2;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
        sendBtn.disabled = textarea.value.trim().length === 0;
      });
    } else {
      removeMentionMenu();
    }
  });

  textarea?.addEventListener('keydown', (e) => {
    // Navigate mention menu
    if (document.getElementById('mention-menu')) {
      if (e.key === 'ArrowDown') { e.preventDefault(); navigateMentionMenu('down'); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); navigateMentionMenu('up');   return; }
      if (e.key === 'Tab' || e.key === 'Enter') {
        const mention = getActiveMention();
        if (mention) {
          e.preventDefault();
          const cursorPos = textarea.selectionStart;
          const textBefore = textarea.value.slice(0, cursorPos);
          const before = textBefore.slice(0, textBefore.lastIndexOf('@'));
          const after = textarea.value.slice(cursorPos);
          textarea.value = before + '@' + mention.name + ' ' + after;
          const newPos = before.length + mention.name.length + 2;
          textarea.setSelectionRange(newPos, newPos);
          removeMentionMenu();
          textarea.focus();
          sendBtn.disabled = false;
          return;
        }
      }
      if (e.key === 'Escape') { removeMentionMenu(); return; }
    }

    // Navigate slash menu
    if (document.getElementById('slash-menu')) {
      if (e.key === 'ArrowDown') { e.preventDefault(); navigateSlashMenu('down'); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); navigateSlashMenu('up');   return; }
      if (e.key === 'Tab' || e.key === 'Enter') {
        const cmd = getActiveSlashCommand();
        if (cmd) {
          e.preventDefault();
          textarea.value = cmd + ' ';
          removeSlashMenu();
          textarea.focus();
          sendBtn.disabled = false;
          return;
        }
      }
      if (e.key === 'Escape') { removeSlashMenu(); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  });

  sendBtn?.addEventListener('click', doSend);

  // NexAI button — toggles panel open/closed
  const nexaiBtn = document.getElementById('nexai-btn');
  nexaiBtn?.addEventListener('click', () => {
    toggleNexAIPanel(onNexAI);
  });

  document.getElementById('attach-btn')?.addEventListener('click', () => {
    document.getElementById('file-input')?.click();
  });

  // gif-btn wired in nexchat.js selectRoom

  // Wire search button
  document.querySelector('.chat-header .icon-btn[title="Search messages"]')
    ?.addEventListener('click', () => {
      if (document.getElementById('search-panel')) { closeSearchPanel(); }
      else if (onSearch) { onSearch(); }
    });

  function doSend() {
    const text = textarea?.value?.trim();
    if (!text) return;
    textarea.value = '';
    sendBtn.disabled = true;
    autoResizeTextarea(textarea);
    if (onTyping) onTyping(false);
    onSendMessage(text);
  }

  // Focus input
  setTimeout(() => textarea?.focus(), 50);
}

function autoResizeTextarea(el) {
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 140) + 'px';
}

/* ──────────────────────────────────────────────
   Message Timeline Rendering
─────────────────────────────────────────────── */
export function renderTimeline(room) {
  const container = document.getElementById('messages-container');
  if (!container) return;

  const events = getTimeline(room, 150);
  const myId   = getMyUserId();

  if (!events.length) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">${Icons.chat}</div>
        <h3>${t('chat.no_messages')}</h3>
        <p>${t('chat.be_first')}</p>
      </div>
    `;
    return;
  }

  container.innerHTML = '';

  // Add load more indicator at top
  const loadMoreEl = document.createElement('div');
  loadMoreEl.className = 'load-more-link';
  loadMoreEl.style.cursor = 'pointer';
  loadMoreEl.addEventListener('click', () => window.dispatchEvent(new CustomEvent('nx:load-more-history')));
  loadMoreEl.innerHTML = '<span style="color:var(--accent-text);cursor:pointer;">↑ Load older messages</span>';
  loadMoreEl.style.textAlign = 'center';
  container.appendChild(loadMoreEl);

  let lastDay    = '';
  let lastSender = '';
  let lastTs     = 0;
  let currentGroupEl = null;

  for (const event of events) {
    const msg    = formatMessage(event);
    const isOwn  = msg.senderId === myId;
    const day    = formatDay(msg.ts);
    const groupBreak = (msg.ts - lastTs) > 5 * 60 * 1000; // 5 min gap = new group

    // Day divider
    if (day !== lastDay) {
      const div = document.createElement('div');
      div.className = 'day-divider';
      div.innerHTML = `<span class="day-divider-text">${day}</span>`;
      container.appendChild(div);
      lastDay = day;
      lastSender = '';
    }

    // New group when sender changes or time gap
    if (msg.senderId !== lastSender || groupBreak) {
      if (!isOwn) {
        const group = document.createElement('div');
        group.className = 'message-group';
        const displayName = getDisplayName(msg.senderId);
        group.innerHTML = `
          <div class="message-group-header">
            <div class="message-group-avatar" style="background:${getAvatarColor(displayName)};position:relative;overflow:hidden;" data-sender-id="${msg.senderId}">
              ${getInitials(displayName)}
            </div>
            <div class="message-group-meta">
              <span class="message-group-name">${escHtml(displayName)}</span>
              <span class="message-group-time">${formatTime(msg.ts)}</span>
            </div>
          </div>
          <div class="message-group-body" data-group-body></div>
        `;
        loadMsgGroupAvatar(group.querySelector('.message-group-avatar'), msg.senderId);
        container.appendChild(group);
        currentGroupEl = group.querySelector('[data-group-body]');
      } else {
        currentGroupEl = null; // own messages don't get a group header
      }
    }

    lastSender = msg.senderId;
    lastTs     = msg.ts;

    // Build message row using buildMessageContent (includes reply + react buttons)
    const row = document.createElement('div');
    row.className = `message-row${isOwn ? ' own' : ''}`;
    row.dataset.eventId = msg.id;
    row.appendChild(buildMessageContent(msg, isOwn));

    if (isOwn || !currentGroupEl) {
      container.appendChild(row);
    } else {
      currentGroupEl.appendChild(row);
    }
  }

  scrollToBottom(container);
}

/* ──────────────────────────────────────────────
   Append a single new message (live)
─────────────────────────────────────────────── */
export function appendMessage(event, room) {
  const container = document.getElementById('messages-container');
  if (!container) return;

  // Deduplicate plaintext messages — encrypted placeholders are allowed through
  const eventId = event.getId();
  const txnId = event.getUnsigned()?.transaction_id || event.getTxnId?.();
  const isEncrypted = event.getType() === 'm.room.encrypted';

  if (!isEncrypted) {
    // For plaintext: skip if already in DOM
    if (eventId && container.querySelector(`[data-event-id="${eventId}"]`)) return;
    if (txnId && container.querySelector(`[data-txn-id="${txnId}"]`)) return;
  }

  // Remove empty state if present
  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  const msg    = formatMessage(event);
  const myId   = getMyUserId();
  const isOwn  = msg.senderId === myId;

  const row = document.createElement('div');
  row.className = `message-row${isOwn ? ' own' : ''}`;
  row.dataset.eventId = msg.id;
  if (txnId) row.dataset.txnId = txnId;

  if (!isOwn) {
    const displayName = getDisplayName(msg.senderId);
    // Check if last message was same sender (within 5 min)
    const lastRow = container.lastElementChild;
    const lastSenderId = lastRow?.dataset?.senderId;
    const showHeader = lastSenderId !== msg.senderId;

    if (showHeader) {
      const group = document.createElement('div');
      group.className = 'message-group';
      group.dataset.senderId = msg.senderId;
      group.innerHTML = `
        <div class="message-group-header">
          <div class="message-group-avatar" style="background:${getAvatarColor(displayName)}">
            ${getInitials(displayName)}
          </div>
          <div class="message-group-meta">
            <span class="message-group-name">${escHtml(displayName)}</span>
            <span class="message-group-time">${formatTime(msg.ts)}</span>
          </div>
        </div>
        <div class="message-group-body" data-group-body></div>
      `;
      loadMsgGroupAvatar(group.querySelector('.message-group-avatar'), msg.senderId);

      const body = group.querySelector('[data-group-body]');
      body.appendChild(buildMessageContent(msg, isOwn));
      container.appendChild(group);
    } else {
      const lastBody = lastRow?.querySelector('[data-group-body]') || container;
      lastBody.appendChild(buildMessageContent(msg, isOwn));
    }
  } else {
    container.appendChild(buildMessageContent(msg, isOwn));
  }

  scrollToBottom(container);
}

function buildMessageContent(msg, isOwn) {
  const bubble = document.createElement('div');
  bubble.className = 'message-bubble';

  if (msg.replyTo) {
    const s = (msg.replyTo.senderId||'').split(':')[0].replace('@','');
    const rp = document.createElement('div');
    rp.className = 'reply-preview';
    rp.innerHTML = `<span class="reply-sender">${escHtml(s)}</span><span class="reply-body">${escHtml((msg.replyTo.body||'').slice(0,80))}${(msg.replyTo.body||'').length>80?'…':''}</span>`;
    bubble.appendChild(rp);
  }

  const bodyEl = document.createElement('div');
  bodyEl.className = 'msg-body';
  const FILE_TYPES = ['m.image','m.video','m.audio','m.file'];
  if (msg.type === 'org.matrix.call.invite' || msg.type === 'org.matrix.call.end') {
    // Call event — render as a pill
    const isInvite = msg.type === 'org.matrix.call.invite';
    const callType = msg._event?.getContent()?.call_type || 'voice';
    const icon = callType === 'video' ? '📹' : '📞';
    const pill = document.createElement('div');
    pill.style.cssText = 'display:inline-flex;align-items:center;gap:6px;padding:6px 12px;background:var(--bg-elevated);border:1px solid var(--border);border-radius:999px;font-size:12px;color:var(--text-muted);';
    pill.innerHTML = `${icon} <span>${escHtml(isInvite ? (callType === 'video' ? 'Started a video call' : 'Started a voice call') : 'Call ended')}</span>`;
    bodyEl.appendChild(pill);
  } else if (msg.type === 'org.matrix.msc3381.poll.start' && msg._event) {
    // Render poll card
    const room = msg._event.getRoomId ? getRoom(msg._event.getRoomId()) : null;
    const myId = getMyUserId();
    const card = buildPollCard(msg._event, room, myId,
      (pollId, answerIds) => {
        if (room) window._sendPollVote?.(room.roomId, pollId, answerIds);
      },
      (pollId) => {
        if (room) window._endPoll?.(room.roomId, pollId);
      }
    );
    if (card) bodyEl.appendChild(card);
  } else if (msg.failed) {
    bodyEl.textContent = '🔐 Encrypted message'; bodyEl.style.opacity = '0.5'; bodyEl.style.fontStyle = 'italic';
  } else if (FILE_TYPES.includes(msg.type) && msg.fileUrl) {
    // External URL (GIF) — show inline immediately
    if (!msg.fileUrl.startsWith('mxc://') && msg.type === 'm.image') {
      const img = document.createElement('img');
      img.src = msg.fileUrl;
      img.alt = msg.fileName || 'Image';
      img.className = 'msg-image';
      img.addEventListener('click', () => window.open(msg.fileUrl, '_blank'));
      bodyEl.appendChild(img);
    } else if (msg.type === 'm.image') {
      const img = document.createElement('img');
      img.alt = msg.fileName || 'Image';
      img.className = 'msg-image';
      img.style.background = 'var(--bg-elevated)';
      bodyEl.appendChild(img);
      // Use real event so decryptAndBlobUrl has access to E2EE key/iv
      const realEvent = msg._event || { getContent: () => ({
        msgtype: msg.type, url: msg.fileUrl,
        file: msg.fileEncrypted || null,
        external_url: null, info: msg.fileInfo, body: msg.fileName,
      })};
      decryptAndBlobUrl(realEvent).then(blobUrl => {
        if (blobUrl) { img.src = blobUrl; img.addEventListener('click', () => window.open(blobUrl, '_blank')); }
      }).catch(() => {});
    } else if (msg.type === 'm.video') {
      const video = document.createElement('video');
      video.src = mxcToHttp(msg.fileUrl); video.controls = true;
      video.className = 'msg-video';
      bodyEl.appendChild(video);
    } else if (msg.type === 'm.audio') {
      const isVoice = msg._event?.getContent()?.['org.matrix.msc3245.voice'] !== undefined;
      const src = mxcToHttp(msg.fileUrl);
      if (isVoice) {
        const durationMs = msg.fileInfo?.duration || 0;
        bodyEl.appendChild(buildVoicePlayer(src, durationMs));
      } else {
        const audio = document.createElement('audio');
        audio.src = src; audio.controls = true;
        audio.className = 'msg-audio';
        bodyEl.appendChild(audio);
      }
    } else if (msg.type === 'm.file') {
      const url = mxcToHttp(msg.fileUrl);
      const size = msg.fileInfo?.size ? formatFileSize(msg.fileInfo.size) : '';
      const isPdf = (msg.fileName || '').toLowerCase().endsWith('.pdf') || msg.fileInfo?.mimetype === 'application/pdf';
      if (isPdf) {
        const wrap = document.createElement('div');
        wrap.className = 'msg-pdf-wrap';
        const canvasId = 'pdf-canvas-' + (msg.id || Math.random().toString(36).slice(2)).replace(/[^a-z0-9]/gi, '').slice(-12);
        wrap.innerHTML = `
          <div class="msg-pdf-canvas-wrap">
            <canvas class="msg-pdf-canvas" id="${canvasId}"></canvas>
            <div class="msg-pdf-loading">Loading PDF…</div>
          </div>
          <div class="msg-pdf-footer">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span class="msg-file-name">${escHtml(msg.fileName || 'Document.pdf')}</span>
            ${size ? `<span class="msg-file-size">${size}</span>` : ''}
            <span class="msg-pdf-pages" id="pages-${canvasId}"></span>
            <button class="msg-pdf-expand" id="expand-${canvasId}" title="View full PDF" style="margin-left:auto;background:none;border:none;cursor:pointer;color:var(--accent-text);font-size:11px;display:flex;align-items:center;gap:3px;">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              View
            </button>
            <a href="${url}" download="${escHtml(msg.fileName || 'document.pdf')}" class="msg-pdf-dl" title="Download">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </a>
          </div>`;
        bodyEl.appendChild(wrap);
        // Render first page via PDF.js — defer so DOM is ready
        // Queue PDF render — fires after DOM insertion
        window._pendingPdfRenders = window._pendingPdfRenders || [];
        window._pendingPdfRenders.push({ canvasId, url, fileName: msg.fileName || 'Document.pdf' });
        // Click canvas → full viewer (wired after insertion via flushPdfRenders)
      } else {
        const card = document.createElement('a');
        card.href = url; card.target = '_blank';
        card.className = 'msg-file-card';
        card.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <div class="msg-file-info">
            <span class="msg-file-name">${escHtml(msg.fileName || 'File')}</span>
            ${size ? `<span class="msg-file-size">${size}</span>` : ''}
          </div>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
        bodyEl.appendChild(card);
      }
    }
  } else if (msg.type === 'nx.post') {
    const title = msg._event?.getContent()?.['nx.post.title'] || msg.body?.split('\n')[0] || 'Post';
    const postBody = msg._event?.getContent()?.['nx.post.body'] || msg.body?.split('\n\n').slice(1).join('\n\n') || '';
    const isExpanded = false;
    const card = document.createElement('div');
    card.className = 'post-card';
    card.innerHTML = `
      <div class="post-card-header">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        <span class="post-badge">Post</span>
        <span class="post-title">${escHtml(title)}</span>
      </div>
      <div class="post-body" style="display:none;">${escHtml(postBody)}</div>
      <button class="post-toggle">Read more ▾</button>`;
    const postBodyEl = card.querySelector('.post-body');
    const postToggleBtn = card.querySelector('.post-toggle');
    postToggleBtn.addEventListener('click', () => {
      const shown = postBodyEl.style.display !== 'none';
      postBodyEl.style.display = shown ? 'none' : 'block';
      postToggleBtn.textContent = shown ? 'Read more ▾' : 'Collapse ▴';
    });
    bodyEl.appendChild(card);
  } else {
    bodyEl.textContent = msg.body || '';
  }
  bubble.appendChild(bodyEl);

  const timeEl = document.createElement('div');
  timeEl.className = 'message-time';
  timeEl.textContent = formatTime(msg.ts);
  if (isOwn) {
    const statusEl = document.createElement('span');
    statusEl.className = 'message-status';
    statusEl.dataset.eventId = msg.id;
    statusEl.innerHTML = Icons.check;
    statusEl.title = 'Sent';
    timeEl.appendChild(statusEl);
  }

  // Thread badge — populated async by nexchat.js after render
  const threadBadge = document.createElement('div');
  threadBadge.className = 'thread-badge';
  threadBadge.id = 'thread-badge-' + msg.id;
  threadBadge.style.display = 'none'; // hidden until thread replies exist
  threadBadge.dataset.eventId = msg.id;

  const replyBtn = document.createElement('button');
  replyBtn.className = 'reply-btn'; replyBtn.title = 'Reply';
  replyBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>';
  replyBtn.dataset.eventId = msg.id; replyBtn.dataset.senderId = msg.senderId; replyBtn.dataset.body = msg.body||'';

  const reactBtn = document.createElement('button');
  reactBtn.className = 'react-btn';
  reactBtn.title = 'Add reaction';
  reactBtn.textContent = '😊';
  reactBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    showEmojiPicker(wrapper, msg.id, (emoji) => {
      wrapper.dispatchEvent(new CustomEvent('nx:react', { bubbles: true, detail: { eventId: msg.id, emoji } }));
    });
  });

  const msgRow = document.createElement('div');
  msgRow.className = 'msg-with-reply';
  // More actions button (⋯)
  const moreBtn = document.createElement('button');
  moreBtn.className = 'more-btn';
  moreBtn.title = 'More options';
  moreBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>';
  moreBtn.dataset.eventId  = msg.id;
  moreBtn.dataset.body     = msg.body || '';
  moreBtn.dataset.isOwn    = isOwn ? '1' : '';
  moreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    showMessageActionMenu(moreBtn, msg, isOwn, wrapper);
  });

  if (isOwn) { msgRow.appendChild(replyBtn); msgRow.appendChild(reactBtn); msgRow.appendChild(moreBtn); msgRow.appendChild(bubble); }
  else        { msgRow.appendChild(bubble);   msgRow.appendChild(replyBtn); msgRow.appendChild(reactBtn); msgRow.appendChild(moreBtn); }

  const wrapper = document.createElement('div');
  wrapper.dataset.senderId = msg.senderId;
  wrapper.dataset.eventId  = msg.id;
  if (msg.txnId) wrapper.dataset.txnId = msg.txnId;
  wrapper.style.cssText = isOwn
    ? 'display:flex;flex-direction:column;align-items:flex-end;margin-bottom:2px;'
    : 'display:flex;flex-direction:column;align-items:flex-start;margin-bottom:2px;';
  wrapper.appendChild(msgRow);
  wrapper.appendChild(timeEl);
  wrapper.appendChild(threadBadge);

  if (isOwn) {
    const receiptAvatars = document.createElement('div');
    receiptAvatars.className = 'receipt-avatars';
    receiptAvatars.id = 'receipt-avatars-' + msg.id;
    wrapper.appendChild(receiptAvatars);
  }

  return wrapper;
}

/* ──────────────────────────────────────────────
   Typing indicator
─────────────────────────────────────────────── */
export function updateTypingIndicator(roomId, users) {
  const el = document.getElementById('typing-indicator');
  if (!el) return;

  const myId = getMyUserId();
  const others = users.filter(u => u !== myId);

  if (!others.length) {
    el.innerHTML = '';
    el.classList.remove('visible');
    return;
  }

  const COLORS = ['#7c5cfc','#ef4444','#3b82f6','#10b981','#f59e0b','#ec4899'];

  // Build avatars for up to 3 users
  const avatarUsers = others.slice(0, 3);
  const avatarsHtml = avatarUsers.map(userId => {
    const name = getDisplayName(userId) || userId.split(':')[0].slice(1);
    const initials = name.slice(0, 2).toUpperCase();
    const color = COLORS[name.charCodeAt(0) % COLORS.length];
    const avatarUrl = window._mxcToHttp
      ? window._mxcToHttp(window._mxClient?.getUser(userId)?.avatarUrl || '')
      : null;
    return `
      <div style="
        width:22px;height:22px;border-radius:50%;
        background:${color};
        display:flex;align-items:center;justify-content:center;
        font-size:9px;font-weight:700;color:white;
        flex-shrink:0;overflow:hidden;
        border:2px solid var(--bg-primary);
        margin-right:-6px;
      " title="${escHtml(name)}">
        ${avatarUrl
          ? `<img src="${escHtml(avatarUrl)}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" onerror="this.parentElement.textContent='${escHtml(initials)}'" />`
          : escHtml(initials)
        }
      </div>
    `;
  }).join('');

  const names = others.slice(0, 2).map(u => escHtml(getDisplayName(u) || u.split(':')[0].slice(1)));
  const label = others.length === 1
    ? `${names[0]} is typing`
    : others.length === 2
      ? `${names[0]} and ${names[1]} are typing`
      : `${names[0]} and ${others.length - 1} others are typing`;

  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;">
      <div style="display:flex;align-items:center;padding-right:6px;">
        ${avatarsHtml}
      </div>
      <div class="typing-dots">
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
      </div>
      <span style="font-size:12px;color:var(--text-muted);">${label}…</span>
    </div>
  `;
  el.classList.add('visible');
  // Scroll messages to bottom to show typing indicator
  const mc = document.getElementById('messages-container');
  if (mc) mc.scrollTop = mc.scrollHeight;
}

/* ──────────────────────────────────────────────
   Connection banner
─────────────────────────────────────────────── */
export function setConnectionStatus(isConnected) {
  const banner = document.getElementById('connection-banner');
  if (banner) banner.classList.toggle('hidden', isConnected);
}

/* ──────────────────────────────────────────────
   New Room / DM Modal
─────────────────────────────────────────────── */
export function renderNewRoomModal(onCreateRoom, onClose) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'new-room-modal';

  overlay.innerHTML = `
    <div class="modal">
      <h2 class="modal-title">New conversation</h2>
      <p class="modal-subtitle">Start a new channel or direct message.</p>

      <div class="modal-tabs">
        <div class="modal-tab active" data-tab="dm">${t('notif.dm')}</div>
        <div class="modal-tab" data-tab="channel">Channel</div>
        <div class="modal-tab" data-tab="broadcast">Broadcast</div>
        <div class="modal-tab" data-tab="guest">Guest Link</div>
      </div>

      <!-- DM Tab -->
      <div id="tab-dm">
        <div class="form-group">
          <label class="form-label">User ID or username</label>
          <input type="text" class="form-input" id="dm-user-input" placeholder="${t('room.dm_placeholder')}" />
        </div>
      </div>

      <!-- Channel Tab -->
      <div id="tab-channel" class="hidden">
        <div class="form-group">
          <label class="form-label">${t('room.channel_label')}</label>
          <input type="text" class="form-input" id="channel-name-input" placeholder="${t('room.channel_placeholder')}" />
        </div>
      </div>

      <!-- Broadcast Tab -->
      <div id="tab-broadcast" class="hidden">
        <div class="form-group">
          <label class="form-label">Message</label>
          <textarea class="form-input" id="broadcast-text" rows="3" placeholder="Type your broadcast message…" style="resize:none;"></textarea>
        </div>
        <div class="form-group">
          <label class="form-label">Send to</label>
          <div class="broadcast-room-list" id="broadcast-room-list"></div>
        </div>
        <div style="font-size:11px;color:var(--text-secondary);margin-top:4px;" id="broadcast-count">0 selected</div>
      </div>

      <!-- Guest Tab -->
      <div id="tab-guest" class="hidden">
        <div class="form-group">
          <label class="form-label">Room to invite guest to</label>
          <select class="form-input" id="guest-room-select" style="background:var(--bg-input);color:var(--text-primary);border:1px solid var(--border);border-radius:8px;padding:8px 12px;"></select>
        </div>
        <div class="form-group">
          <label class="form-label">Guest name / label (optional)</label>
          <input type="text" class="form-input" id="guest-label" placeholder="e.g. Client, Contractor…" />
        </div>
        <div id="guest-invite-result" style="display:none;margin-top:8px;">
          <div style="font-size:12px;color:var(--text-secondary);margin-bottom:4px;">Share this invite link:</div>
          <div style="display:flex;gap:8px;align-items:center;">
            <input type="text" class="form-input" id="guest-invite-link" readonly style="font-size:11px;flex:1;" />
            <button class="pdf-nav-btn" id="guest-copy-btn">Copy</button>
          </div>
          <div style="font-size:11px;color:var(--text-secondary);margin-top:6px;">Link expires in 24 hours. To remove guest access, kick them from the room via the room members panel.</div>
        </div>
      </div>

      <div id="new-room-error" class="auth-error"></div>

      <div class="modal-actions">
        <button class="btn btn-secondary" id="modal-cancel">${t('btn.cancel')}</button>
        <button class="btn btn-primary" id="modal-create" style="margin-top:0">Create</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  let activeTab = 'dm';

  // Populate broadcast room list and guest room select
  const allRooms = typeof window._getAllRooms === 'function' ? window._getAllRooms() : [];
  const broadcastList = overlay.querySelector('#broadcast-room-list');
  const guestSelect = overlay.querySelector('#guest-room-select');
  const selected = new Set();

  allRooms.forEach(room => {
    // Broadcast checkboxes
    const item = document.createElement('label');
    item.className = 'broadcast-room-item';
    item.innerHTML = `<input type="checkbox" value="${room.roomId}" class="bc-check" />
      <span>${escHtml(room.name || room.roomId)}</span>`;
    item.querySelector('.bc-check').addEventListener('change', e => {
      if (e.target.checked) selected.add(room.roomId);
      else selected.delete(room.roomId);
      overlay.querySelector('#broadcast-count').textContent = selected.size + ' selected';
    });
    broadcastList?.appendChild(item);

    // Guest room select
    const opt = document.createElement('option');
    opt.value = room.roomId;
    opt.textContent = room.name || room.roomId;
    guestSelect?.appendChild(opt);
  });

  // Tab switching
  overlay.querySelectorAll('.modal-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      overlay.querySelectorAll('.modal-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      activeTab = tab.dataset.tab;
      ['dm','channel','broadcast','guest'].forEach(t => {
        document.getElementById('tab-' + t)?.classList.toggle('hidden', activeTab !== t);
      });
    });
  });

  // Cancel / backdrop close
  const closeModal = () => {
    overlay.classList.remove('visible');
    setTimeout(() => overlay.remove(), 200);
    onClose?.();
  };
  document.getElementById('modal-cancel').addEventListener('click', closeModal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });

  // Create
  document.getElementById('modal-create').addEventListener('click', async () => {
    const errEl  = document.getElementById('new-room-error');
    const createBtn = document.getElementById('modal-create');
    errEl.classList.remove('visible');

    try {
      createBtn.disabled = true;
      createBtn.textContent = 'Creating…';

      if (activeTab === 'dm') {
        const userId = document.getElementById('dm-user-input').value.trim();
        if (!userId) throw new Error('Please enter a user ID');
        await onCreateRoom({ type: 'dm', userId });
        closeModal();
      } else if (activeTab === 'channel') {
        const name = document.getElementById('channel-name-input').value.trim();
        if (!name) throw new Error('Please enter a channel name');
        await onCreateRoom({ type: 'channel', name });
        closeModal();
      } else if (activeTab === 'broadcast') {
        const text = document.getElementById('broadcast-text').value.trim();
        if (!text) throw new Error('Please enter a message');
        if (selected.size === 0) throw new Error('Please select at least one room');
        await onCreateRoom({ type: 'broadcast', text, roomIds: [...selected] });
        closeModal();
      } else if (activeTab === 'guest') {
        const roomId = document.getElementById('guest-room-select').value;
        const label = document.getElementById('guest-label').value.trim();
        if (!roomId) throw new Error('Please select a room');
        const result = await onCreateRoom({ type: 'guest', roomId, label });
        if (result?.inviteLink) {
          document.getElementById('guest-invite-result').style.display = 'block';
          document.getElementById('guest-invite-link').value = result.inviteLink;
          document.getElementById('guest-copy-btn').addEventListener('click', () => {
            navigator.clipboard.writeText(result.inviteLink);
            showToast('🔗 Invite link copied!', 'success');
          });
          createBtn.disabled = false;
          createBtn.textContent = 'Create';
          return; // Don't close — show the link
        }
      }
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('visible');
      createBtn.disabled = false;
      createBtn.textContent = 'Create';
    }
  });

  // Focus first input
  setTimeout(() => document.getElementById('dm-user-input')?.focus(), 100);
}

/* ──────────────────────────────────────────────
   Toast notifications
─────────────────────────────────────────────── */
export function showToast(message, type = 'success', duration = 3500) {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const icon = type === 'success' ? Icons.check : Icons.wifi_off;

  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.innerHTML = `<span class="toast-icon">${icon}</span><span>${escHtml(message)}</span>`;

  container.appendChild(toast);
  setTimeout(() => {
    toast.style.animation = 'toastIn 0.2s ease reverse forwards';
    setTimeout(() => toast.remove(), 200);
  }, duration);
}

/* ──────────────────────────────────────────────
   Update active room in sidebar
─────────────────────────────────────────────── */
export function setActiveRoom(roomId) {
  document.querySelectorAll('.room-item').forEach(item => {
    item.classList.toggle('active', item.dataset.roomId === roomId);
  });
}

/* ──────────────────────────────────────────────
   Update unread badge on a specific room item
─────────────────────────────────────────────── */
export function updateRoomUnread(roomId) {
  const item = document.querySelector(`.room-item[data-room-id="${roomId}"]`);
  if (!item) return;
  const room = getRoom(roomId);
  if (!room) return;
  const count = getUnreadCount(room);
  const badge = item.querySelector('.unread-badge');
  if (count > 0) {
    if (!badge) {
      const newBadge = document.createElement('span');
      newBadge.className = 'unread-badge';
      newBadge.textContent = count > 99 ? '99+' : count;
      item.querySelector('.room-info').appendChild(newBadge);
    } else {
      badge.textContent = count > 99 ? '99+' : count;
    }
    item.classList.add('unread');
  } else {
    badge?.remove();
    item.classList.remove('unread');
  }
}

/* ──────────────────────────────────────────────
   NexAI message rendering
─────────────────────────────────────────────── */
export function appendNexAIMessage(text) {
  const container = document.getElementById('messages-container');
  if (!container) return;

  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  const bubble = document.createElement('div');
  bubble.id = 'nexai-response-bubble';
  bubble.className = 'nexai-bubble';
  bubble.innerHTML = `
    <div class="nexai-bubble-header">
      <span class="nexai-icon">${Icons.nexai}</span>
      <span class="nexai-label">NexAI</span>
    </div>
    <div class="nexai-bubble-body" id="nexai-bubble-body">${escHtml(text)}</div>
  `;
  container.appendChild(bubble);
  container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
}

export function showNexAITyping() {
  const container = document.getElementById('messages-container');
  if (!container || document.getElementById('nexai-typing')) return;
  const el = document.createElement('div');
  el.id = 'nexai-typing';
  el.className = 'nexai-typing';
  el.innerHTML = `
    <span class="nexai-icon">${Icons.nexai}</span>
    <span class="nexai-label">NexAI</span>
    <div class="typing-dots">
      <div class="typing-dot"></div>
      <div class="typing-dot"></div>
      <div class="typing-dot"></div>
    </div>
  `;
  container.appendChild(el);
  container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
}

export function hideNexAITyping() {
  document.getElementById('nexai-typing')?.remove();
}

export function updateNexAIMessage(text) {
  const body = document.getElementById('nexai-bubble-body');
  if (body) body.textContent = text;
  const container = document.getElementById('messages-container');
  if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
}

export function setNexAIButtonState(state) {
  const btn = document.getElementById('nexai-btn');
  if (!btn) return;
  if (state === 'loading') {
    btn.disabled = true;
    btn.classList.add('loading');
  } else {
    btn.disabled = false;
    btn.classList.remove('loading');
  }
}

/* ──────────────────────────────────────────────
   Reactions
─────────────────────────────────────────────── */
const QUICK_EMOJIS = ['❤️', '👍', '😂', '🔥', '✅', '😮', '😢', '👀'];

export function updateMessageReactions(eventId, reactions, myUserId, onToggle) {
  // Find the message-row that contains this event
  const row = document.querySelector('.message-row[data-event-id="' + eventId + '"]')
           || document.querySelector('[data-event-id="' + eventId + '"]')?.closest('.message-row');
  if (!row) return;

  // Remove existing reaction bar
  row.querySelector('.reaction-bar')?.remove();
  if (Object.keys(reactions).length === 0) return;

  const bar = document.createElement('div');
  bar.className = 'reaction-bar';

  // Check if current user has already reacted (any emoji)
  const myExistingReaction = Object.entries(reactions)
    .flatMap(([emoji, users]) => users.filter(u => u.userId === myUserId).map(u => ({ emoji, eventId: u.eventId })))
    [0] || null;

  for (const [emoji, users] of Object.entries(reactions)) {
    const myReaction = users.find(u => u.userId === myUserId);
    const btn = document.createElement('button');
    btn.className = 'reaction-pill' + (myReaction ? ' mine' : '');
    btn.innerHTML = emoji + ' <span>' + users.length + '</span>';
    btn.title = users.map(u => u.userId.split(':')[0].slice(1)).join(', ');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Enrich users with displayName for modal
      const enriched = Object.fromEntries(
        Object.entries(reactions).map(([em, us]) => [em, us.map(u => ({
          ...u,
          displayName: u.displayName || u.userId.split(':')[0].slice(1)
        }))])
      );
      showReactionSummaryModal(enriched, myUserId, onToggle);
    });
    bar.appendChild(btn);
  }



  // Remove old bar and append new one directly to message-row
  row.querySelector('.reaction-bar')?.remove();
  row.appendChild(bar);
}

/* ──────────────────────────────────────────────
   Reaction summary modal
─────────────────────────────────────────────── */
export function showReactionSummaryModal(reactions, myUserId, onToggle) {
  document.getElementById('reaction-summary-modal')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'reaction-summary-modal';
  overlay.className = 'modal-overlay visible';

  const emojis = Object.keys(reactions);
  let activeEmoji = emojis[0];

  function renderModal() {
    overlay.innerHTML = `
      <div class="rsm-box">
        <div class="rsm-header">
          <span class="rsm-title">Reactions</span>
          <button class="rsm-close" id="rsm-close">✕</button>
        </div>
        <div class="rsm-tabs">
          ${emojis.map(e => `
            <button class="rsm-tab${e === activeEmoji ? ' rsm-tab-active' : ''}" data-emoji="${e}">
              ${e} ${reactions[e].length}
            </button>`).join('')}
        </div>
        <div class="rsm-list">
          ${(reactions[activeEmoji] || []).map(u => {
            const name = u.displayName || u.userId.split(':')[0].slice(1);
            const isMe = u.userId === myUserId;
            return `<div class="rsm-user${isMe ? ' rsm-user-me' : ''}">
              <div class="rsm-avatar" style="background:${getAvatarColor(name)};">
                ${u.avatarUrl ? `<img class="rsm-avatar-img" src="" data-mxc="${u.avatarUrl}" />` : name[0].toUpperCase()}
              </div>
              <span class="rsm-name">${escHtml(name)}${isMe ? ' <em>(you)</em>' : ''}</span>
              ${isMe ? `<button class="rsm-remove-btn" data-emoji="${activeEmoji}" data-event-id="${u.eventId}">Remove</button>` : ''}
            </div>`;
          }).join('')}
        </div>
      </div>`;

    overlay.querySelectorAll('img[data-mxc]').forEach(img => {
      img.src = typeof window._mxcToHttp === 'function' ? window._mxcToHttp(img.dataset.mxc) : img.dataset.mxc;
    });

    overlay.querySelector('#rsm-close').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
    overlay.querySelectorAll('.rsm-tab').forEach(tab => {
      tab.addEventListener('click', () => { activeEmoji = tab.dataset.emoji; renderModal(); });
    });
    overlay.querySelectorAll('.rsm-remove-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        onToggle(btn.dataset.emoji, btn.dataset.eventId);
        overlay.remove();
      });
    });
  }

  renderModal();
  document.body.appendChild(overlay);
}

function showEmojiPicker(wrapper, eventId, onReact) {
  document.querySelector('.emoji-picker-popup')?.remove();

  const EMOJIS = {
    'Smileys': ['😀','😂','😅','😊','😍','🥰','😘','😎','🤔','😮','😢','😡','🤩','🥳','😴','🤗','😇','🤪','😜','😏'],
    'Gestures': ['👍','👎','👏','🙌','🤝','✌️','🤞','👌','🤙','💪','🙏','👋','🤚','✋','👆','👇','👈','👉','🖕','🤘'],
    'Hearts': ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','💕','💞','💓','💗','💖','💘','💝','❣️','💔','❤️‍🔥','♥️','🫶'],
    'Objects': ['🔥','⭐','✨','💫','🎉','🎊','🎁','🏆','🥇','💎','🚀','💡','🎯','💯','✅','❌','⚡','🌈','🍀','🌸'],
  };
  const QUICK = ['❤️','👍','😂','🔥','✅','😮','😢','🙏'];

  const picker = document.createElement('div');
  picker.className = 'emoji-picker-popup';
  picker.innerHTML = `
    <div class="emoji-picker-header">
      <input class="emoji-search" placeholder="Search emoji…" type="text" />
    </div>
    <div class="emoji-picker-body">
      <div class="emoji-section">
        <div class="emoji-section-title">Quick Reactions</div>
        <div class="emoji-grid quick-grid">${QUICK.map(e => `<button class="emoji-btn" data-emoji="${e}">${e}</button>`).join('')}</div>
      </div>
      ${Object.entries(EMOJIS).map(([cat, emojis]) => `
        <div class="emoji-section">
          <div class="emoji-section-title">${cat}</div>
          <div class="emoji-grid">${emojis.map(e => `<button class="emoji-btn" data-emoji="${e}">${e}</button>`).join('')}</div>
        </div>
      `).join('')}
    </div>
  `;

  // Position picker above the wrapper
  const rect = wrapper.getBoundingClientRect();
  const pickerH = 280;
  const top = rect.top - pickerH - 8;
  picker.style.top = Math.max(8, top) + 'px';
  picker.style.left = Math.min(rect.left, window.innerWidth - 288) + 'px';
  document.body.appendChild(picker);

  // Wire emoji clicks
  picker.querySelectorAll('.emoji-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      picker.remove();
      onReact(btn.dataset.emoji);
    });
  });

  // Wire search
  const search = picker.querySelector('.emoji-search');
  search?.addEventListener('input', () => {
    const q = search.value.toLowerCase();
    picker.querySelectorAll('.emoji-btn').forEach(btn => {
      btn.style.display = !q || btn.dataset.emoji.includes(q) ? '' : 'none';
    });
    picker.querySelectorAll('.emoji-section').forEach(sec => {
      const visible = [...sec.querySelectorAll('.emoji-btn')].some(b => b.style.display !== 'none');
      sec.style.display = visible ? '' : 'none';
    });
  });

  search?.focus();

  setTimeout(() => {
    document.addEventListener('click', (e) => {
      if (!picker.contains(e.target)) picker.remove();
    }, { once: true });
  }, 10);
}

/* ──────────────────────────────────────────────
   Read receipt ticks
─────────────────────────────────────────────── */
export function updateMessageTick(eventId, status, onReadByClick) {
  const container = document.getElementById('messages-container');
  const el = (container || document).querySelector(`.message-status[data-event-id="${eventId}"]`);
  if (!el) return;
  if (status === 'sent') {
    el.innerHTML = Icons.check; el.style.color = ''; el.title = 'Sent';
    el.style.cursor = '';
    el.onclick = null;
  } else if (status === 'delivered') {
    el.innerHTML = Icons.checkDouble; el.style.color = ''; el.title = 'Delivered';
    el.style.cursor = '';
    el.onclick = null;
  } else if (status === 'read') {
    el.innerHTML = Icons.checkDouble; el.style.color = 'var(--accent-text)'; el.title = 'Read by…';
    el.style.cursor = 'pointer';
    el.onclick = (e) => {
      e.stopPropagation();
      if (onReadByClick) onReadByClick(eventId, el);
    };
  }
}

/* ──────────────────────────────────────────────
   Stories
─────────────────────────────────────────────── */
const STORY_BG_COLORS = [
  '#6c3ef4','#0ea5e9','#10b981','#f59e0b','#ef4444',
  '#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1'
];

export function renderStoriesBar(stories, myUserId, onView, onAdd) {
  const scroll = document.getElementById('stories-scroll');
  if (!scroll) return;

  // Keep the add button, rebuild the rest
  const addBtn = document.getElementById('story-add-btn');

  // Update own avatar in add button
  const myStory = stories.find(s => s.userId === myUserId);
  const myAvEl = document.getElementById('my-story-avatar');
  if (myAvEl) {
    if (myStory?.avatarUrl) {
      const url = typeof window._mxcToHttp === 'function' ? window._mxcToHttp(myStory.avatarUrl) : myStory.avatarUrl;
      myAvEl.style.cssText = 'background-image:url(' + url + ');background-size:cover;background-position:center;';
      myAvEl.textContent = '';
    } else {
      myAvEl.textContent = myStory ? '✓' : '+';
    }
  }

  // Remove old story items
  scroll.querySelectorAll('.story-item').forEach(el => el.remove());

  // Add others' stories
  const others = stories.filter(s => s.userId !== myUserId);
  for (const story of others) {
    const item = document.createElement('div');
    item.className = 'story-item';
    const initial = (story.displayName || '?')[0].toUpperCase();
    const avatarHtml = story.avatarUrl
      ? `<div class="story-avatar-inner" style="background-image:url(${typeof window._mxcToHttp === 'function' ? window._mxcToHttp(story.avatarUrl) : story.avatarUrl});background-size:cover;background-position:center;"></div>`
      : `<div class="story-avatar-inner" style="background:${getAvatarColor(story.displayName)};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:600;font-size:14px;">${initial}</div>`;
    item.innerHTML = `
      <div class="story-avatar-ring">
        ${avatarHtml}
      </div>
      <span class="story-name">${escHtml(story.displayName.split(' ')[0])}</span>`;
    item.addEventListener('click', () => onView(story));
    scroll.appendChild(item);
  }

  // Wire add button
  if (addBtn) {
    addBtn.onclick = () => onAdd(myStory);
  }

  // Always show bar (so user can add stories)
  const bar = document.getElementById('stories-bar');
  if (bar) bar.style.display = 'block';
}

export function showStoryComposer(existingStory, onPost) {
  document.getElementById('story-composer-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'story-composer-modal';
  overlay.className = 'modal-overlay visible';

  const bgColor = existingStory?.bgColor || STORY_BG_COLORS[Math.floor(Math.random() * STORY_BG_COLORS.length)];

  overlay.innerHTML = `
    <div class="story-composer">
      <div class="story-preview" id="story-preview" style="background:${bgColor};">
        <textarea class="story-text-input" id="story-text" placeholder="What's on your mind?" maxlength="280">${existingStory?.text || ''}</textarea>
      </div>
      <div class="story-composer-toolbar">
        <div class="story-color-row">
          ${STORY_BG_COLORS.map(c => `<button class="story-color-swatch${c === bgColor ? ' active' : ''}" data-color="${c}" style="background:${c};"></button>`).join('')}
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:10px;">
          <button class="rsm-remove-btn" id="story-cancel">Cancel</button>
          ${existingStory ? `<button class="rsm-remove-btn" id="story-delete" style="color:#ef4444;border-color:#ef4444;">Delete</button>` : ''}
          <button class="send-btn" id="story-post" style="padding:6px 20px;">
            ${existingStory ? 'Update' : 'Share'}
          </button>
        </div>
      </div>
    </div>`;

  let selectedColor = bgColor;

  overlay.querySelectorAll('.story-color-swatch').forEach(btn => {
    btn.addEventListener('click', () => {
      selectedColor = btn.dataset.color;
      overlay.querySelectorAll('.story-color-swatch').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      overlay.querySelector('#story-preview').style.background = selectedColor;
    });
  });

  overlay.querySelector('#story-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  overlay.querySelector('#story-delete')?.addEventListener('click', () => {
    onPost({ text: '', bgColor: selectedColor, deleted: true });
    overlay.remove();
  });

  overlay.querySelector('#story-post').addEventListener('click', () => {
    const text = overlay.querySelector('#story-text').value.trim();
    if (!text) { overlay.querySelector('#story-text').focus(); return; }
    onPost({ text, bgColor: selectedColor, color: '#ffffff' });
    overlay.remove();
  });

  document.body.appendChild(overlay);
  setTimeout(() => overlay.querySelector('#story-text')?.focus(), 50);
}

export function showStoryViewer(stories, startIndex = 0) {
  document.getElementById('story-viewer-modal')?.remove();
  if (!stories.length) return;

  let current = startIndex;
  let timer = null;
  const DURATION = 5000;

  const overlay = document.createElement('div');
  overlay.id = 'story-viewer-modal';
  overlay.className = 'modal-overlay visible';
  overlay.style.cssText = 'background:rgba(0,0,0,0.95);z-index:10000;flex-direction:column;padding:0;';

  function render() {
    const story = stories[current];
    const timeAgo = _timeAgo(story.ts);
    const expiresIn = Math.max(0, Math.round((story.expires - Date.now()) / 3600000));

    overlay.innerHTML = `
      <div class="story-viewer">
        <div class="story-progress-bar">
          ${stories.map((_, i) => `<div class="story-progress-seg${i < current ? ' done' : i === current ? ' active' : ''}"><div class="story-progress-fill" id="spf-${i}"></div></div>`).join('')}
        </div>
        <div class="story-viewer-header">
          <div class="story-viewer-user">
            <div class="story-vavatar" style="background:${getAvatarColor(story.displayName)};">
              ${story.avatarUrl ? `<img src="${typeof window._mxcToHttp === 'function' ? window._mxcToHttp(story.avatarUrl) : story.avatarUrl}" style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />` : story.displayName[0].toUpperCase()}
            </div>
            <div>
              <div style="font-size:13px;font-weight:600;color:#fff;">${escHtml(story.displayName)}</div>
              <div style="font-size:11px;color:rgba(255,255,255,0.6);">${timeAgo} · ${expiresIn}h left</div>
            </div>
          </div>
          <button class="story-close-btn" id="story-close">✕</button>
        </div>
        <div class="story-viewer-body" style="background:${story.bgColor};">
          <p class="story-viewer-text" style="color:${story.color};">${escHtml(story.text)}</p>
        </div>
        <div class="story-viewer-nav">
          <div class="story-nav-prev" id="story-prev"></div>
          <div class="story-nav-next" id="story-next"></div>
        </div>
      </div>`;

    overlay.querySelector('#story-close').addEventListener('click', () => { clearTimeout(timer); overlay.remove(); });
    overlay.querySelector('#story-prev').addEventListener('click', () => { if (current > 0) { current--; clearTimeout(timer); render(); } });
    overlay.querySelector('#story-next').addEventListener('click', () => advance());

    // Auto-advance
    clearTimeout(timer);
    const fill = overlay.querySelector('#spf-' + current);
    if (fill) {
      fill.style.transition = 'width ' + DURATION + 'ms linear';
      requestAnimationFrame(() => requestAnimationFrame(() => { fill.style.width = '100%'; }));
    }
    timer = setTimeout(advance, DURATION);
  }

  function advance() {
    clearTimeout(timer);
    if (current < stories.length - 1) { current++; render(); }
    else overlay.remove();
  }

  render();
  document.body.appendChild(overlay);
}

function _timeAgo(ts) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return Math.floor(hrs / 24) + 'd ago';
}

/* ──────────────────────────────────────────────
   Post composer
─────────────────────────────────────────────── */
export function showPostComposer(onPost) {
  document.getElementById('post-composer-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'post-composer-modal';
  overlay.className = 'modal-overlay visible';
  overlay.innerHTML = `
    <div class="rsm-box" style="width:520px;max-width:95vw;">
      <div class="rsm-header">
        <span class="rsm-title">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:6px;"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          New Post
        </span>
        <button class="rsm-close" id="post-close">✕</button>
      </div>
      <div style="padding:16px;">
        <input type="text" class="topic-input" id="post-title" placeholder="Post title…" maxlength="100"
          style="width:100%;margin-bottom:12px;font-size:15px;font-weight:600;box-sizing:border-box;" />
        <textarea class="topic-input" id="post-body" placeholder="Write your post…" rows="8"
          style="width:100%;resize:vertical;font-size:13px;line-height:1.6;box-sizing:border-box;"></textarea>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:12px;">
          <button class="rsm-remove-btn" id="post-cancel">Cancel</button>
          <button class="send-btn" id="post-submit" style="padding:6px 20px;">Publish Post</button>
        </div>
      </div>
    </div>`;

  overlay.querySelector('#post-close').addEventListener('click', () => overlay.remove());
  overlay.querySelector('#post-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#post-submit').addEventListener('click', () => {
    const title = overlay.querySelector('#post-title').value.trim();
    const body = overlay.querySelector('#post-body').value.trim();
    if (!title) { overlay.querySelector('#post-title').focus(); return; }
    if (!body) { overlay.querySelector('#post-body').focus(); return; }
    onPost(title, body);
    overlay.remove();
  });

  document.body.appendChild(overlay);
  setTimeout(() => overlay.querySelector('#post-title')?.focus(), 50);
}

/* ──────────────────────────────────────────────
   Working Hours / DND
─────────────────────────────────────────────── */
const WH_KEY = 'nx_working_hours';
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export function getWorkingHours() {
  try { return JSON.parse(localStorage.getItem(WH_KEY) || 'null'); } catch { return null; }
}

export function isInWorkingHours() {
  const wh = getWorkingHours();
  if (!wh || !wh.enabled) return true; // no schedule = always available
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  if (!wh.days.includes(day)) return false; // not a work day = DND
  const hhmm = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = wh.start.split(':').map(Number);
  const [eh, em] = wh.end.split(':').map(Number);
  const start = sh * 60 + sm;
  const end = eh * 60 + em;
  return hhmm >= start && hhmm < end;
}

export function showWorkingHoursModal() {
  document.getElementById('working-hours-modal')?.remove();
  const wh = getWorkingHours() || { enabled: false, start: '09:00', end: '18:00', days: [1,2,3,4,5] };

  const overlay = document.createElement('div');
  overlay.id = 'working-hours-modal';
  overlay.className = 'modal-overlay visible';

  overlay.innerHTML = `
    <div class="rsm-box" style="width:360px;">
      <div class="rsm-header">
        <span class="rsm-title">⏰ Working Hours & DND</span>
        <button class="rsm-close" id="wh-close">✕</button>
      </div>
      <div style="padding:16px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
          <span style="font-size:13px;color:var(--text-primary);font-weight:500;">Enable Working Hours</span>
          <label class="wh-toggle">
            <input type="checkbox" id="wh-enabled" ${wh.enabled ? 'checked' : ''} />
            <span class="wh-toggle-slider"></span>
          </label>
        </div>
        <div class="wh-settings" id="wh-settings" style="${wh.enabled ? '' : 'opacity:0.4;pointer-events:none;'}">
          <div style="font-size:12px;color:var(--text-secondary);margin-bottom:8px;">Notifications muted outside these hours</div>
          <div style="display:flex;gap:12px;align-items:center;margin-bottom:16px;">
            <div style="flex:1;">
              <label style="font-size:11px;color:var(--text-secondary);display:block;margin-bottom:4px;">Start</label>
              <input type="time" id="wh-start" value="${wh.start}" class="topic-input" style="width:100%;padding:6px 10px;" />
            </div>
            <span style="color:var(--text-secondary);margin-top:16px;">→</span>
            <div style="flex:1;">
              <label style="font-size:11px;color:var(--text-secondary);display:block;margin-bottom:4px;">End</label>
              <input type="time" id="wh-end" value="${wh.end}" class="topic-input" style="width:100%;padding:6px 10px;" />
            </div>
          </div>
          <div style="font-size:11px;color:var(--text-secondary);margin-bottom:8px;">Active days</div>
          <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:16px;">
            ${DAYS.map((d, i) => `
              <label class="wh-day-btn">
                <input type="checkbox" value="${i}" ${wh.days.includes(i) ? 'checked' : ''} class="wh-day-check" style="display:none;" />
                <span class="wh-day-label${wh.days.includes(i) ? ' active' : ''}">${d}</span>
              </label>`).join('')}
          </div>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px;">
          <button class="rsm-remove-btn" id="wh-cancel">Cancel</button>
          <button class="send-btn" id="wh-save" style="padding:6px 18px;font-size:13px;">Save</button>
        </div>
      </div>
    </div>`;

  // Toggle enabled
  overlay.querySelector('#wh-enabled').addEventListener('change', (e) => {
    overlay.querySelector('#wh-settings').style.cssText = e.target.checked ? '' : 'opacity:0.4;pointer-events:none;';
  });

  // Day button toggle
  overlay.querySelectorAll('.wh-day-check').forEach(cb => {
    cb.addEventListener('change', () => {
      cb.nextElementSibling.classList.toggle('active', cb.checked);
    });
  });

  overlay.querySelector('#wh-close').addEventListener('click', () => overlay.remove());
  overlay.querySelector('#wh-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  overlay.querySelector('#wh-save').addEventListener('click', () => {
    const enabled = overlay.querySelector('#wh-enabled').checked;
    const start = overlay.querySelector('#wh-start').value;
    const end = overlay.querySelector('#wh-end').value;
    const days = [...overlay.querySelectorAll('.wh-day-check:checked')].map(cb => parseInt(cb.value));
    const data = { enabled, start, end, days };
    localStorage.setItem(WH_KEY, JSON.stringify(data));
    overlay.remove();
    showToast(enabled ? '⏰ Working hours saved — DND active outside schedule' : 'Working hours disabled', 'success');
    window.dispatchEvent(new CustomEvent('nx:working-hours-updated'));
  });

  document.body.appendChild(overlay);
}

/* ──────────────────────────────────────────────
   Bookmarks
─────────────────────────────────────────────── */
const BOOKMARKS_KEY = 'nx_bookmarks';

function _getBookmarks() {
  try { return JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || '[]'); } catch { return []; }
}
function _saveBookmarks(bm) {
  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bm));
}
function _getBookmarkFolders() {
  const bm = _getBookmarks();
  const folders = ['General', ...new Set(bm.map(b => b.folder).filter(f => f && f !== 'General'))];
  return folders;
}

export function showBookmarkFolderPicker(msg) {
  document.getElementById('bookmark-folder-modal')?.remove();
  const folders = _getBookmarkFolders();
  const bm = _getBookmarks();
  const existing = bm.find(b => b.id === msg.id);

  const overlay = document.createElement('div');
  overlay.id = 'bookmark-folder-modal';
  overlay.className = 'modal-overlay visible';

  function render() {
    const folders = _getBookmarkFolders();
    overlay.innerHTML = `
      <div class="rsm-box">
        <div class="rsm-header">
          <span class="rsm-title">${existing ? 'Move Bookmark' : 'Add Bookmark'}</span>
          <button class="rsm-close" id="bfm-close">✕</button>
        </div>
        <div class="rsm-list" style="padding:8px 12px;">
          ${folders.map(f => `
            <button class="bfm-folder-btn${existing?.folder === f ? ' bfm-active' : ''}" data-folder="${escHtml(f)}">
              🗂 ${escHtml(f)}
              ${existing?.folder === f ? ' <span style="margin-left:auto;font-size:10px;color:var(--accent-text);">current</span>' : ''}
            </button>`).join('')}
          <div class="bfm-new-row">
            <input class="topic-input" id="bfm-new-input" placeholder="New folder name…" style="flex:1;" />
            <button class="pdf-nav-btn" id="bfm-new-btn">+ Add</button>
          </div>
          ${existing ? `<button class="bfm-remove-btn" id="bfm-remove">🗑 Remove bookmark</button>` : ''}
        </div>
      </div>`;

    overlay.querySelector('#bfm-close').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

    overlay.querySelectorAll('.bfm-folder-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const folder = btn.dataset.folder;
        const bm = _getBookmarks().filter(b => b.id !== msg.id);
        bm.unshift({
          id: msg.id,
          roomId: window._currentRoomId || '',
          body: msg.body || '',
          senderId: msg.senderId || '',
          ts: msg.ts || Date.now(),
          folder,
        });
        _saveBookmarks(bm);
        showToast('🔖 Bookmarked in ' + folder, 'success');
        overlay.remove();
      });
    });

    overlay.querySelector('#bfm-new-btn').addEventListener('click', () => {
      const name = overlay.querySelector('#bfm-new-input').value.trim();
      if (!name) return;
      const bm = _getBookmarks().filter(b => b.id !== msg.id);
      bm.unshift({ id: msg.id, roomId: window._currentRoomId || '', body: msg.body || '', senderId: msg.senderId || '', ts: msg.ts || Date.now(), folder: name });
      _saveBookmarks(bm);
      showToast('🔖 Bookmarked in ' + name, 'success');
      overlay.remove();
    });

    overlay.querySelector('#bfm-new-input')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') overlay.querySelector('#bfm-new-btn').click();
    });

    overlay.querySelector('#bfm-remove')?.addEventListener('click', () => {
      _saveBookmarks(_getBookmarks().filter(b => b.id !== msg.id));
      showToast('Bookmark removed', 'success');
      overlay.remove();
    });
  }

  render();
  document.body.appendChild(overlay);
}

export function showBookmarksPanel(onJump) {
  document.getElementById('bookmarks-panel')?.remove();
  const panel = document.createElement('div');
  panel.id = 'bookmarks-panel';
  panel.className = 'search-panel';

  let activeFolder = _getBookmarkFolders()[0] || 'General';

  function renderPanel() {
    const folders = _getBookmarkFolders();
    const bm = _getBookmarks();
    const folderBm = bm.filter(b => b.folder === activeFolder);

    panel.innerHTML = `
      <div class="search-panel-header">
        <span style="font-weight:600;font-size:14px;">🔖 Bookmarks</span>
        <button class="icon-btn" id="bookmarks-panel-close">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div class="rsm-tabs" style="padding:8px 12px 0;">
        ${folders.map(f => `<button class="rsm-tab${f === activeFolder ? ' rsm-tab-active' : ''}" data-folder="${escHtml(f)}">${escHtml(f)}</button>`).join('')}
      </div>
      <div class="search-panel-results" id="bookmarks-list">
        ${!folderBm.length ? '<div class="search-panel-empty">No bookmarks in this folder.</div>' : ''}
      </div>`;

    panel.querySelector('#bookmarks-panel-close').addEventListener('click', () => panel.remove());
    panel.querySelectorAll('.rsm-tab').forEach(tab => {
      tab.addEventListener('click', () => { activeFolder = tab.dataset.folder; renderPanel(); });
    });

    const list = panel.querySelector('#bookmarks-list');
    for (const entry of folderBm) {
      const senderShort = (entry.senderId || '').split(':')[0].replace('@', '');
      const item = document.createElement('div');
      item.className = 'search-result-item';
      item.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
          <div style="cursor:pointer;flex:1;" class="bm-jump">
            <div style="font-size:11px;color:var(--text-secondary);margin-bottom:3px;">${escHtml(senderShort)} · ${new Date(entry.ts).toLocaleDateString()}</div>
            <div style="font-size:13px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml((entry.body || '').slice(0, 120))}</div>
          </div>
          <button class="bm-remove-btn" data-id="${entry.id}" title="Remove bookmark" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);font-size:14px;flex-shrink:0;">✕</button>
        </div>`;
      item.querySelector('.bm-jump').addEventListener('click', () => {
        panel.remove();
        onJump?.(entry.id, entry.roomId);
      });
      item.querySelector('.bm-remove-btn').addEventListener('click', () => {
        _saveBookmarks(_getBookmarks().filter(b => b.id !== entry.id));
        item.remove();
        if (!panel.querySelectorAll('.search-result-item').length) {
          list.innerHTML = '<div class="search-panel-empty">No bookmarks in this folder.</div>';
        }
      });
      list.appendChild(item);
    }
  }

  renderPanel();
  const container = document.getElementById('chat-container') || document.body;
  container.appendChild(panel);
}

/* ──────────────────────────────────────────────
   Room topic
─────────────────────────────────────────────── */
export function updateRoomTopic(topic) {
  const textEl = document.getElementById('chat-topic-text');
  const topicRow = document.getElementById('chat-header-topic');
  if (!textEl || !topicRow) return;
  if (topic) {
    textEl.textContent = topic;
    topicRow.style.display = 'flex';
  } else {
    textEl.textContent = '';
    topicRow.style.display = 'flex'; // always show so pencil is accessible
  }
}

export function showTopicEditModal(currentTopic, onSave) {
  document.getElementById('topic-edit-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'topic-edit-modal';
  overlay.className = 'modal-overlay visible';
  overlay.innerHTML = `
    <div class="rsm-box" style="padding:20px;">
      <div class="rsm-header" style="margin-bottom:12px;">
        <span class="rsm-title">Room Topic</span>
        <button class="rsm-close" id="topic-modal-close">✕</button>
      </div>
      <input id="topic-input" type="text" class="topic-input"
        placeholder="Add a topic…" value="${escHtml(currentTopic || '')}" maxlength="250" />
      <div style="display:flex;gap:8px;margin-top:12px;justify-content:flex-end;">
        <button class="rsm-remove-btn" id="topic-cancel">Cancel</button>
        <button class="send-btn" id="topic-save" style="padding:6px 18px;font-size:13px;">Save</button>
      </div>
    </div>`;
  overlay.querySelector('#topic-modal-close').addEventListener('click', () => overlay.remove());
  overlay.querySelector('#topic-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#topic-save').addEventListener('click', () => {
    const val = overlay.querySelector('#topic-input').value.trim();
    onSave(val);
    overlay.remove();
  });
  overlay.querySelector('#topic-input').addEventListener('keydown', e => {
    if (e.key === 'Enter') { e.preventDefault(); overlay.querySelector('#topic-save').click(); }
    if (e.key === 'Escape') overlay.remove();
  });
  document.body.appendChild(overlay);
  setTimeout(() => overlay.querySelector('#topic-input')?.focus(), 50);
}

/* ──────────────────────────────────────────────
   Receipt avatars (tiny read-by faces)
─────────────────────────────────────────────── */
export function updateReceiptAvatars(eventId, readers) {
  const el = document.getElementById('receipt-avatars-' + eventId);
  if (!el) return;
  el.innerHTML = '';
  if (!readers || !readers.length) return;
  // Show up to 3 avatars, most recent readers first
  const sorted = [...readers].sort((a, b) => b.ts - a.ts).slice(0, 3);
  for (const r of sorted) {
    const av = document.createElement('div');
    av.className = 'receipt-avatar';
    av.title = r.displayName;
    if (r.avatarUrl) {
      const url = typeof window._mxcToHttp === 'function' ? window._mxcToHttp(r.avatarUrl) : r.avatarUrl;
      av.style.backgroundImage = 'url(' + url + ')';
      av.style.backgroundSize = 'cover';
      av.style.backgroundPosition = 'center';
    } else {
      av.textContent = (r.displayName || '?')[0].toUpperCase();
      av.style.background = getAvatarColor(r.displayName);
      av.style.color = '#fff';
      av.style.fontSize = '9px';
      av.style.display = 'flex';
      av.style.alignItems = 'center';
      av.style.justifyContent = 'center';
    }
    el.appendChild(av);
  }
}

/* ──────────────────────────────────────────────
   Reply button wiring (event delegation)
─────────────────────────────────────────────── */
export function wireReplyButtons(container, onReply) {
  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.reply-btn');
    if (!btn) return;
    onReply({ eventId: btn.dataset.eventId, senderId: btn.dataset.senderId, body: btn.dataset.body });
  });
}

/* ──────────────────────────────────────────────
   Reply-to bar above input
─────────────────────────────────────────────── */
export function setReplyingTo(msg) {
  document.getElementById('reply-to-bar')?.remove();
  if (!msg) return;
  const area = document.querySelector('.message-input-area');
  if (!area) return;
  const senderShort = (msg.senderId || '').split(':')[0].replace('@','');
  const bar = document.createElement('div');
  bar.id = 'reply-to-bar';
  bar.className = 'reply-to-bar';
  bar.innerHTML = `
    <div class="reply-to-content">
      <span class="reply-to-icon"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg></span>
      <div class="reply-to-text">
        <span class="reply-to-sender">${escHtml(senderShort)}</span>
        <span class="reply-to-body">${escHtml((msg.body||'').slice(0,100))}</span>
      </div>
    </div>
    <button class="reply-to-cancel" id="cancel-reply" title="Cancel">✕</button>
  `;
  area.insertBefore(bar, area.firstChild);
  document.getElementById('cancel-reply')?.addEventListener('click', () => {
    bar.remove();
    window.dispatchEvent(new CustomEvent('nx:reply-cancelled'));
  });
}

/* ──────────────────────────────────────────────
   Update existing message in place (after decryption)
─────────────────────────────────────────────── */
export function updateMessageInPlace(event) {
  const container = document.getElementById('messages-container');
  if (!container) return;

  const eventId = event.getId();
  if (!eventId) return;

  // Try to find by real event ID first
  let existing = container.querySelector('[data-event-id="' + eventId + '"]');

  // If not found, try by transaction ID (pending -> confirmed transition)
  if (!existing) {
    const txnId = event.getUnsigned()?.transaction_id || event.getTxnId?.();
    if (txnId) {
      existing = container.querySelector('[data-txn-id="' + txnId + '"]');
      if (existing) {
        existing.querySelectorAll('[data-event-id]').forEach(el => {
          el.dataset.eventId = eventId;
        });
        existing.dataset.eventId = eventId;
      }
    }
  }

  if (!existing) {
    appendMessage(event, null);
    return;
  }

  if (event.isDecryptionFailure?.()) return;

  const bubble = existing.querySelector('.message-bubble');
  if (!bubble) return;

  const content = event.getContent();
  const msgtype = content?.msgtype;
  const bodyEl = bubble.querySelector('.msg-body, div') || bubble;

  // File/media message — use shared helper
  const fileUrl = content.file?.url || content.url || content.external_url;
  if (['m.image','m.video','m.audio','m.file'].includes(msgtype) && fileUrl) {
    bodyEl.style.opacity = '';
    bodyEl.style.fontStyle = '';
    // External URL (GIF)
    if (fileUrl && !fileUrl.startsWith('mxc://') && msgtype === 'm.image') {
      const img = document.createElement('img');
      img.src = fileUrl; img.alt = content.body || 'Image'; img.className = 'msg-image';
      img.addEventListener('click', () => window.open(fileUrl, '_blank'));
      bodyEl.innerHTML = ''; bodyEl.appendChild(img);
    } else if (msgtype === 'm.image') {
      const img = document.createElement('img');
      img.alt = content.body || 'Image'; img.className = 'msg-image';
      img.style.background = 'var(--bg-elevated)';
      bodyEl.innerHTML = ''; bodyEl.appendChild(img);
      decryptAndBlobUrl(event).then(blobUrl => {
        if (blobUrl) { img.src = blobUrl; img.addEventListener('click', () => window.open(blobUrl, '_blank')); }
      }).catch(() => {});
    } else if (msgtype === 'm.video') {
      const video = document.createElement('video');
      video.src = mxcToHttp(fileUrl); video.controls = true; video.className = 'msg-video';
      bodyEl.innerHTML = ''; bodyEl.appendChild(video);
    } else if (msgtype === 'm.audio') {
      const isVoice = content?.['org.matrix.msc3245.voice'] !== undefined;
      const src = mxcToHttp(fileUrl);
      bodyEl.innerHTML = '';
      if (isVoice) {
        bodyEl.appendChild(buildVoicePlayer(src, content?.info?.duration || 0));
      } else {
        const audio = document.createElement('audio');
        audio.src = src; audio.controls = true; audio.className = 'msg-audio';
        bodyEl.appendChild(audio);
      }
    } else if (msgtype === 'm.file') {
      const url = mxcToHttp(fileUrl);
      const size = content.info?.size ? formatFileSize(content.info.size) : '';
      const card = document.createElement('a');
      card.href = url; card.target = '_blank'; card.className = 'msg-file-card';
      card.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <div class="msg-file-info">
          <span class="msg-file-name">${escHtml(content.body || 'File')}</span>
          ${size ? `<span class="msg-file-size">${size}</span>` : ''}
        </div>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
      bodyEl.innerHTML = ''; bodyEl.appendChild(card);
    }
    return;
  }

  // Text message
  if (content?.body) {
    bodyEl.textContent = content.body;
    bodyEl.style.opacity = '';
    bodyEl.style.fontStyle = '';
  }
}

/* ──────────────────────────────────────────────
   Scroll helpers
─────────────────────────────────────────────── */
function scrollToBottom(container, smooth = false) {
  if (!container) return;
  container.scrollTo({
    top: container.scrollHeight,
    behavior: smooth ? 'smooth' : 'instant',
  });
}

/* ──────────────────────────────────────────────
   Date/time formatters
─────────────────────────────────────────────── */
function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function formatDay(ts) {
  const date = new Date(ts);
  const now  = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (isSameDay(date, now))        return 'Today';
  if (isSameDay(date, yesterday))  return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

/* ──────────────────────────────────────────────
   XSS protection
─────────────────────────────────────────────── */
function escHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}


/* ──────────────────────────────────────────────
   NexAI Panel
─────────────────────────────────────────────── */
let _nexaiPanelOpen = false;
let _nexaiHistory   = [];

export function toggleNexAIPanel(onNexAI) {
  _nexaiPanelOpen ? closeNexAIPanel() : openNexAIPanel(onNexAI);
}

export function openNexAIPanel(onNexAI) {
  if (document.getElementById('nexai-panel')) return;
  _nexaiPanelOpen = true;

  const chatArea = document.getElementById('chat-area');


  const panel = document.createElement('div');
  panel.id = 'nexai-panel';
  panel.className = 'nexai-panel';
  panel.innerHTML = `
    <div class="nexai-panel-header">
      <div class="nexai-panel-title">
        <span class="nexai-panel-icon">${Icons.nexai}</span>
        <span>NexAI</span>
        <span class="nexai-panel-badge">Beta</span>
      </div>
      <div style="display:flex;gap:6px;align-items:center;">
        <button class="icon-btn" id="nexai-clear-btn" title="Clear">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
        </button>
        <button class="icon-btn" id="nexai-close-btn" title="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>
    <div class="nexai-panel-messages" id="nexai-panel-messages">
      <div class="nexai-panel-welcome">
        <div class="nexai-welcome-icon">${Icons.nexai}</div>
        <div class="nexai-welcome-title">Ask NexAI anything</div>
        <div class="nexai-welcome-sub">AI assistant with context from this conversation</div>
        <div class="nexai-suggestions">
          <button class="nexai-suggestion" data-text="Summarise this conversation">📋 Summarise chat</button>
          <button class="nexai-suggestion" data-text="What are the action items from this conversation?">✅ Action items</button>
          <button class="nexai-suggestion" data-text="Draft a reply to the last message">✏️ Draft a reply</button>
        </div>
      </div>
    </div>
    <div class="nexai-panel-input-area">
      <div class="nexai-panel-input-wrapper">
        <textarea class="nexai-panel-textarea" id="nexai-panel-input" placeholder="${t('nexai.placeholder')}" rows="1"></textarea>
        <button class="nexai-panel-send-btn" id="nexai-panel-send" disabled>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(panel);
  document.getElementById('nexai-btn')?.classList.add('active');
  document.getElementById('nexai-close-btn')?.addEventListener('click', closeNexAIPanel);
  document.getElementById('nexai-clear-btn')?.addEventListener('click', () => {
    const msgs = document.getElementById('nexai-panel-messages');
    if (msgs) msgs.innerHTML = '';
    _nexaiHistory = [];
  });

  panel.querySelectorAll('.nexai-suggestion').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = document.getElementById('nexai-panel-input');
      if (input) { input.value = btn.dataset.text; input.dispatchEvent(new Event('input')); }
      _sendNexAIPanelMessage(onNexAI);
    });
  });

  const panelInput = document.getElementById('nexai-panel-input');
  const panelSend  = document.getElementById('nexai-panel-send');
  panelInput?.addEventListener('input', () => {
    autoResizeTextarea(panelInput);
    panelSend.disabled = !panelInput.value.trim();
  });
  panelInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); _sendNexAIPanelMessage(onNexAI); }
  });
  panelSend?.addEventListener('click', () => _sendNexAIPanelMessage(onNexAI));
  setTimeout(() => panelInput?.focus(), 100);
}

export function closeNexAIPanel() {
  document.getElementById('nexai-panel')?.remove();
  _nexaiPanelOpen = false;

  document.getElementById('nexai-btn')?.classList.remove('active');
}

async function _sendNexAIPanelMessage(onNexAI) {
  const input = document.getElementById('nexai-panel-input');
  const send  = document.getElementById('nexai-panel-send');
  if (!input) return;
  const text = input.value.trim();
  if (!text || !onNexAI) return;

  input.value = ''; send.disabled = true; autoResizeTextarea(input);
  document.querySelector('.nexai-panel-welcome')?.remove();

  const msgs = document.getElementById('nexai-panel-messages');
  if (!msgs) return;

  const userBubble = document.createElement('div');
  userBubble.className = 'nexai-msg nexai-msg-user';
  userBubble.textContent = text;
  msgs.appendChild(userBubble);
  msgs.scrollTo({ top: msgs.scrollHeight, behavior: 'smooth' });
  _nexaiHistory.push({ role: 'user', content: text });

  const aiBubble = document.createElement('div');
  aiBubble.className = 'nexai-msg nexai-msg-ai';
  aiBubble.innerHTML = `<div class="nexai-msg-header"><span class="nexai-icon">${Icons.nexai}</span><span class="nexai-label">NexAI</span></div><div class="typing-dots"><div class="typing-dot"></div><div class="typing-dot"></div><div class="typing-dot"></div></div>`;
  msgs.appendChild(aiBubble);
  msgs.scrollTo({ top: msgs.scrollHeight, behavior: 'smooth' });

  try {
    await onNexAI(text, {
      history: _nexaiHistory.slice(-20),
      onChunk: (fullText) => {
        aiBubble.innerHTML = `<div class="nexai-msg-header"><span class="nexai-icon">${Icons.nexai}</span><span class="nexai-label">NexAI</span></div><div class="nexai-msg-body"></div>`;
        const body = aiBubble.querySelector('.nexai-msg-body');
        if (body) body.textContent = fullText;
        msgs.scrollTo({ top: msgs.scrollHeight, behavior: 'instant' });
      },
      onDone: (fullText) => {
        _nexaiHistory.push({ role: 'assistant', content: fullText });
        send.disabled = false;
        setTimeout(() => input?.focus(), 50);
      },
      onError: (err) => {
        aiBubble.innerHTML = `<div class="nexai-msg-error">Error: ${escHtml(err)}</div>`;
        send.disabled = false;
      },
    });
  } catch (_) { send.disabled = false; }
}

/* ──────────────────────────────────────────────
   File upload progress
─────────────────────────────────────────────── */
/* ──────────────────────────────────────────────
   Link preview
─────────────────────────────────────────────── */
const _linkPreviewCache = {};

export async function attachLinkPreview(wrapper, messageBody, nexaiToken) {
  if (!messageBody || !nexaiToken) return;

  // Only attach to plain text message wrappers — skip file/image/video/pdf wrappers
  if (wrapper.querySelector('.msg-file-card, .msg-pdf-wrap, .msg-video, img.msg-img')) return;

  const urlMatch = messageBody.match(/https?:\/\/[^\s<>"{}|\^`[\]]{10,}/);
  if (!urlMatch) return;
  const url = urlMatch[0].replace(/[.,;!?)]+$/, '');

  // Don't preview Matrix media or own server
  if (url.includes('ethosserver.com') || url.includes('mxc://')) return;

  // Check if preview already attached
  if (wrapper.querySelector('.link-preview-card')) return;

  try {
    let data = _linkPreviewCache[url];
    if (!data) {
      const res = await fetch('/api/link-preview?url=' + encodeURIComponent(url), {
        headers: { 'Authorization': 'Bearer ' + nexaiToken }
      });
      if (!res.ok) return;
      data = await res.json();
      if (!data || data.error || !data.title) return;
      _linkPreviewCache[url] = data;
    }
    if (!data.title) return;

    const card = document.createElement('a');
    card.href = url;
    card.target = '_blank';
    card.rel = 'noopener noreferrer';
    card.className = 'link-preview-card';
    card.innerHTML = `
      ${data.image ? `<div class="lp-image-wrap"><img class="lp-image" src="${escHtml(data.image)}" alt="" loading="lazy" onerror="this.parentElement.style.display='none'" /></div>` : ''}
      <div class="lp-body">
        <div class="lp-site">${escHtml(data.siteName || new URL(url).hostname)}</div>
        <div class="lp-title">${escHtml(data.title)}</div>
        ${data.description ? `<div class="lp-desc">${escHtml(data.description.slice(0, 120))}${data.description.length > 120 ? '…' : ''}</div>` : ''}
      </div>`;

    // wrapper param is .message-row — inner flex div is first child
    const innerWrapper = wrapper.firstElementChild;
    const target = innerWrapper || wrapper;
    const timeEl = target.querySelector('.message-time');
    card.style.cssText = 'align-self:stretch;max-width:360px;margin-top:4px;';
    if (timeEl) {
      target.insertBefore(card, timeEl);
    } else {
      target.appendChild(card);
    }
  } catch (err) {
    console.warn('[lp] error:', err.message);
  }
}

/* ──────────────────────────────────────────────
   PDF.js preview renderer
─────────────────────────────────────────────── */
let _pdfjsLib = null;
async function _getPdfJs() {
  if (_pdfjsLib) return _pdfjsLib;
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      _pdfjsLib = window.pdfjsLib;
      resolve(_pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export function flushPdfRenders() {
  const pending = window._pendingPdfRenders || [];
  window._pendingPdfRenders = [];
  for (const { canvasId, url, fileName } of pending) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) continue;
    // Wire click handlers
    const wrap = canvas.closest('.msg-pdf-wrap');
    wrap?.querySelector('.msg-pdf-canvas-wrap')?.addEventListener('click', () => _showPdfViewer(url, fileName));
    wrap?.querySelector('.msg-pdf-expand')?.addEventListener('click', (e) => {
      e.stopPropagation();
      _showPdfViewer(url, fileName);
    });
    // Only render if canvas is empty
    if (canvas.width === 0) _renderPdfPreview(url, canvasId);
  }
}

async function _showPdfViewer(url, fileName) {
  document.getElementById('pdf-viewer-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'pdf-viewer-modal';
  overlay.className = 'modal-overlay visible';
  overlay.style.cssText = 'z-index:10000;flex-direction:column;padding:0;background:rgba(0,0,0,0.92);';
  overlay.innerHTML = `
    <div class="pdf-viewer-bar">
      <span class="pdf-viewer-name">${escHtml(fileName)}</span>
      <div style="display:flex;gap:8px;align-items:center;">
        <button class="pdf-nav-btn" id="pdf-prev">‹ Prev</button>
        <span class="pdf-page-info" id="pdf-page-info">Page 1</span>
        <button class="pdf-nav-btn" id="pdf-next">Next ›</button>
        <a href="${url}" download="${escHtml(fileName)}" class="pdf-nav-btn">↓ Download</a>
        <button class="pdf-nav-btn" id="pdf-close">✕</button>
      </div>
    </div>
    <div class="pdf-viewer-body" id="pdf-viewer-body">
      <div class="msg-pdf-loading" style="color:#aaa;font-size:14px;">Loading…</div>
    </div>`;

  document.body.appendChild(overlay);
  overlay.querySelector('#pdf-close').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  const body = overlay.querySelector('#pdf-viewer-body');
  const pageInfo = overlay.querySelector('#pdf-page-info');
  let currentPage = 1;
  let pdfDoc = null;

  async function renderPage(num) {
    body.innerHTML = '<div class="msg-pdf-loading" style="color:#aaa;">Rendering…</div>';
    const page = await pdfDoc.getPage(num);
    const maxW = Math.min(window.innerWidth * 0.9, 860);
    const viewport = page.getViewport({ scale: 1 });
    const scale = maxW / viewport.width;
    const scaled = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = scaled.width;
    canvas.height = scaled.height;
    canvas.style.cssText = 'max-width:100%;border-radius:4px;display:block;';
    body.innerHTML = '';
    body.appendChild(canvas);
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaled }).promise;
    pageInfo.textContent = `Page ${num} of ${pdfDoc.numPages}`;
  }

  overlay.querySelector('#pdf-prev').addEventListener('click', () => {
    if (currentPage > 1) { currentPage--; renderPage(currentPage); }
  });
  overlay.querySelector('#pdf-next').addEventListener('click', () => {
    if (pdfDoc && currentPage < pdfDoc.numPages) { currentPage++; renderPage(currentPage); }
  });
  document.addEventListener('keydown', function onKey(e) {
    if (!document.getElementById('pdf-viewer-modal')) { document.removeEventListener('keydown', onKey); return; }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') overlay.querySelector('#pdf-next').click();
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') overlay.querySelector('#pdf-prev').click();
    if (e.key === 'Escape') overlay.remove();
  });

  try {
    const pdfjsLib = await _getPdfJs();
    const res = await fetch(url);
    const ab = await res.arrayBuffer();
    pdfDoc = await pdfjsLib.getDocument({ data: ab }).promise;
    await renderPage(1);
  } catch (err) {
    body.innerHTML = '<div style="color:#f66;padding:20px;">Failed to load PDF</div>';
  }
}

async function _renderPdfPreview(url, canvasId) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  const loadingEl = canvas.parentElement?.querySelector('.msg-pdf-loading');
  try {
    const pdfjsLib = await _getPdfJs();
    // Fetch binary ourselves so auth token in URL is used correctly
    const res = await fetch(url);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const arrayBuffer = await res.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const scale = Math.min(320 / viewport.width, 400 / viewport.height);
    const scaled = page.getViewport({ scale });
    canvas.width = scaled.width;
    canvas.height = scaled.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaled }).promise;
    if (loadingEl) loadingEl.remove();
    const pagesEl = document.getElementById('pages-' + canvasId);
    if (pagesEl) pagesEl.textContent = pdf.numPages + (pdf.numPages === 1 ? ' page' : ' pages');
  } catch (err) {
    if (loadingEl) loadingEl.textContent = 'Preview unavailable';
    console.warn('[pdf] render failed:', err);
  }
}

/* ──────────────────────────────────────────────
   Pre-send file preview
─────────────────────────────────────────────── */
export function showFileSendPreview(file, onConfirm, onCancel) {
  document.getElementById('file-send-preview')?.remove();
  const bar = document.createElement('div');
  bar.id = 'file-send-preview';
  bar.className = 'file-send-preview';

  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const isAudio = file.type.startsWith('audio/');
  const isPdf   = file.type === 'application/pdf';
  const size    = formatFileSize(file.size);

  bar.innerHTML = `
    <div class="fsp-inner">
      <div class="fsp-preview-area" id="fsp-preview-area"></div>
      <div class="fsp-info">
        <span class="fsp-name">${escHtml(file.name)}</span>
        <span class="fsp-size">${size}</span>
      </div>
      <div class="fsp-actions">
        <button class="rsm-remove-btn" id="fsp-cancel">Cancel</button>
        <button class="send-btn" id="fsp-send" style="padding:6px 18px;font-size:13px;">Send</button>
      </div>
    </div>`;

  const previewArea = bar.querySelector('#fsp-preview-area');

  if (isImage) {
    const img = document.createElement('img');
    img.className = 'fsp-img';
    img.src = URL.createObjectURL(file);
    previewArea.appendChild(img);
  } else if (isVideo) {
    const video = document.createElement('video');
    video.className = 'fsp-video';
    video.src = URL.createObjectURL(file);
    video.muted = true;
    video.preload = 'metadata';
    // Draw first frame as poster via canvas
    video.addEventListener('loadeddata', () => {
      video.currentTime = 0.5;
    });
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const poster = canvas.toDataURL('image/jpeg');
      video.poster = poster;
    });
    video.controls = true;
    previewArea.appendChild(video);
  } else if (isAudio) {
    const audio = document.createElement('audio');
    audio.className = 'fsp-audio';
    audio.src = URL.createObjectURL(file);
    audio.controls = true;
    previewArea.appendChild(audio);
    // Draw waveform
    _drawAudioWaveform(file, previewArea);
  } else if (isPdf) {
    previewArea.innerHTML = `
      <div class="fsp-pdf-card">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>
        <span>PDF Document</span>
      </div>`;
  } else {
    previewArea.innerHTML = `
      <div class="fsp-pdf-card">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <span>${escHtml(file.type || 'File')}</span>
      </div>`;
  }

  bar.querySelector('#fsp-cancel').addEventListener('click', () => { bar.remove(); onCancel?.(); });
  bar.querySelector('#fsp-send').addEventListener('click', () => { bar.remove(); onConfirm(file); });

  const inputArea = document.querySelector('.message-input-area');
  if (inputArea) inputArea.insertBefore(bar, inputArea.firstChild);
}

function _drawAudioWaveform(file, container) {
  const canvas = document.createElement('canvas');
  canvas.className = 'fsp-waveform';
  canvas.width = 220; canvas.height = 40;
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  const reader = new FileReader();
  reader.onload = async (e) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const buffer = await audioCtx.decodeAudioData(e.target.result);
      const data = buffer.getChannelData(0);
      const step = Math.ceil(data.length / canvas.width);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'var(--accent)';
      ctx.lineWidth = 1.5;
      for (let i = 0; i < canvas.width; i++) {
        let max = 0;
        for (let j = 0; j < step; j++) max = Math.max(max, Math.abs(data[i * step + j] || 0));
        const h = max * canvas.height;
        const y = (canvas.height - h) / 2;
        ctx.beginPath();
        ctx.moveTo(i, y);
        ctx.lineTo(i, y + h);
        ctx.stroke();
      }
    } catch (_) {}
  };
  reader.readAsArrayBuffer(file);
}

export function setFileUploadProgress(active, fileName) {
  const wrapper = document.querySelector('.message-input-wrapper');
  if (!wrapper) return;
  let bar = document.getElementById('upload-progress-bar');
  if (active) {
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'upload-progress-bar';
      bar.className = 'upload-progress';
      bar.innerHTML = `
        <div class="spinner" style="width:14px;height:14px;border-color:rgba(108,62,244,0.3);border-top-color:#6c3ef4;"></div>
        <span>Uploading ${escHtml(fileName || 'file')}…</span>
      `;
      wrapper.parentNode.insertBefore(bar, wrapper);
    }
    const sendBtn = document.getElementById('send-btn');
    const attachBtn = document.getElementById('attach-btn');
    if (sendBtn) sendBtn.disabled = true;
    if (attachBtn) attachBtn.disabled = true;
  } else {
    bar?.remove();
    const attachBtn = document.getElementById('attach-btn');
    if (attachBtn) attachBtn.disabled = false;
  }
}

/* ──────────────────────────────────────────────
   Message action menu
─────────────────────────────────────────────── */
function showMessageActionMenu(anchor, msg, isOwn, wrapper) {
  document.querySelector('.msg-action-menu')?.remove();

  const menu = document.createElement('div');
  menu.className = 'msg-action-menu';

  const pinnedIds = window._currentRoomPinnedIds || [];
  const isPinned = pinnedIds.includes(msg.id);
  const starKey = 'nx_starred_' + (window._currentRoomId || '');
  const starList = JSON.parse(localStorage.getItem(starKey) || '[]');
  const isStarred = starList.includes(msg.id);
  const items = [
    { icon: '📋', label: 'Copy text', action: 'copy' },
    { icon: '↩️', label: 'Reply', action: 'reply' },
    { icon: '↗️', label: 'Forward', action: 'forward' },
    { icon: isPinned ? '📌' : '📌', label: isPinned ? 'Unpin' : 'Pin message', action: 'pin' },
    { icon: isStarred ? '⭐' : '☆', label: isStarred ? 'Unstar' : 'Star message', action: 'star' },
    { icon: '🔖', label: 'Bookmark', action: 'bookmark' },
    { icon: '💬', label: 'Open thread', action: 'thread' },
    { icon: '🌐', label: t('msg.translate') || 'Translate', action: 'translate' },
  ];
  if (isOwn) {
    items.push({ icon: '✏️', label: 'Edit', action: 'edit' });
    items.push({ icon: '🗑️', label: 'Delete', action: 'delete', danger: true });
  }

  for (const item of items) {
    const btn = document.createElement('button');
    btn.className = 'msg-action-item' + (item.danger ? ' danger' : '');
    btn.innerHTML = `<span>${item.icon}</span><span>${item.label}</span>`;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.remove();
      if (item.action === 'copy') {
        wrapper.dispatchEvent(new CustomEvent('nx:copy', { bubbles: true, detail: { body: msg.body } }));
      } else if (item.action === 'reply') {
        wrapper.dispatchEvent(new CustomEvent('nx:reply-click', { bubbles: true, detail: { eventId: msg.id, senderId: msg.senderId, body: msg.body } }));
      } else if (item.action === 'forward') {
        wrapper.dispatchEvent(new CustomEvent('nx:forward', { bubbles: true, detail: { body: msg.body, senderId: msg.senderId } }));
      } else if (item.action === 'thread') {
        wrapper.dispatchEvent(new CustomEvent('nx:open-thread', {
          bubbles: true, detail: { eventId: msg.id }
        }));
      } else if (item.action === 'star') {
        const k = 'nx_starred_' + (window._currentRoomId || '');
        const l = JSON.parse(localStorage.getItem(k) || '[]');
        const i = l.indexOf(msg.id);
        if (i >= 0) l.splice(i, 1); else l.unshift(msg.id);
        localStorage.setItem(k, JSON.stringify(l.slice(0, 200)));
        showToast(i >= 0 ? 'Unstarred' : '⭐ Starred', 'success');
      } else if (item.action === 'bookmark') {
        showBookmarkFolderPicker(msg);
      } else if (item.action === 'pin') {
        const pinned = (window._currentRoomPinnedIds || []).includes(msg.id);
        wrapper.dispatchEvent(new CustomEvent('nx:pin', { bubbles: true, detail: { eventId: msg.id, isPinned: pinned } }));
      } else if (item.action === 'edit') {
        wrapper.dispatchEvent(new CustomEvent('nx:edit', { bubbles: true, detail: { eventId: msg.id, currentBody: msg.body } }));
      } else if (item.action === 'translate') {
        wrapper.dispatchEvent(new CustomEvent('nx:action', { bubbles: true, detail: { action: 'translate', eventId: msg.id } }));
      } else if (item.action === 'delete') {
        if (confirm('Delete this message?')) {
          wrapper.dispatchEvent(new CustomEvent('nx:delete', { bubbles: true, detail: { eventId: msg.id } }));
          const row = wrapper.closest('.message-row');
          if (row) {
            const bodyEl = row.querySelector('.message-bubble div:not(.reply-preview)');
            if (bodyEl) { bodyEl.textContent = 'Message deleted'; bodyEl.style.opacity = '0.5'; bodyEl.style.fontStyle = 'italic'; }
          }
        }
      }
    });
    menu.appendChild(btn);
  }

  const rect = anchor.getBoundingClientRect();
  menu.style.top = (rect.bottom + 4) + 'px';
  menu.style.left = isOwn ? Math.max(4, rect.right - 140) + 'px' : rect.left + 'px';
  document.body.appendChild(menu);

  setTimeout(() => {
    document.addEventListener('click', () => menu.remove(), { once: true });
  }, 10);
}

export function showEditInput(eventId, currentBody, onSave) {
  const container = document.getElementById('messages-container');
  const row = container?.querySelector(`.message-row[data-event-id="${eventId}"]`);
  if (!row) return;

  const bubble = row.querySelector('.message-bubble');
  if (!bubble) return;

  const bodyEl = bubble.querySelector('div:not(.reply-preview)') || bubble;
  const origText = bodyEl.textContent;

  const input = document.createElement('textarea');
  input.className = 'edit-input';
  input.value = currentBody;
  input.rows = 1;
  bodyEl.replaceWith(input);
  input.focus();
  input.setSelectionRange(input.value.length, input.value.length);

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = input.scrollHeight + 'px';
  });
  input.dispatchEvent(new Event('input'));

  const saveEdit = () => {
    const newBody = input.value.trim();
    const newDiv = document.createElement('div');
    if (newBody && newBody !== currentBody) {
      newDiv.textContent = newBody;
      input.replaceWith(newDiv);
      onSave(newBody);
    } else {
      newDiv.textContent = origText;
      input.replaceWith(newDiv);
    }
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') {
      const d = document.createElement('div');
      d.textContent = origText;
      input.replaceWith(d);
    }
  });
  input.addEventListener('blur', saveEdit);
}

/* ──────────────────────────────────────────────
   Forward message modal
─────────────────────────────────────────────── */
export function showForwardModal(rooms, onForward) {
  document.getElementById('forward-modal')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'forward-modal';

  overlay.innerHTML = `
    <div class="modal">
      <h2 class="modal-title">Forward message</h2>
      <p class="modal-subtitle">Choose a conversation to forward to.</p>
      <input type="text" class="form-input" id="forward-search" placeholder="${t('sidebar.search')}" style="margin-bottom:12px;" />
      <div class="forward-room-list" id="forward-room-list"></div>
      <div class="modal-actions" style="margin-top:12px;">
        <button class="btn btn-secondary" id="forward-cancel">${t('btn.cancel')}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  const listEl = overlay.querySelector('#forward-room-list');

  function renderList(filter) {
    listEl.innerHTML = '';
    const filtered = filter
      ? rooms.filter(r => r.name.toLowerCase().includes(filter.toLowerCase()))
      : rooms;

    for (const room of filtered.slice(0, 20)) {
      const item = document.createElement('button');
      item.className = 'forward-room-item';
      item.innerHTML = `
        <div class="forward-room-avatar" style="background:${getAvatarColor(room.name)}">${getInitials(room.name)}</div>
        <span class="forward-room-name">${escHtml(room.name)}</span>
      `;
      item.addEventListener('click', () => {
        overlay.remove();
        onForward(room.roomId);
      });
      listEl.appendChild(item);
    }

    if (!filtered.length) {
      listEl.innerHTML = '<div style="text-align:center;padding:20px;color:var(--text-muted);font-size:13px;">No conversations found</div>';
    }
  }

  renderList('');

  overlay.querySelector('#forward-search').addEventListener('input', (e) => {
    renderList(e.target.value);
  });

  overlay.querySelector('#forward-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  setTimeout(() => overlay.querySelector('#forward-search')?.focus(), 100);
}


/* ──────────────────────────────────────────────
   Search panel
─────────────────────────────────────────────── */
export function showSearchPanel(roomId, onSearch, onJumpTo) {
  closeSearchPanel();

  const panel = document.createElement('div');
  panel.id = 'search-panel';
  panel.className = 'search-panel';
  panel.innerHTML = `
    <div class="search-panel-header">
      <span class="search-panel-title">${t('search.title')}</span>
      <button class="icon-btn" id="search-panel-close">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="search-panel-input-wrap">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;color:var(--text-muted)"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <input class="search-panel-input" id="search-panel-input" placeholder="Search in this conversation…" type="text" />
    </div>
    <div class="search-panel-results" id="search-panel-results">
      <div class="search-panel-empty">Type to search messages</div>
    </div>
  `;

  document.body.appendChild(panel);
  document.getElementById('search-panel-close')?.addEventListener('click', closeSearchPanel);

  const input = document.getElementById('search-panel-input');
  const results = document.getElementById('search-panel-results');
  let debounceTimer;

  input?.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const q = input.value.trim();
    if (!q) {
      results.innerHTML = '<div class="search-panel-empty">Type to search messages</div>';
      return;
    }
    results.innerHTML = '<div class="search-panel-empty">Searching…</div>';
    debounceTimer = setTimeout(async () => {
      const hits = await onSearch(q);
      if (!hits.length) {
        results.innerHTML = '<div class="search-panel-empty">No results found</div>';
        return;
      }
      results.innerHTML = `<div style="font-size:11px;color:var(--text-muted);padding:8px 12px 4px;font-weight:500;">${hits.length} result${hits.length !== 1 ? 's' : ''}</div>`;
      for (const hit of hits) {
        const item = document.createElement('div');
        item.className = 'search-result-item';
        const name = hit.senderName || hit.senderId?.split(':')[0].replace('@','') || 'Unknown';
        const date = new Date(hit.ts);
        const timeStr = date.toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
        const safeQ = q.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
        const highlighted = escHtml(hit.body).replace(
          new RegExp(safeQ, 'gi'),
          m => `<mark style="background:var(--accent-glow);color:var(--accent-text);border-radius:3px;padding:0 2px;">${m}</mark>`
        );
        const roomBadge = hit.roomName && hit.roomName !== 'undefined'
          ? `<span style="font-size:10px;background:var(--bg-hover);color:var(--text-muted);padding:2px 6px;border-radius:999px;margin-left:4px;">💬 ${escHtml(hit.roomName)}</span>`
          : '';
        const encBadge = hit.encrypted
          ? `<span style="font-size:10px;color:var(--accent-text);" title="End-to-end encrypted">🔒</span>`
          : '';
        item.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;flex-wrap:wrap;gap:4px;">
            <div style="display:flex;align-items:center;gap:4px;">
              <span class="search-result-sender">${escHtml(name)}</span>
              ${encBadge}
              ${roomBadge}
            </div>
            <span class="search-result-time">${timeStr}</span>
          </div>
          <div class="search-result-body">${highlighted}</div>
        `;
        item.addEventListener('click', () => {
          closeSearchPanel();
          onJumpTo(hit.eventId);
        });
        results.appendChild(item);
      }
    }, 400);
  });

  setTimeout(() => input?.focus(), 100);
}

export function closeSearchPanel() {
  document.getElementById('search-panel')?.remove();
}

/* ──────────────────────────────────────────────
   Profile modal
─────────────────────────────────────────────── */
export function showProfileModal(currentName, currentAvatarUrl, onSave) {
  document.getElementById('profile-modal')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'profile-modal';

  const avatarHtml = currentAvatarUrl
    ? `<img src="${escHtml(currentAvatarUrl)}" class="profile-avatar-preview" id="avatar-preview" />`
    : `<div class="profile-avatar-preview profile-avatar-placeholder" id="avatar-preview">${getInitials(currentName)}</div>`;

  overlay.innerHTML = `
    <div class="modal">
      <h2 class="modal-title">${t('profile.edit')}</h2>
      <div class="profile-avatar-wrap">
        ${avatarHtml}
        <button class="btn btn-secondary profile-avatar-change" id="avatar-change-btn">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
          Change photo
        </button>
        <input type="file" id="avatar-file-input" style="display:none" accept="image/*" />
      </div>
      <div class="form-group" style="margin-top:16px;">
        <label class="form-label">Display Name</label>
        <input type="text" class="form-input" id="profile-name-input" value="${escHtml(currentName || '')}" placeholder="Your name" />
      </div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="profile-cancel">${t('btn.cancel')}</button>
        <button class="btn btn-primary" id="profile-save">Save changes</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  overlay.querySelector('#profile-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  // Avatar upload
  const avatarFileInput = overlay.querySelector('#avatar-file-input');
  overlay.querySelector('#avatar-change-btn')?.addEventListener('click', () => avatarFileInput?.click());

  let newAvatarFile = null;
  avatarFileInput?.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (!file) return;
    newAvatarFile = file;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const preview = document.getElementById('avatar-preview');
      if (preview) {
        preview.outerHTML = `<img src="${ev.target.result}" class="profile-avatar-preview" id="avatar-preview" />`;
      }
    };
    reader.readAsDataURL(file);
  });

  // Save
  overlay.querySelector('#profile-save')?.addEventListener('click', async () => {
    const saveBtn = overlay.querySelector('#profile-save');
    saveBtn.textContent = 'Saving…';
    saveBtn.disabled = true;
    const newName = overlay.querySelector('#profile-name-input')?.value.trim();
    await onSave(newName, newAvatarFile);
    overlay.remove();
  });

  setTimeout(() => overlay.querySelector('#profile-name-input')?.focus(), 100);
}

/* ──────────────────────────────────────────────
   Presence setter (called from dropdown)
─────────────────────────────────────────────── */
window.setPresence = function(status, btn) {
  const COLORS = { online: 'var(--green)', unavailable: '#f59e0b', busy: '#ef4444', offline: '#6b7280' };
  const presence = document.querySelector('.user-avatar-presence');
  if (presence) presence.style.background = COLORS[status] || COLORS.online;
  document.querySelector('#profile-dropdown')?.classList.remove('visible');
  // Dispatch event for nexchat.js to handle
  window.dispatchEvent(new CustomEvent('nx:set-presence', { detail: { status } }));
};

/* ──────────────────────────────────────────────
   Notification settings modal
─────────────────────────────────────────────── */
export function showNotifSettings(getSettings, saveSettings, requestPermission, requestPush) {
  document.getElementById('notif-modal')?.remove();

  const s = getSettings();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'notif-modal';

  overlay.innerHTML = `
    <div class="modal" style="max-width:460px;">
      <h2 class="modal-title">${t('notif.title')}</h2>

      <div class="notif-section">
        <div class="notif-section-title">General</div>
        <label class="notif-toggle-row">
          <div>
            <div class="notif-label">${t('notif.browser')}</div>
            <div class="notif-sub">${t('notif.browser_sub')}</div>
          </div>
          <div class="toggle ${s.browserEnabled ? 'on' : ''}" id="toggle-browser"></div>
        </label>
        <label class="notif-toggle-row">
          <div>
            <div class="notif-label">${t('notif.sound')}</div>
            <div class="notif-sub">${t('notif.sound_sub')}</div>
          </div>
          <div class="toggle ${s.soundEnabled ? 'on' : ''}" id="toggle-sound"></div>
        </label>
        <div class="notif-toggle-row">
          <div>
            <div class="notif-label">${t('notif.volume')}</div>
          </div>
          <input type="range" min="0" max="1" step="0.1" value="${s.volume || 0.5}" id="volume-slider" style="width:120px;" />
        </div>
      </div>

      <div class="notif-section">
        <div class="notif-section-title">${t('notif.push')}</div>
        <label class="notif-toggle-row">
          <div>
            <div class="notif-label">Push notifications</div>
            <div class="notif-sub">${t('notif.push_sub')}</div>
          </div>
          <button class="btn btn-secondary" id="push-subscribe-btn" style="font-size:12px;padding:6px 14px;flex-shrink:0;">
            ${'Notification' in window && Notification.permission === 'granted' ? t('notif.push_enabled') : t('notif.push_enable')}
          </button>
        </label>
      </div>

      <div class="notif-section">
        <div class="notif-section-title">${t('notif.sounds_title')}</div>
        <div class="notif-sound-row">
          <div class="notif-label">${t('notif.new_message')}</div>
          <div style="display:flex;gap:8px;align-items:center;">
            <button class="btn btn-secondary notif-preview-btn" data-type="message">▶ Preview</button>
            <label class="btn btn-secondary" style="cursor:pointer;">
              📎 Upload
              <input type="file" accept="audio/*" style="display:none" data-type="message" class="sound-upload" />
            </label>
            ${localStorage.getItem('nx_sound_message') ? '<button class="btn btn-secondary notif-clear-btn" data-type="message">✕ Clear</button>' : ''}
          </div>
        </div>
        <div class="notif-sound-row">
          <div class="notif-label">${t('notif.mention')}</div>
          <div style="display:flex;gap:8px;align-items:center;">
            <button class="btn btn-secondary notif-preview-btn" data-type="mention">▶ Preview</button>
            <label class="btn btn-secondary" style="cursor:pointer;">
              📎 Upload
              <input type="file" accept="audio/*" style="display:none" data-type="mention" class="sound-upload" />
            </label>
            ${localStorage.getItem('nx_sound_mention') ? '<button class="btn btn-secondary notif-clear-btn" data-type="mention">✕ Clear</button>' : ''}
          </div>
        </div>
        <div class="notif-sound-row">
          <div class="notif-label">${t('notif.dm')}</div>
          <div style="display:flex;gap:8px;align-items:center;">
            <button class="btn btn-secondary notif-preview-btn" data-type="dm">▶ Preview</button>
            <label class="btn btn-secondary" style="cursor:pointer;">
              📎 Upload
              <input type="file" accept="audio/*" style="display:none" data-type="dm" class="sound-upload" />
            </label>
            ${localStorage.getItem('nx_sound_dm') ? '<button class="btn btn-secondary notif-clear-btn" data-type="dm">✕ Clear</button>' : ''}
          </div>
        </div>
        <div class="notif-sound-row">
          <div class="notif-label">${t('notif.ringtone')}</div>
          <div style="display:flex;gap:8px;align-items:center;">
            <label class="btn btn-secondary" style="cursor:pointer;">
              📎 Upload
              <input type="file" accept="audio/*" style="display:none" data-type="ringtone" class="sound-upload" />
            </label>
            ${localStorage.getItem('nx_sound_ringtone') ? '<button class="btn btn-secondary notif-clear-btn" data-type="ringtone">✕ Clear</button>' : ''}
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn btn-secondary" id="notif-close">${t('btn.close')}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  // Close
  overlay.querySelector('#notif-close')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  // Browser toggle
  overlay.querySelector('#toggle-browser')?.addEventListener('click', async (e) => {
    const el = e.currentTarget;
    if (!s.browserEnabled) {
      const granted = await requestPermission();
      if (!granted) { alert('Please allow notifications in your browser settings'); return; }
    }
    s.browserEnabled = !s.browserEnabled;
    el.classList.toggle('on', s.browserEnabled);
    saveSettings(s);
  });

  // Sound toggle
  overlay.querySelector('#toggle-sound')?.addEventListener('click', (e) => {
    s.soundEnabled = !s.soundEnabled;
    e.currentTarget.classList.toggle('on', s.soundEnabled);
    saveSettings(s);
  });

  // Volume
  overlay.querySelector('#volume-slider')?.addEventListener('input', (e) => {
    s.volume = parseFloat(e.target.value);
    saveSettings(s);
  });

  // Preview buttons
  overlay.querySelectorAll('.notif-preview-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('nx:preview-sound', { detail: { type: btn.dataset.type } }));
    });
  });

  // Upload custom sound
  overlay.querySelectorAll('.sound-upload').forEach(input => {
    input.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 1024 * 1024) { alert('Sound file must be under 1MB'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        localStorage.setItem('nx_sound_' + input.dataset.type, ev.target.result);
        showNotifSettings(getSettings, saveSettings, requestPermission);
      };
      reader.readAsDataURL(file);
    });
  });

  // Clear custom sound
  overlay.querySelectorAll('.notif-clear-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      localStorage.removeItem('nx_sound_' + btn.dataset.type);
      showNotifSettings(getSettings, saveSettings, requestPermission);
    });
  });
}

/* ──────────────────────────────────────────────
   Pins panel
─────────────────────────────────────────────── */
export function showPinsPanel(messages, onJump, onUnpin) {
  closePinsPanel();

  const panel = document.createElement('div');
  panel.id = 'pins-panel';
  panel.className = 'search-panel';

  panel.innerHTML = `
    <div class="search-panel-header">
      <span class="search-panel-title">📌 Pinned Messages</span>
      <button class="icon-btn" id="pins-panel-close">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="search-panel-results" id="pins-panel-list">
      ${messages.length === 0 ? '<div class="search-panel-empty">No pinned messages</div>' : ''}
    </div>
  `;

  document.body.appendChild(panel);
  document.getElementById('pins-panel-close')?.addEventListener('click', closePinsPanel);

  const list = document.getElementById('pins-panel-list');
  for (const msg of messages) {
    const item = document.createElement('div');
    item.className = 'search-result-item';
    item.style.cssText = 'display:flex;align-items:flex-start;gap:8px;justify-content:space-between;';
    const name = msg.senderId.split(':')[0].replace('@', '');
    const time = new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    item.innerHTML = `
      <div style="flex:1;cursor:pointer;">
        <div class="search-result-meta">
          <span class="search-result-sender">${escHtml(name)}</span>
          <span class="search-result-time">${time}</span>
        </div>
        <div class="search-result-body">${escHtml(msg.body.slice(0, 100))}</div>
      </div>
      <button class="btn btn-secondary" style="font-size:11px;padding:3px 8px;flex-shrink:0;" data-unpin="${escHtml(msg.id)}">${t('msg.unpin')}</button>
    `;
    item.querySelector('div')?.addEventListener('click', () => onJump(msg.id));
    item.querySelector('[data-unpin]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      onUnpin(msg.id);
      item.remove();
      if (!list.children.length) list.innerHTML = '<div class="search-panel-empty">No pinned messages</div>';
    });
    list.appendChild(item);
  }
}

export function closePinsPanel() {
  document.getElementById('pins-panel')?.remove();
}

export function updatePinsBadge(count) {
  const badge = document.getElementById('pins-badge');
  if (!badge) return;
  if (count > 0) {
    badge.textContent = count;
    badge.style.display = 'flex';
  } else {
    badge.style.display = 'none';
  }
}

/* ──────────────────────────────────────────────
   Read by popup
─────────────────────────────────────────────── */

/* ──────────────────────────────────────────────
   Theme Picker
─────────────────────────────────────────────── */
const THEMES = [
  { id: 'dark',           name: 'Dark',           dark: true,  color: '#6c3ef4', bg: '#0f0f13' },
  { id: 'light',          name: 'Light',          dark: false, color: '#6c3ef4', bg: '#f8f8fc' },
  { id: 'midnight',       name: 'Midnight',       dark: true,  color: '#7c3aed', bg: '#000000' },
  { id: 'midnight-light', name: 'Midnight Light', dark: false, color: '#7c3aed', bg: '#fafafa' },
  { id: 'ocean',          name: 'Ocean',          dark: true,  color: '#0ea5e9', bg: '#0a0f1e' },
  { id: 'ocean-light',    name: 'Ocean Light',    dark: false, color: '#0ea5e9', bg: '#f0f9ff' },
  { id: 'forest',         name: 'Forest',         dark: true,  color: '#22c55e', bg: '#0a120a' },
  { id: 'forest-light',   name: 'Forest Light',   dark: false, color: '#22c55e', bg: '#f0fdf4' },
  { id: 'sunset',         name: 'Sunset',         dark: true,  color: '#f97316', bg: '#150a0a' },
  { id: 'sunset-light',   name: 'Sunset Light',   dark: false, color: '#f97316', bg: '#fff7ed' },
  { id: 'rose',           name: 'Rose',           dark: true,  color: '#ec4899', bg: '#150a10' },
  { id: 'rose-light',     name: 'Rose Light',     dark: false, color: '#ec4899', bg: '#fdf2f8' },
];

import { t, getLocale, getLocaleNames, setLocale, getSupportedLocales } from './i18n.js';

export function applyTheme(themeId) {
  document.body.setAttribute('data-theme', themeId);
  localStorage.setItem('nx_theme', themeId);
}

export function loadSavedTheme() {
  const saved = localStorage.getItem('nx_theme') || 'dark';
  applyTheme(saved);
}


/* ──────────────────────────────────────────────
   Scheduled Messages UI
─────────────────────────────────────────────── */
export function showScheduleModal(roomId, messageText, replyTo, onScheduled) {
  document.getElementById('schedule-modal')?.remove();

  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const localIso = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours()+1)}:${pad(now.getMinutes())}`;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'schedule-modal';
  overlay.innerHTML = `
    <div class="modal" style="max-width:440px;">
      <h2 class="modal-title">${t('schedule.title')}</h2>
      <div class="modal-body">
        <div style="background:var(--bg-elevated);border:1px solid var(--border);border-radius:var(--radius-md);padding:10px 12px;margin-bottom:16px;font-size:13px;color:var(--text-secondary);max-height:80px;overflow:hidden;text-overflow:ellipsis;">
          ${escHtml((messageText||'').slice(0, 200))}${(messageText||'').length > 200 ? '…' : ''}
        </div>
        <label style="font-size:12px;color:var(--text-secondary);font-weight:500;display:block;margin-bottom:6px;">${t('schedule.send_at')}</label>
        <input type="datetime-local" id="sched-datetime" class="form-input" value="${localIso}" style="width:100%;margin-bottom:12px;" />
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:6px;font-weight:500;">${t('schedule.quick')}</div>
        <div class="schedule-time-presets">
          <button class="schedule-preset" data-mins="30">${t('schedule.30min')}</button>
          <button class="schedule-preset" data-mins="60">${t('schedule.1h')}</button>
          <button class="schedule-preset" data-mins="180">${t('schedule.3h')}</button>
          <button class="schedule-preset" data-mins="1440">${t('schedule.tomorrow')}</button>
          <button class="schedule-preset" data-mins="10080">${t('schedule.next_week')}</button>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="sched-cancel">${t('btn.cancel')}</button>
        <button class="btn btn-primary" id="sched-confirm">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          Schedule Send
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  // Preset buttons
  overlay.querySelectorAll('.schedule-preset').forEach(btn => {
    btn.addEventListener('click', () => {
      const mins = parseInt(btn.dataset.mins);
      const d = new Date(Date.now() + mins * 60000);
      const pad = n => String(n).padStart(2, '0');
      document.getElementById('sched-datetime').value =
        `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    });
  });

  document.getElementById('sched-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  document.getElementById('sched-confirm')?.addEventListener('click', async () => {
    const dtVal = document.getElementById('sched-datetime')?.value;
    if (!dtVal) return;
    const scheduledAt = new Date(dtVal).toISOString();
    if (new Date(dtVal) <= new Date()) {
      showToast('Please choose a future time', 'error');
      return;
    }
    overlay.remove();
    onScheduled?.(scheduledAt);
  });
}

export async function showScheduledList(getJwt, getRoomName) {
  document.getElementById('scheduled-list-modal')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'scheduled-list-modal';
  const nowFmt = new Date().toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  overlay.innerHTML = `
    <div class="modal" style="max-width:500px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
        <h2 class="modal-title" style="margin:0;">⏰ Scheduled Messages</h2>
        <div style="font-size:11px;color:var(--text-muted);background:var(--bg-elevated);padding:4px 10px;border-radius:999px;border:1px solid var(--border);">
          Now: ${nowFmt}
        </div>
      </div>
      <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px;">${t('schedule.list_desc')}</p>
      <div id="sched-list-body" style="min-height:80px;display:flex;align-items:center;justify-content:center;">
        <div class="spinner"></div>
      </div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="sched-list-close">${t('btn.close')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  document.getElementById('sched-list-close')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  try {
    const jwt = await getJwt();
    const res = await fetch('/api/scheduled/list', {
      headers: { 'Authorization': `Bearer ${jwt}` }
    });
    const data = await res.json();
    const body = document.getElementById('sched-list-body');
    if (!body) return;

    if (!data.messages?.length) {
      body.innerHTML = '<p style="color:var(--text-muted);font-size:13px;text-align:center;padding:24px;">No scheduled messages</p>';
      return;
    }

    body.innerHTML = '<div class="scheduled-list" id="sched-items"></div>';
    const list = document.getElementById('sched-items');

    const renderItems = (msgs) => {
      const now = new Date();
      list.innerHTML = '';

      msgs.forEach(msg => {
        const d = new Date(msg.scheduled_at);
        const fmt = d.toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
        const roomName = getRoomName?.(msg.room_id) || msg.room_id;

        // Time remaining
        const diffMs = d - now;
        let timeLeft = '';
        if (diffMs > 0) {
          const diffMins = Math.floor(diffMs / 60000);
          const diffHrs  = Math.floor(diffMins / 60);
          const diffDays = Math.floor(diffHrs / 24);
          if (diffDays > 0) timeLeft = `in ${diffDays}d ${diffHrs % 24}h`;
          else if (diffHrs > 0) timeLeft = `in ${diffHrs}h ${diffMins % 60}m`;
          else timeLeft = `in ${diffMins}m`;
        } else {
          timeLeft = 'sending soon…';
        }

        const item = document.createElement('div');
        item.className = 'scheduled-item';
        item.style.cssText = `
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 12px 14px;
          display: flex; flex-direction: column; gap: 8px;
        `;
        item.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
            <div style="font-size:13px;color:var(--text-primary);line-height:1.4;flex:1;">
              ${escHtml(msg.message.slice(0, 150))}${msg.message.length > 150 ? '…' : ''}
            </div>
            <div style="
              font-size:10px;font-weight:600;padding:3px 8px;border-radius:999px;
              background:var(--accent-glow);color:var(--accent-text);
              white-space:nowrap;flex-shrink:0;
            ">${timeLeft}</div>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <div style="display:flex;flex-direction:column;gap:2px;">
              <div style="font-size:11px;color:var(--accent-text);font-weight:500;">
                📅 ${fmt}
              </div>
              <div style="font-size:11px;color:var(--text-muted);">
                💬 ${escHtml(roomName)}
              </div>
            </div>
            <div style="display:flex;gap:6px;">
              <button class="sched-cancel-btn" data-id="${msg.id}" style="
                font-size:11px;padding:5px 10px;border-radius:6px;
                background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.3);
                color:#f87171;cursor:pointer;font-family:var(--font-sans);
                transition:all 0.15s;font-weight:500;
              ">✕ Cancel</button>
            </div>
          </div>
        `;
        list.appendChild(item);
      });

      // Cancel buttons
      list.querySelectorAll('.sched-cancel-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.id;
          btn.disabled = true; btn.textContent = '…';
          const jwt = await getJwt();
          const r = await fetch(`/api/scheduled/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${jwt}` }
          });
          if (r.ok) {
            const remaining = data.messages.filter(m => m.id != id);
            data.messages = remaining;
            if (!remaining.length) {
              list.innerHTML = '<p style="color:var(--text-muted);font-size:13px;text-align:center;padding:24px;">No scheduled messages</p>';
            } else {
              renderItems(remaining);
            }
            showToast('Message cancelled', 'success');
          } else {
            showToast('Failed to cancel', 'error');
            btn.disabled = false; btn.textContent = '✕ Cancel';
          }
        });
      });
    };

    renderItems(data.messages);
  } catch (err) {
    const body = document.getElementById('sched-list-body');
    if (body) body.innerHTML = '<p style="color:var(--text-muted);font-size:13px;text-align:center;padding:24px;">Failed to load</p>';
  }
}



/* ──────────────────────────────────────────────
   Slash Command Menu
─────────────────────────────────────────────── */
const SLASH_COMMANDS = [
  { cmd: '/poll',     icon: '📊', name: '/poll',     desc: 'Create a poll — /poll Question | Option1 | Option2' },
  { cmd: '/giphy',    icon: '🎬', name: '/giphy',    desc: 'Search and send a GIF — /giphy [search term]' },
  { cmd: '/remind',   icon: '⏰', name: '/remind',   desc: 'Set a reminder — /remind [time] [message]' },
  { cmd: '/status',   icon: '💬', name: '/status',   desc: 'Set your status — /status [message]' },
  { cmd: '/shrug',    icon: '🤷', name: '/shrug',    desc: 'Send ¯\_(ツ)_/¯' },
  { cmd: '/tableflip',icon: '😤', name: '/tableflip',desc: 'Send (╯°□°）╯︵ ┻━┻' },
  { cmd: '/me',       icon: '✨', name: '/me',       desc: 'Send an action message — /me [action]' },
  { cmd: '/clear',    icon: '🗑️', name: '/clear',    desc: 'Clear the message input' },
];


/* ──────────────────────────────────────────────
   @mention Autocomplete
─────────────────────────────────────────────── */
export function showMentionMenu(members, query, anchorEl, onSelect) {
  removeMentionMenu();

  const q = query.toLowerCase();
  const matches = members.filter(m => {
    const name = (m.name || m.userId || '').toLowerCase();
    const id = (m.userId || '').toLowerCase();
    return name.includes(q) || id.includes(q);
  }).slice(0, 8);

  if (!matches.length) return;

  const menu = document.createElement('div');
  menu.className = 'mention-menu';
  menu.id = 'mention-menu';

  menu.innerHTML = matches.map((m, i) => {
    const name = m.name || m.userId.split(':')[0].slice(1);
    const rawAvatar = m.getMxcAvatarUrl ? m.getMxcAvatarUrl() : null;
    const avatarUrl = rawAvatar && window._mxcToHttp ? window._mxcToHttp(rawAvatar) : null;
    const initials = name.slice(0, 2).toUpperCase();
    const color = ['#7c5cfc','#ef4444','#3b82f6','#10b981','#f59e0b'][
      name.charCodeAt(0) % 5
    ];
    const highlightedName = name.replace(
      new RegExp(escHtml(q), 'gi'),
      m => `<span class="mention-highlight">${m}</span>`
    );
    return `
      <div class="mention-item${i === 0 ? ' active' : ''}" data-idx="${i}" data-userid="${escHtml(m.userId)}" data-name="${escHtml(name)}">
        <div class="mention-avatar" style="background:${color}">
          ${avatarUrl
            ? `<img src="${escHtml(avatarUrl)}" alt="${escHtml(initials)}" />`
            : initials
          }
        </div>
        <div>
          <div class="mention-name">${highlightedName}</div>
          <div class="mention-id">${escHtml(m.userId)}</div>
        </div>
      </div>
    `;
  }).join('');

  document.body.appendChild(menu);

  // Position above input
  const rect = anchorEl.getBoundingClientRect();
  menu.style.bottom = `${window.innerHeight - rect.top + 4}px`;
  menu.style.left = `${rect.left}px`;
  menu.style.width = `${Math.min(rect.width, 320)}px`;

  menu.querySelectorAll('.mention-item').forEach(item => {
    item.addEventListener('mousedown', e => {
      e.preventDefault();
      onSelect?.(item.dataset.userid, item.dataset.name);
      removeMentionMenu();
    });
  });
}

export function removeMentionMenu() {
  document.getElementById('mention-menu')?.remove();
}

export function navigateMentionMenu(direction) {
  const menu = document.getElementById('mention-menu');
  if (!menu) return false;
  const items = [...menu.querySelectorAll('.mention-item')];
  const active = menu.querySelector('.mention-item.active');
  const idx = items.indexOf(active);
  items.forEach(i => i.classList.remove('active'));
  let next = direction === 'down' ? idx + 1 : idx - 1;
  if (next < 0) next = items.length - 1;
  if (next >= items.length) next = 0;
  items[next]?.classList.add('active');
  items[next]?.scrollIntoView({ block: 'nearest' });
  return true;
}

export function getActiveMention() {
  const active = document.querySelector('#mention-menu .mention-item.active');
  return active ? { userId: active.dataset.userid, name: active.dataset.name } : null;
}


export function showSlashMenu(query, anchorEl, onSelect) {
  removeSlashMenu();
  const matches = query === '/'
    ? SLASH_COMMANDS
    : SLASH_COMMANDS.filter(c => c.cmd.startsWith(query.toLowerCase()));

  if (!matches.length) return;

  const menu = document.createElement('div');
  menu.className = 'slash-menu';
  menu.id = 'slash-menu';
  menu.innerHTML = `
    <div class="slash-menu-header">⚡ Commands</div>
    ${matches.map((c, i) => `
      <div class="slash-item${i === 0 ? ' active' : ''}" data-cmd="${escHtml(c.cmd)}">
        <div class="slash-item-icon">${c.icon}</div>
        <div>
          <div class="slash-item-name">${escHtml(c.name)}</div>
          <div class="slash-item-desc">${escHtml(c.desc)}</div>
        </div>
      </div>
    `).join('')}
  `;

  // Append to body, position just above input bar
  document.body.appendChild(menu);

  const inputArea = document.querySelector('.message-input-area');
  if (!inputArea) return;

  const rect = inputArea.getBoundingClientRect();
  menu.style.cssText = `
    position: fixed;
    bottom: ${window.innerHeight - rect.top + 4}px;
    left: ${rect.left + 8}px;
    width: ${Math.min(rect.width - 16, 400)}px;
    max-width: calc(100vw - 32px);
    z-index: 500;
  `;

  menu.querySelectorAll('.slash-item').forEach(item => {
    item.addEventListener('mousedown', (e) => {
      e.preventDefault(); // prevent textarea blur
      onSelect?.(item.dataset.cmd);
      removeSlashMenu();
    });
  });
}

export function removeSlashMenu() {
  document.getElementById('slash-menu')?.remove();
}

export function navigateSlashMenu(direction) {
  const menu = document.getElementById('slash-menu');
  if (!menu) return false;
  const items = [...menu.querySelectorAll('.slash-item')];
  const active = menu.querySelector('.slash-item.active');
  const idx = items.indexOf(active);
  items.forEach(i => i.classList.remove('active'));
  let next = direction === 'down' ? idx + 1 : idx - 1;
  if (next < 0) next = items.length - 1;
  if (next >= items.length) next = 0;
  items[next]?.classList.add('active');
  items[next]?.scrollIntoView({ block: 'nearest' });
  return true;
}

export function getActiveSlashCommand() {
  return document.querySelector('#slash-menu .slash-item.active')?.dataset?.cmd;
}



/* ──────────────────────────────────────────────
   Language Picker
─────────────────────────────────────────────── */
export function showLanguagePicker() {
  document.getElementById('language-picker-modal')?.remove();

  const current = getLocale();
  const names = getLocaleNames();
  const locales = getSupportedLocales();

  const FLAGS = { en: '🇺🇸', es: '🇪🇸', fr: '🇫🇷', zh: '🇨🇳' };

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'language-picker-modal';
  overlay.innerHTML = `
    <div class="modal" style="max-width:340px;">
      <h2 class="modal-title">🌐 Language</h2>
      <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px;">Choose your preferred language</p>
      <div style="display:flex;flex-direction:column;gap:6px;margin-bottom:16px;">
        ${locales.map(loc => `
          <div class="slash-item${loc === current ? ' active' : ''}" data-locale="${loc}" style="border-radius:10px;border:2px solid ${loc === current ? 'var(--accent)' : 'transparent'};cursor:pointer;padding:10px 12px;">
            <div class="slash-item-icon" style="font-size:18px;background:none;">${FLAGS[loc] || '🌐'}</div>
            <div>
              <div class="slash-item-name">${names[loc]}</div>
              ${loc === current ? '<div class="slash-item-desc">Currently active</div>' : ''}
            </div>
            ${loc === current ? '<div style="margin-left:auto;color:var(--accent-text);font-size:12px;">✓</div>' : ''}
          </div>
        `).join('')}
      </div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="lang-close">${t('btn.close')}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  overlay.querySelectorAll('[data-locale]').forEach(item => {
    item.addEventListener('click', () => {
      setLocale(item.dataset.locale);
      overlay.remove();
      // Reload to apply all translations
      window.location.reload();
    });
  });

  document.getElementById('lang-close')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}



/* ──────────────────────────────────────────────
   Emoji Status
─────────────────────────────────────────────── */
const EMOJI_STATUS_PRESETS = [
  { emoji: '💼', text: 'In a meeting' },
  { emoji: '🎧', text: 'Focusing' },
  { emoji: '🏠', text: 'Working from home' },
  { emoji: '🌴', text: 'On vacation' },
  { emoji: '🤒', text: 'Out sick' },
  { emoji: '🚀', text: 'Shipping it' },
  { emoji: '📚', text: 'Learning' },
  { emoji: '☕', text: 'On a break' },
  { emoji: '🚗', text: 'Commuting' },
  { emoji: '🔕', text: 'Do not disturb' },
  { emoji: '👋', text: 'Available' },
  { emoji: '🎯', text: 'On a deadline' },
];

export function showEmojiStatusModal(onSet) {
  document.getElementById('emoji-status-modal')?.remove();

  const current = JSON.parse(localStorage.getItem('nx_emoji_status') || '{}');

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'emoji-status-modal';
  overlay.innerHTML = `
    <div class="modal" style="max-width:400px;">
      <h2 class="modal-title">💬 Emoji Status</h2>
      <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px;">Set a status to let your team know what you're up to.</p>

      <div style="display:flex;gap:8px;margin-bottom:16px;">
        <button id="emoji-status-emoji-btn" style="
          width:48px;height:48px;border-radius:12px;border:1px solid var(--border);
          background:var(--bg-elevated);font-size:24px;cursor:pointer;flex-shrink:0;
          display:flex;align-items:center;justify-content:center;
        ">${current.emoji || '💬'}</button>
        <input id="emoji-status-text-input" class="form-input" type="text"
          placeholder="What's your status?"
          value="${escHtml(current.text || '')}"
          maxlength="80"
          style="flex:1;" />
      </div>

      <div style="font-size:11px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">Suggestions</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:16px;">
        ${EMOJI_STATUS_PRESETS.map(p => `
          <div class="slash-item emoji-status-preset" data-emoji="${p.emoji}" data-text="${escHtml(p.text)}" style="border-radius:8px;cursor:pointer;padding:8px 10px;">
            <span style="font-size:16px;">${p.emoji}</span>
            <span style="font-size:12px;color:var(--text-secondary);">${escHtml(p.text)}</span>
          </div>
        `).join('')}
      </div>

      <div class="modal-actions">
        <button class="btn btn-secondary" id="emoji-status-clear">Clear status</button>
        <button class="btn btn-secondary" id="emoji-status-cancel">Cancel</button>
        <button class="btn btn-primary" id="emoji-status-save">Save</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  let selectedEmoji = current.emoji || '💬';

  // Emoji button — simple emoji picker inline
  const emojiBtn = document.getElementById('emoji-status-emoji-btn');
  emojiBtn?.addEventListener('click', () => {
    const emojis = ['💬','💼','🎧','🏠','🌴','🤒','🚀','📚','☕','🚗','🔕','👋','🎯',
      '🔥','⚡','🎉','😊','🤔','😴','🍕','🎮','📱','💪','🧠','✈️','🌍','🎵','📝'];
    const picker = document.createElement('div');
    picker.style.cssText = 'position:absolute;background:var(--bg-elevated);border:1px solid var(--border);border-radius:12px;padding:8px;display:flex;flex-wrap:wrap;gap:4px;width:220px;z-index:600;box-shadow:var(--shadow-lg);';
    emojis.forEach(e => {
      const span = document.createElement('span');
      span.textContent = e;
      span.style.cssText = 'font-size:20px;cursor:pointer;padding:4px;border-radius:6px;';
      span.addEventListener('mouseenter', () => span.style.background = 'var(--bg-hover)');
      span.addEventListener('mouseleave', () => span.style.background = '');
      span.addEventListener('click', () => {
        selectedEmoji = e;
        emojiBtn.textContent = e;
        picker.remove();
      });
      picker.appendChild(span);
    });
    emojiBtn.parentElement?.appendChild(picker);
    setTimeout(() => document.addEventListener('click', () => picker.remove(), { once: true }), 10);
  });

  // Preset click
  overlay.querySelectorAll('.emoji-status-preset').forEach(item => {
    item.addEventListener('click', () => {
      selectedEmoji = item.dataset.emoji;
      emojiBtn.textContent = selectedEmoji;
      document.getElementById('emoji-status-text-input').value = item.dataset.text;
    });
  });

  // Save
  document.getElementById('emoji-status-save')?.addEventListener('click', () => {
    const text = document.getElementById('emoji-status-text-input')?.value?.trim();
    const status = { emoji: selectedEmoji, text: text || '' };
    localStorage.setItem('nx_emoji_status', JSON.stringify(status));
    onSet?.(status);
    overlay.remove();
    _updateEmojiStatusDisplay(status);
  });

  // Clear
  document.getElementById('emoji-status-clear')?.addEventListener('click', () => {
    localStorage.removeItem('nx_emoji_status');
    onSet?.(null);
    overlay.remove();
    _updateEmojiStatusDisplay(null);
  });

  document.getElementById('emoji-status-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

export function _updateEmojiStatusDisplay(status) {
  const icon = document.getElementById('current-emoji-status-icon');
  const text = document.getElementById('current-emoji-status-text');
  const sidebarStatus = document.getElementById('user-status-emoji');

  if (status?.text || status?.emoji) {
    if (icon) icon.textContent = status.emoji || '💬';
    if (text) text.textContent = status.text || 'Emoji status set';
    // Update sidebar user status area
    if (sidebarStatus) {
      sidebarStatus.textContent = `${status.emoji || ''} ${status.text || ''}`.trim();
    }
  } else {
    if (icon) icon.textContent = '💬';
    if (text) text.textContent = 'Set emoji status…';
    if (sidebarStatus) sidebarStatus.textContent = '';
  }
}

export function loadEmojiStatus() {
  const saved = localStorage.getItem('nx_emoji_status');
  if (saved) {
    try { _updateEmojiStatusDisplay(JSON.parse(saved)); } catch {}
  }
}



/* ──────────────────────────────────────────────
   Per-room Notification Settings Modal
─────────────────────────────────────────────── */
export function showRoomNotifSettings(roomId, roomName, currentSetting, onSave) {
  document.getElementById('room-notif-modal')?.remove();

  const OPTIONS = [
    {
      value: 'all',
      icon: '🔔',
      label: 'All messages',
      desc: 'Notify for every new message',
    },
    {
      value: 'mentions',
      icon: '🔕',
      label: 'Mentions only',
      desc: 'Only notify when @mentioned',
    },
    {
      value: 'mute',
      icon: '🚫',
      label: 'Mute',
      desc: 'Never notify for this room',
    },
  ];

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'room-notif-modal';
  overlay.innerHTML = `
    <div class="modal" style="max-width:380px;">
      <h2 class="modal-title">🔔 Notifications</h2>
      <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px;">
        Settings for <strong>${escHtml(roomName)}</strong>
      </p>
      <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:20px;">
        ${OPTIONS.map(opt => `
          <label class="slash-item" style="
            border-radius:10px;cursor:pointer;padding:12px;
            border:2px solid ${opt.value === currentSetting ? 'var(--accent)' : 'var(--border)'};
            transition:border-color 0.15s;
          ">
            <input type="radio" name="room-notif" value="${opt.value}"
              ${opt.value === currentSetting ? 'checked' : ''}
              style="display:none;" />
            <div class="slash-item-icon" style="font-size:18px;background:none;">${opt.icon}</div>
            <div>
              <div class="slash-item-name">${opt.label}</div>
              <div class="slash-item-desc">${opt.desc}</div>
            </div>
            ${opt.value === currentSetting ? '<div style="margin-left:auto;color:var(--accent-text);">✓</div>' : ''}
          </label>
        `).join('')}
      </div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="room-notif-cancel">Cancel</button>
        <button class="btn btn-primary" id="room-notif-save">Save</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  // Highlight selected on click
  overlay.querySelectorAll('label').forEach(label => {
    label.addEventListener('click', () => {
      overlay.querySelectorAll('label').forEach(l => {
        l.style.borderColor = 'var(--border)';
        l.querySelector('div[style*="margin-left:auto"]')?.remove();
      });
      label.style.borderColor = 'var(--accent)';
      const checkDiv = document.createElement('div');
      checkDiv.style.cssText = 'margin-left:auto;color:var(--accent-text);';
      checkDiv.textContent = '✓';
      label.appendChild(checkDiv);
    });
  });

  document.getElementById('room-notif-save')?.addEventListener('click', () => {
    const selected = overlay.querySelector('input[name="room-notif"]:checked')?.value || 'all';
    onSave?.(selected);
    overlay.remove();
  });

  document.getElementById('room-notif-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}


export function showThemePicker() {
  document.getElementById('theme-picker-modal')?.remove();
  const current = localStorage.getItem('nx_theme') || 'dark';
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'theme-picker-modal';
  overlay.innerHTML = `
    <div class="modal" style="max-width:500px;">
      <h2 class="modal-title">🎨 Choose Theme</h2>
      <p style="font-size:13px;color:var(--text-secondary);margin-bottom:16px;">12 themes — dark and light variants for every style.</p>
      <div id="theme-grid" style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:16px 0;"></div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="theme-close">${t('btn.close')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));
  const grid = document.getElementById('theme-grid');
  for (const theme of THEMES) {
    const isCurrent = theme.id === current;
    const card = document.createElement('div');
    card.style.cssText = `cursor:pointer;border-radius:12px;overflow:hidden;border:2px solid ${isCurrent ? theme.color : 'transparent'};transition:all 0.15s;box-shadow:${isCurrent ? '0 0 0 3px ' + theme.color + '33' : 'none'};`;
    card.innerHTML = `
      <div style="background:${theme.bg};height:56px;display:flex;flex-direction:column;padding:8px;gap:3px;position:relative;">
        <div style="position:absolute;left:0;top:0;bottom:0;width:24px;background:${theme.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};"></div>
        <div style="margin-left:28px;height:7px;background:${theme.color};border-radius:4px;width:60%;opacity:0.9;"></div>
        <div style="margin-left:28px;height:7px;background:${theme.dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};border-radius:4px;width:80%;"></div>
        <div style="margin-left:28px;height:7px;background:${theme.color};border-radius:4px;width:45%;opacity:0.5;"></div>
        ${isCurrent ? `<div style="position:absolute;top:4px;right:4px;width:14px;height:14px;border-radius:50%;background:${theme.color};display:flex;align-items:center;justify-content:center;font-size:9px;color:white;line-height:14px;text-align:center;">✓</div>` : ''}
      </div>
      <div style="background:${theme.dark ? '#1a1a1a' : '#efefef'};padding:5px 8px;font-size:11px;font-weight:600;color:${theme.dark ? '#ccc' : '#333'};display:flex;align-items:center;gap:5px;">
        <span style="width:7px;height:7px;border-radius:50%;background:${theme.color};flex-shrink:0;display:inline-block;"></span>
        ${theme.name}
      </div>
    `;
    card.addEventListener('mouseenter', () => { if (!isCurrent) card.style.border = `2px solid ${theme.color}66`; });
    card.addEventListener('mouseleave', () => { if (!isCurrent) card.style.border = '2px solid transparent'; });
    card.addEventListener('click', () => { applyTheme(theme.id); overlay.remove(); showThemePicker(); });
    grid.appendChild(card);
  }
  document.getElementById('theme-close')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}


export function showReadByPopup(users, anchorEl) {
  document.getElementById('read-by-popup')?.remove();
  if (!users.length) return;

  const popup = document.createElement('div');
  popup.id = 'read-by-popup';
  popup.className = 'read-by-popup';

  popup.innerHTML = `
    <div class="read-by-title">Read by</div>
    ${users.map(u => `
      <div class="read-by-item">
        <div class="read-by-avatar" style="background:${getAvatarColor(u.displayName)}">
          ${getInitials(u.displayName)}
        </div>
        <span class="read-by-name">${escHtml(u.displayName)}</span>
        <span class="read-by-time">${new Date(u.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    `).join('')}
  `;

  // Position near the tick element
  document.body.appendChild(popup);
  const rect = anchorEl.getBoundingClientRect();
  const popupRect = popup.getBoundingClientRect();
  popup.style.top = (rect.top - popupRect.height - 8) + 'px';
  popup.style.left = Math.min(rect.left, window.innerWidth - popupRect.width - 8) + 'px';

  // Load real avatars
  users.forEach((u, i) => {
    if (!u.avatarUrl) return;
    const url = mxcToHttp(u.avatarUrl);
    if (!url) return;
    const avatarEl = popup.querySelectorAll('.read-by-avatar')[i];
    if (!avatarEl) return;
    const img = document.createElement('img');
    img.src = url;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:50%;';
    img.onload = () => { avatarEl.innerHTML = ''; avatarEl.appendChild(img); };
  });

  setTimeout(() => {
    document.addEventListener('click', () => popup.remove(), { once: true });
  }, 10);
}





/* ──────────────────────────────────────────────
   Thread Panel
─────────────────────────────────────────────── */
let _threadPanelRootId = null;

export function openThreadPanel(event, room, myUserId, onSendReply) {
  closeThreadPanel();

  const rootEventId = event.getId();
  _threadPanelRootId = rootEventId;
  const content = event.getContent();
  const senderName = getDisplayName(event.getSender());
  const rootBody = content?.body || '';

  const panel = document.createElement('div');
  panel.id = 'thread-panel';
  panel.className = 'thread-panel';

  panel.innerHTML = `
    <div class="thread-panel-header">
      <div class="thread-panel-title">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        Thread
      </div>
      <div style="display:flex;align-items:center;gap:6px;">
        <button class="icon-btn" id="thread-summarise-btn" title="${t('thread.summarise')}" style="font-size:11px;font-weight:600;color:var(--accent-text);width:auto;padding:4px 8px;border-radius:6px;background:var(--accent-glow);">
          ✨ Summary
        </button>
        <button class="icon-btn" id="thread-panel-close">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>
    <div class="thread-panel-messages" id="thread-panel-messages">
      <div class="thread-original">
        <div class="thread-original-sender">${escHtml(senderName)}</div>
        <div class="thread-original-body">${escHtml(rootBody)}</div>
      </div>
      <div class="thread-divider" id="thread-divider">${t('thread.replies')}</div>
      <div id="thread-replies-list"></div>
    </div>
    <div class="thread-input-area">
      <div class="thread-input-wrap">
        <textarea class="thread-textarea" id="thread-input"
          placeholder="${t('thread.reply_placeholder')}" rows="1"></textarea>
        <button class="thread-send-btn" id="thread-send-btn" disabled>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
      <div style="font-size:11px;color:var(--text-muted);margin-top:4px;padding-left:2px;">
        ${t('thread.hint')}
      </div>
    </div>
  `;

  document.body.appendChild(panel);

  // Render existing replies
  _renderThreadReplies(room, rootEventId);

  // Close
  document.getElementById('thread-panel-close')?.addEventListener('click', closeThreadPanel);
  document.getElementById('thread-panel-close')?.addEventListener('click', closeThreadPanel);

  // Summarise thread with AI
  document.getElementById('thread-summarise-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('thread-summarise-btn');
    if (!btn) return;
    btn.textContent = '⏳…';
    btn.disabled = true;
    document.getElementById('thread-summary-box')?.remove();

    // Collect thread messages
    const original = document.querySelector('.thread-original-body')?.textContent?.trim() || '';
    const replies = [...document.querySelectorAll('#thread-replies-list .thread-reply-body')]
      .map(r => r.textContent?.trim()).filter(Boolean);
    const allTexts = [original, ...replies].filter(Boolean);

    if (allTexts.length < 2) {
      showToast(t('thread.not_enough'), 'error');
      btn.textContent = t('thread.summarise'); btn.disabled = false;
      return;
    }

    try {
      const jwt = localStorage.getItem('nx_nexai_token');
      // Get sender names too
      const senderEls = [...document.querySelectorAll('#thread-replies-list .thread-reply-name')];
      const now = new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'});
      const messages = [
        { senderName: document.querySelector('.thread-original-sender')?.textContent?.trim() || 'Author', content: original, timestamp: now },
        ...replies.map((body, i) => ({
          senderName: senderEls[i]?.textContent?.trim() || `User ${i+1}`,
          content: body,
          timestamp: document.querySelectorAll('#thread-replies-list .thread-reply-time')[i]?.textContent?.trim() || now
        }))
      ].filter(m => m.content);

      const res = await fetch('/api/nexai/summarise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
        body: JSON.stringify({ messages, channelName: 'thread' })
      });
      const data = await res.json();
      const summary = data.summary || data.error || 'Could not summarise';

      const box = document.createElement('div');
      box.id = 'thread-summary-box';
      box.style.cssText = 'margin:8px 12px 4px;background:var(--accent-glow);border:1px solid rgba(124,92,252,0.3);border-radius:var(--radius-md);padding:10px 12px;font-size:13px;color:var(--text-primary);line-height:1.5;position:relative;';
      box.innerHTML = `
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:var(--accent-text);margin-bottom:6px;">✨ AI Summary</div>
        <div>${escHtml(summary)}</div>
        <button onclick="this.closest('#thread-summary-box').remove()" style="position:absolute;top:6px;right:8px;background:none;border:none;color:var(--text-muted);cursor:pointer;font-size:14px;">✕</button>
      `;
      const divider = document.getElementById('thread-divider');
      const msgs = document.getElementById('thread-panel-messages');
      if (divider) msgs.insertBefore(box, divider);
      else msgs.prepend(box);
    } catch (err) {
      showToast('Summarisation failed', 'error');
    } finally {
      btn.textContent = t('thread.summarise'); btn.disabled = false;
    }
  });

  // Input
  const textarea = document.getElementById('thread-input');
  const sendBtn  = document.getElementById('thread-send-btn');

  textarea?.addEventListener('input', () => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';
    sendBtn.disabled = !textarea.value.trim();
  });

  textarea?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  });

  sendBtn?.addEventListener('click', doSend);

  function doSend() {
    const text = textarea?.value?.trim();
    if (!text) return;
    textarea.value = '';
    sendBtn.disabled = true;
    textarea.style.height = 'auto';
    onSendReply(rootEventId, text);
  }

  setTimeout(() => textarea?.focus(), 100);
}

function _renderThreadReplies(room, rootEventId) {
  const list = document.getElementById('thread-replies-list');
  if (!list || !room) return;

  const replies = room.getLiveTimeline().getEvents().filter(e => {
    if (e.getType() !== 'm.room.message') return false;
    if (e.isRedacted?.()) return false;
    const rel = e.getContent()?.['m.relates_to'];
    return rel?.rel_type === 'm.thread' && rel?.event_id === rootEventId;
  });

  list.innerHTML = '';

  if (!replies.length) {
    list.innerHTML = '<div style="text-align:center;padding:20px;font-size:12px;color:var(--text-muted);">No replies yet. Be the first!</div>';
    return;
  }

  for (const reply of replies) {
    const sender = reply.getSender();
    const name   = getDisplayName(sender);
    const body   = reply.getContent()?.body || '';
    const ts     = reply.getTs();
    const time   = new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const row = document.createElement('div');
    row.className = 'thread-reply';
    row.dataset.eventId = reply.getId();

    const avatarColor = _getAvatarColorForThread(name);
    const initials = name.slice(0, 2).toUpperCase();

    row.innerHTML = `
      <div class="thread-reply-avatar" style="background:${avatarColor}">${escHtml(initials)}</div>
      <div class="thread-reply-content">
        <div class="thread-reply-meta">
          <span class="thread-reply-name">${escHtml(name)}</span>
          <span class="thread-reply-time">${time}</span>
        </div>
        <div class="thread-reply-body">${escHtml(body)}</div>
      </div>
    `;
    list.appendChild(row);

    // Load avatar
    getUserAvatarUrl(sender).then(mxc => {
      if (!mxc) return;
      const url = mxcToHttp(mxc);
      const av = row.querySelector('.thread-reply-avatar');
      if (!av) return;
      const img = document.createElement('img');
      img.src = url;
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:50%;';
      img.onload = () => { av.innerHTML = ''; av.appendChild(img); };
    }).catch(() => {});
  }

  // Scroll to bottom
  const msgs = document.getElementById('thread-panel-messages');
  if (msgs) msgs.scrollTo({ top: msgs.scrollHeight, behavior: 'smooth' });
}

function _getAvatarColorForThread(name) {
  const COLORS = ['#6c3ef4','#0ea5e9','#10b981','#f59e0b','#ef4444','#ec4899','#8b5cf6','#14b8a6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return COLORS[Math.abs(hash) % COLORS.length];
}

export function updateThreadPanel(room, rootEventId) {
  if (!document.getElementById('thread-panel')) return;
  if (_threadPanelRootId !== rootEventId) return;
  _renderThreadReplies(room, rootEventId);
}

export function closeThreadPanel() {
  document.getElementById('thread-panel')?.remove();
  _threadPanelRootId = null;
}

export function isThreadPanelOpen() {
  return !!document.getElementById('thread-panel');
}

/* ──────────────────────────────────────────────
   Poll Creator Modal
─────────────────────────────────────────────── */
export function showPollCreator(onSubmit) {
  document.getElementById('poll-creator-modal')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'poll-creator-modal';

  overlay.innerHTML = `
    <div class="modal" style="max-width:420px;">
      <h2 class="modal-title">📊 Create Poll</h2>
      <div class="form-group">
        <label class="form-label">Question</label>
        <input type="text" class="form-input" id="poll-question" placeholder="${t('poll.question_placeholder')}" maxlength="200" />
      </div>
      <div class="form-group">
        <label class="form-label">Type</label>
        <div class="poll-type-row">
          <button class="poll-type-btn active" id="poll-type-single" data-type="single">${t('poll.single')}</button>
          <button class="poll-type-btn" id="poll-type-multi" data-type="multi">${t('poll.multi')}</button>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Options</label>
        <div class="poll-modal-options" id="poll-options">
          <div class="poll-option-row"><input class="poll-option-input" placeholder="Option 1" maxlength="100" /><button class="poll-option-remove" style="visibility:hidden;">✕</button></div>
          <div class="poll-option-row"><input class="poll-option-input" placeholder="Option 2" maxlength="100" /><button class="poll-option-remove" style="visibility:hidden;">✕</button></div>
        </div>
        <button class="poll-add-option" id="poll-add-option">+ Add option</button>
      </div>
      <div id="poll-error" class="auth-error"></div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="poll-cancel">${t('btn.cancel')}</button>
        <button class="btn btn-primary" id="poll-submit">${t('poll.create')}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  let pollType = 'single';

  // Type toggle
  overlay.querySelectorAll('.poll-type-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      overlay.querySelectorAll('.poll-type-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      pollType = btn.dataset.type;
    });
  });

  // Add option
  const optionsEl = document.getElementById('poll-options');
  document.getElementById('poll-add-option')?.addEventListener('click', () => {
    const rows = optionsEl.querySelectorAll('.poll-option-row');
    if (rows.length >= 20) return;
    const row = document.createElement('div');
    row.className = 'poll-option-row';
    row.innerHTML = `<input class="poll-option-input" placeholder="Option ${rows.length + 1}" maxlength="100" /><button class="poll-option-remove">✕</button>`;
    row.querySelector('.poll-option-remove')?.addEventListener('click', () => {
      row.remove();
      updateRemoveBtns();
    });
    optionsEl.appendChild(row);
    updateRemoveBtns();
    row.querySelector('input')?.focus();
  });

  function updateRemoveBtns() {
    const rows = optionsEl.querySelectorAll('.poll-option-row');
    rows.forEach((r, i) => {
      const btn = r.querySelector('.poll-option-remove');
      if (btn) btn.style.visibility = rows.length > 2 ? 'visible' : 'hidden';
    });
  }

  // Wire existing remove buttons
  optionsEl.querySelectorAll('.poll-option-remove').forEach(btn => {
    btn.addEventListener('click', () => { btn.closest('.poll-option-row').remove(); updateRemoveBtns(); });
  });

  // Cancel
  const close = () => { overlay.classList.remove('visible'); setTimeout(() => overlay.remove(), 200); };
  document.getElementById('poll-cancel')?.addEventListener('click', close);
  overlay.addEventListener('click', e => { if (e.target === overlay) close(); });

  // Submit
  document.getElementById('poll-submit')?.addEventListener('click', () => {
    const question = document.getElementById('poll-question')?.value.trim();
    const options = [...optionsEl.querySelectorAll('.poll-option-input')]
      .map(i => i.value.trim()).filter(Boolean);
    const errEl = document.getElementById('poll-error');

    if (!question) { errEl.textContent = 'Please enter a question'; errEl.classList.add('visible'); return; }
    if (options.length < 2) { errEl.textContent = 'Please add at least 2 options'; errEl.classList.add('visible'); return; }

    const kind = pollType === 'multi'
      ? 'org.matrix.msc3381.poll.undisclosed'
      : 'org.matrix.msc3381.poll.disclosed';

    close();
    onSubmit(question, options, kind, pollType);
  });

  setTimeout(() => document.getElementById('poll-question')?.focus(), 100);
}

/* ──────────────────────────────────────────────
   Poll Card Renderer
─────────────────────────────────────────────── */
export function buildPollCard(event, room, myUserId, onVote, onEnd) {
  const content = event.getContent();
  const poll = content?.['org.matrix.msc3381.poll'];
  if (!poll) return null;

  const question = poll.question?.['org.matrix.msc1767.text'] || 'Poll';
  const answers  = poll.answers || [];
  const isMulti  = poll.max_selections > 1;
  const pollEventId = event.getId();
  const isOwner  = event.getSender() === myUserId;

  const card = document.createElement('div');
  card.className = 'poll-card';
  card.dataset.pollId = pollEventId;

  function render(tally, myVote, totalVotes, isClosed) {
    const maxVotes = Math.max(1, ...Object.values(tally));
    card.innerHTML = `
      <div class="poll-card-header">
        <div class="poll-card-icon">📊</div>
        <div>
          <div class="poll-card-question">${escHtml(question)}</div>
          <div class="poll-card-type">${isMulti ? 'Multiple choice' : 'Single choice'}${isClosed ? ' · Ended' : ''}</div>
        </div>
      </div>
      <div class="poll-options">
        ${answers.map(a => {
          const votes = tally[a.id] || 0;
          const pct   = totalVotes ? Math.round(votes / totalVotes * 100) : 0;
          const voted = myVote.includes(a.id);
          return `
            <div class="poll-option${voted ? ' voted' : ''}${isClosed ? ' closed' : ''}" data-answer-id="${escHtml(a.id)}">
              <div class="poll-option-bar" style="width:${isClosed || totalVotes ? pct : 0}%"></div>
              <div class="poll-option-content">
                <span class="poll-option-label">${escHtml(a['org.matrix.msc1767.text'] || '')}</span>
                <div class="poll-option-check">${voted ? '✓' : ''}</div>
              </div>
              ${(isClosed || totalVotes > 0) ? `<div class="poll-option-votes">${votes} vote${votes !== 1 ? 's' : ''} · ${pct}%</div>` : ''}
            </div>`;
        }).join('')}
      </div>
      <div class="poll-card-footer">
        <span>${totalVotes} vote${totalVotes !== 1 ? 's' : ''}</span>
        ${isClosed
          ? '<span class="poll-closed-badge">Closed</span>'
          : isOwner ? `<button class="poll-end-btn" id="poll-end-${escHtml(pollEventId)}">End poll</button>` : ''}
      </div>
    `;

    // Wire vote clicks
    if (!isClosed) {
      card.querySelectorAll('.poll-option').forEach(opt => {
        opt.addEventListener('click', () => {
          const answerId = opt.dataset.answerId;
          if (!answerId) return;
          if (isMulti) {
            // Toggle this answer in/out of current selection
            const current = [...card.querySelectorAll('.poll-option.voted')]
              .map(o => o.dataset.answerId);
            const idx = current.indexOf(answerId);
            if (idx >= 0) current.splice(idx, 1);
            else current.push(answerId);
            onVote(pollEventId, current);
          } else {
            // Single choice — just this answer
            onVote(pollEventId, [answerId]);
          }
        });
      });
    }

    // Wire end poll
    document.getElementById(`poll-end-${pollEventId}`)?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (confirm('End this poll? Voting will be closed.')) onEnd(pollEventId);
    });
  }

  // Initial render
  if (room) {
    const { tally, myVote, totalVotes, isClosed } = window._getPollResults
      ? window._getPollResults(room, pollEventId, answers)
      : { tally: {}, myVote: [], totalVotes: 0, isClosed: false };
    render(tally, myVote, totalVotes, isClosed);
  } else {
    render({}, [], 0, false);
  }

  return card;
}

/* ──────────────────────────────────────────────
   Starred Messages Panel
─────────────────────────────────────────────── */
export function showStarredPanel(roomId, onJump) {
  closeStarredPanel();

  // Collect starred from ALL rooms
  const allStarred = []; // { eventId, roomId, roomName, body, senderId, ts }
  const rooms = getRooms();

  for (const room of rooms) {
    const key = 'nx_starred_' + room.roomId;
    const ids = JSON.parse(localStorage.getItem(key) || '[]');
    if (!ids.length) continue;
    const timeline = room.getLiveTimeline().getEvents();
    const roomName = isDmRoom(room)
      ? (getDmPartner(room)?.name || room.name || room.roomId)
      : (room.name || room.roomId);
    for (const eventId of ids) {
      const event = timeline.find(e => e.getId() === eventId);
      allStarred.push({
        eventId,
        roomId: room.roomId,
        roomName,
        body: event?.getContent()?.body || '(not in timeline)',
        senderId: event?.getSender() || '',
        ts: event?.getTs() || 0,
      });
    }
  }

  // Sort by most recently starred (insertion order = most recent first)
  const panel = document.createElement('div');
  panel.id = 'starred-panel';
  panel.className = 'search-panel';
  panel.style.width = '340px';

  panel.innerHTML = `
    <div class="search-panel-header">
      <span class="search-panel-title">⭐ Starred Messages</span>
      <button class="icon-btn" id="starred-panel-close">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="search-panel-results" id="starred-panel-list">
      ${!allStarred.length ? '<div class="search-panel-empty">No starred messages yet.<br>Star messages using the ⋯ menu.</div>' : ''}
    </div>
  `;

  document.body.appendChild(panel);
  document.getElementById('starred-panel-close')?.addEventListener('click', closeStarredPanel);

  if (allStarred.length) {
    const list = document.getElementById('starred-panel-list');

    for (const entry of allStarred) {
      const item = document.createElement('div');
      item.className = 'search-result-item';
      item.style.cssText = 'display:flex;flex-direction:column;gap:4px;';

      const name = entry.senderId.split(':')[0].replace('@', '');
      const time = entry.ts
        ? new Date(entry.ts).toLocaleDateString([], { month:'short', day:'numeric' })
          + ' ' + new Date(entry.ts).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })
        : '';

      item.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:space-between;gap:6px;">
          <span style="font-size:11px;color:var(--accent-text);font-weight:600;">
            ${entry.roomId === roomId ? '' : '# ' + escHtml(entry.roomName)}
          </span>
          <button class="btn btn-secondary" style="font-size:10px;padding:2px 7px;flex-shrink:0;" data-unstar="${escHtml(entry.eventId)}" data-room="${escHtml(entry.roomId)}">${t('msg.unstar')}</button>
        </div>
        <div style="cursor:pointer;" class="starred-jump">
          <div class="search-result-meta">
            <span class="search-result-sender">${escHtml(name)}</span>
            <span class="search-result-time">${time}</span>
          </div>
          <div class="search-result-body">${escHtml((entry.body||'').slice(0,120))}${(entry.body||'').length>120?'…':''}</div>
        </div>
      `;

      item.querySelector('.starred-jump')?.addEventListener('click', () => {
        closeStarredPanel();
        onJump(entry.eventId, entry.roomId);
      });

      item.querySelector('[data-unstar]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const k = 'nx_starred_' + entry.roomId;
        const l = JSON.parse(localStorage.getItem(k) || '[]');
        const i = l.indexOf(entry.eventId);
        if (i >= 0) l.splice(i, 1);
        localStorage.setItem(k, JSON.stringify(l));
        item.remove();
        if (!document.querySelectorAll('#starred-panel-list .search-result-item').length) {
          document.getElementById('starred-panel-list').innerHTML =
            '<div class="search-panel-empty">No starred messages yet.</div>';
        }
      });

      list.appendChild(item);
    }
  }
}

export function closeStarredPanel() {
  document.getElementById('starred-panel')?.remove();
}


/* ──────────────────────────────────────────────
   Voice / Video Call Modal (Element Call)
─────────────────────────────────────────────── */
let _callTimer = null;
let _callSeconds = 0;

export function openCallModal(callUrl, roomName, avatarColor, initials, onEnd) {
  closeCallModal();

  // ── Overlay container ──────────────────────────────────────────────
  const overlay = document.createElement('div');
  overlay.id = 'call-modal';
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:1000',
    'display:flex',
    'flex-direction:column',
    'background:var(--bg-base)',
    'animation:callFadeIn 0.2s ease',
  ].join(';');

  // Inject animation keyframe once
  if (!document.getElementById('call-anim-style')) {
    const style = document.createElement('style');
    style.id = 'call-anim-style';
    style.textContent = `
      @keyframes callFadeIn {
        from { opacity:0; transform:scale(0.98); }
        to   { opacity:1; transform:scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  // ── Top control bar ────────────────────────────────────────────────
  const bar = document.createElement('div');
  bar.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:space-between',
    'padding:10px 16px',
    'background:var(--bg-surface)',
    'border-bottom:1px solid var(--border)',
    'flex-shrink:0',
    'z-index:1',
  ].join(';');

  bar.innerHTML = `
    <div style="display:flex;align-items:center;gap:10px;">
      <div style="
        width:32px;height:32px;border-radius:50%;
        background:${avatarColor};
        display:flex;align-items:center;justify-content:center;
        color:#fff;font-weight:700;font-size:13px;
        flex-shrink:0;
      ">${escHtml(initials)}</div>
      <div>
        <div style="font-size:13px;font-weight:600;color:var(--text-primary);">${escHtml(roomName)}</div>
        <div style="font-size:11px;color:var(--text-secondary);display:flex;align-items:center;gap:5px;">
          <span style="width:6px;height:6px;background:#22c55e;border-radius:50%;display:inline-block;animation:pulse 1.5s ease infinite;"></span>
          <span id="call-duration-label">Call in progress</span>
        </div>
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:8px;">
      <button id="call-popout-btn" title="Open in new tab" style="
        background:var(--bg-input);border:1px solid var(--border);
        border-radius:8px;color:var(--text-secondary);
        padding:6px 12px;font-size:12px;cursor:pointer;
        display:flex;align-items:center;gap:6px;
      ">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
          <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
        Pop out
      </button>
      <button id="call-end-btn" style="
        background:#dc2626;border:none;border-radius:8px;
        color:white;padding:7px 16px;font-size:13px;font-weight:600;
        cursor:pointer;display:flex;align-items:center;gap:6px;
        transition:background 0.15s;
      ">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
          <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
        </svg>
        End call
      </button>
    </div>
  `;

  // ── Element Call iframe ────────────────────────────────────────────
  const iframe = document.createElement('iframe');
  iframe.src = callUrl;
  iframe.style.cssText = [
    'flex:1',
    'border:none',
    'width:100%',
    'min-height:0',
    'background:var(--bg-base)',
  ].join(';');
  // Required permissions for camera, mic, screen share
  iframe.allow = 'camera; microphone; display-capture; autoplay; clipboard-write; encrypted-media; fullscreen';
  iframe.allowFullscreen = true;
  iframe.setAttribute('allow', 'camera; microphone; display-capture; autoplay; clipboard-write; encrypted-media; fullscreen');

  overlay.appendChild(bar);
  overlay.appendChild(iframe);
  document.body.appendChild(overlay);

  // ── Call duration timer ────────────────────────────────────────────
  _callSeconds = 0;
  _callTimer = setInterval(() => {
    _callSeconds++;
    const m = Math.floor(_callSeconds / 60);
    const s = _callSeconds % 60;
    const el = document.getElementById('call-duration-label');
    if (el) el.textContent = `${m}:${String(s).padStart(2, '0')} · Call in progress`;
  }, 1000);

  // ── Button handlers ────────────────────────────────────────────────
  document.getElementById('call-end-btn')?.addEventListener('click', () => {
    closeCallModal();
    onEnd?.();
  });

  document.getElementById('call-popout-btn')?.addEventListener('click', () => {
    // Strip embed param and open standalone
    const standaloneUrl = callUrl.replace('embed=true', 'embed=false');
    window.open(standaloneUrl, '_blank', 'noopener,noreferrer');
  });
}

export function closeCallModal() {
  document.getElementById('call-modal')?.remove();
  if (_callTimer) { clearInterval(_callTimer); _callTimer = null; }
  _callSeconds = 0;
}

export function showIncomingCallBanner(callerName, avatarColor, initials, callType, onAccept, onDecline) {
  document.getElementById('incoming-call-banner')?.remove();

  const banner = document.createElement('div');
  banner.id = 'incoming-call-banner';
  banner.className = 'incoming-call-banner';

  const typeLabel = callType === 'video' ? '📹 Incoming video call' : '📞 Incoming voice call';

  banner.innerHTML = `
    <div class="incoming-call-info">
      <div class="incoming-call-avatar" style="background:${avatarColor}">${escHtml(initials)}</div>
      <div class="incoming-call-text">
        <div class="incoming-call-name">${escHtml(callerName)}</div>
        <div class="incoming-call-type">${typeLabel}</div>
      </div>
    </div>
    <div class="incoming-call-btns">
      <button class="call-accept-btn" id="call-accept-btn">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
        Accept
      </button>
      <button class="call-decline-btn" id="call-decline-btn">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" transform="rotate(135 12 12)"/></svg>
        Decline
      </button>
    </div>
  `;

  document.body.appendChild(banner);

  // Auto-dismiss after 30s
  const autoDecline = setTimeout(() => {
    banner.remove();
    onDecline?.();
  }, 30000);

  document.getElementById('call-accept-btn')?.addEventListener('click', () => {
    clearTimeout(autoDecline);
    banner.remove();
    onAccept?.();
  });

  document.getElementById('call-decline-btn')?.addEventListener('click', () => {
    clearTimeout(autoDecline);
    banner.remove();
    onDecline?.();
  });
}

export function showCallPip(roomName, onReopen, onEnd) {
  document.getElementById('call-pip')?.remove();

  const pip = document.createElement('div');
  pip.id = 'call-pip';
  pip.className = 'call-pip';
  pip.innerHTML = `
    <div class="call-pip-dot"></div>
    <div>
      <div class="call-pip-text">${escHtml(roomName)}</div>
      <div class="call-pip-time" id="pip-time">0:00</div>
    </div>
    <button class="call-pip-end" id="pip-end-btn" title="End call">✕</button>
  `;

  document.body.appendChild(pip);
  pip.addEventListener('click', (e) => {
    if (e.target.closest('#pip-end-btn')) return;
    onReopen?.();
  });
  document.getElementById('pip-end-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    pip.remove();
    onEnd?.();
  });
}

/* ──────────────────────────────────────────────
   Voice Message Recorder — fully self-contained
─────────────────────────────────────────────── */
export function initVoiceRecorder(onSend) {
  const btn = document.getElementById('voice-btn');
  if (!btn) return;

  // State
  let mediaRecorder = null;
  let chunks = [];
  let recordingStart = 0;
  let timerInterval = null;
  let waveInterval = null;
  let analyser = null;
  let audioCtx = null;
  let isRecording = false;
  let isHolding = false;
  let holdTimer = null;

  const fmt = (s) => Math.floor(s/60) + ':' + String(s%60).padStart(2,'0');

  async function startRecording() {
    if (isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      isRecording = true;
      btn.classList.add('recording');

      // Analyser for waveform
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const src = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';

      mediaRecorder = new MediaRecorder(stream, { mimeType });
      chunks = [];
      recordingStart = Date.now();

      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mediaRecorder.start(100);

      // Show recorder UI
      showRecorderBar(stream);

      // Auto-stop at 10 min
      setTimeout(() => { if (isRecording) stopRecording(false); }, 10 * 60 * 1000);

    } catch (err) {
      console.error('[voice] mic error:', err);
      showToast('Microphone access denied', 'error');
      isRecording = false;
      btn.classList.remove('recording');
    }
  }

  function stopRecording(cancelled) {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') return;
    clearInterval(timerInterval);
    clearInterval(waveInterval);
    timerInterval = null; waveInterval = null;
    btn.classList.remove('recording');
    document.getElementById('voice-recorder-bar')?.remove();
    isRecording = false;

    const durationMs = Date.now() - recordingStart;
    const savedChunks = [...chunks];
    const savedMime = mediaRecorder.mimeType || 'audio/ogg';
    chunks = [];

    mediaRecorder.onstop = () => {
      if (audioCtx) { try { audioCtx.close(); } catch(_){} audioCtx = null; }
      if (cancelled || savedChunks.length === 0 || durationMs < 500) return;
      const blob = new Blob(savedChunks, { type: savedMime });
      if (blob.size > 0) onSend(blob, durationMs);
    };
    mediaRecorder.stop();
    mediaRecorder.stream?.getTracks().forEach(t => t.stop());
  }

  function showRecorderBar(stream) {
    document.getElementById('voice-recorder-bar')?.remove();
    const inputArea = document.querySelector('.message-input-area');
    if (!inputArea) return;

    const bar = document.createElement('div');
    bar.id = 'voice-recorder-bar';
    bar.className = 'voice-recorder-bar';
    const barsHtml = Array.from({length:32},(_,i)=>`<div class="voice-waveform-bar" id="vwb-${i}" style="height:3px"></div>`).join('');
    bar.innerHTML = `
      <button class="voice-recorder-cancel" id="voice-cancel-btn" title="Cancel">✕</button>
      <span class="voice-recorder-timer" id="voice-timer">0:00</span>
      <div class="voice-waveform">${barsHtml}</div>
      <button class="voice-recorder-send" id="voice-send-btn" title="Send">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
      </button>`;
    inputArea.insertBefore(bar, inputArea.firstChild);

    timerInterval = setInterval(() => {
      const el = document.getElementById('voice-timer');
      if (el) el.textContent = fmt(Math.floor((Date.now()-recordingStart)/1000));
    }, 500);

    const dataArr = new Uint8Array(analyser?.frequencyBinCount || 32);
    waveInterval = setInterval(() => {
      if (analyser) analyser.getByteFrequencyData(dataArr);
      for (let i=0;i<32;i++) {
        const b = document.getElementById(`vwb-${i}`);
        if (!b) continue;
        const v = analyser ? (dataArr[Math.floor(i * dataArr.length/32)] || 0) : Math.random()*180;
        b.style.height = Math.max(3, Math.min(24, (v/255)*24+3)) + 'px';
      }
    }, 80);

    document.getElementById('voice-cancel-btn')?.addEventListener('click', () => stopRecording(true));
    document.getElementById('voice-send-btn')?.addEventListener('click', () => stopRecording(false));
  }

  // Click to toggle
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isHolding) return;
    if (!isRecording) startRecording();
    else stopRecording(false);
  });

  // Hold to record
  btn.addEventListener('mousedown', () => {
    holdTimer = setTimeout(() => { isHolding = true; startRecording(); }, 300);
  });
  const endHold = () => {
    clearTimeout(holdTimer);
    if (isHolding) { isHolding = false; stopRecording(false); }
  };
  btn.addEventListener('mouseup', endHold);
  btn.addEventListener('mouseleave', endHold);

  // Touch
  btn.addEventListener('touchstart', (e) => {
    e.preventDefault();
    holdTimer = setTimeout(() => { isHolding = true; startRecording(); }, 300);
  }, { passive: false });
  btn.addEventListener('touchend', (e) => {
    e.preventDefault();
    clearTimeout(holdTimer);
    if (isHolding) { isHolding = false; stopRecording(false); }
    else if (!isRecording) startRecording();
    else stopRecording(false);
  }, { passive: false });
}

/* ──────────────────────────────────────────────
   Voice Message Player (renders in chat)
─────────────────────────────────────────────── */
export function buildVoicePlayer(src, durationMs) {
  const totalSec = Math.round((durationMs || 0) / 1000);
  const fmt = (s) => Math.floor(s/60) + ':' + String(s%60).padStart(2,'0');

  const wrap = document.createElement('div');
  wrap.className = 'voice-msg';

  const BARS = 40;
  const barsHtml = Array.from({length: BARS}, (_,i) =>
    `<div class="voice-msg-bar" data-i="${i}" style="height:${4 + Math.sin(i*0.4)*6 + Math.random()*8}px"></div>`
  ).join('');

  wrap.innerHTML = `
    <button class="voice-msg-play" id="vmp-play">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
    </button>
    <div class="voice-msg-waveform" id="vmp-wave">${barsHtml}</div>
    <span class="voice-msg-duration" id="vmp-dur">${fmt(totalSec)}</span>
    <button class="voice-msg-speed" id="vmp-speed" title="Playback speed">1×</button>
  `;

  const audio = new Audio(src);
  const playBtn = wrap.querySelector('#vmp-play');
  const durEl = wrap.querySelector('#vmp-dur');
  const bars = wrap.querySelectorAll('.voice-msg-bar');
  let playing = false;
  let progressTimer = null;

  const updateProgress = () => {
    if (!audio.duration) return;
    const pct = audio.currentTime / audio.duration;
    const played = Math.floor(pct * BARS);
    bars.forEach((b, i) => b.classList.toggle('played', i < played));
    durEl.textContent = fmt(Math.round(audio.currentTime));
  };

  const pause = () => {
    audio.pause();
    playing = false;
    clearInterval(progressTimer);
    playBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>';
  };

  const play = () => {
    // Pause any other playing voice messages
    document.querySelectorAll('.voice-msg-play.playing').forEach(b => b.click());
    audio.play().catch(() => {});
    playing = true;
    playBtn.classList.add('playing');
    playBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
    progressTimer = setInterval(updateProgress, 100);
  };

  playBtn.addEventListener('click', () => playing ? pause() : play());

  audio.addEventListener('ended', () => {
    pause();
    playBtn.classList.remove('playing');
    bars.forEach(b => b.classList.remove('played'));
    durEl.textContent = fmt(totalSec);
  });

  // Click waveform to seek
  wrap.querySelector('#vmp-wave')?.addEventListener('click', (e) => {
    if (!audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * audio.duration;
    updateProgress();
  });

  // Speed toggle: 1× → 1.5× → 2× → 3× → 1×
  const SPEEDS = [1, 1.5, 2, 3];
  let speedIdx = 0;
  const speedBtn = wrap.querySelector('#vmp-speed');
  speedBtn?.addEventListener('click', () => {
    speedIdx = (speedIdx + 1) % SPEEDS.length;
    const speed = SPEEDS[speedIdx];
    audio.playbackRate = speed;
    speedBtn.textContent = speed === 1 ? '1×' : `${speed}×`;
    speedBtn.style.color = speed > 1 ? 'var(--accent-text)' : 'var(--text-muted)';
    speedBtn.style.borderColor = speed > 1 ? 'var(--accent)' : 'var(--border)';
  });

  return wrap;
}

/* ──────────────────────────────────────────────
   GIF & Sticker Picker
─────────────────────────────────────────────── */
let _customStickers = JSON.parse(localStorage.getItem('nx_custom_stickers') || '[]');

// All available packs in the store
const STICKER_STORE = [
  { id: 'memes-gif',    name: 'Memes',        emoji: '😂', count: 12, preview: '/api/sticker-proxy?url=' + encodeURIComponent('https://s3.getstickerpack.com/storage/uploads/sticker-pack/memes-gif/sticker_1.gif') },
  { id: 'pepe-the-frog', name: 'Pepe',        emoji: '🐸', count: 8,  preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/pepe-the-frog/sticker_1.png' },
  { id: 'doge',          name: 'Doge',        emoji: '🐕', count: 6,  preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/doge/sticker_1.png' },
  { id: 'pusheen',       name: 'Pusheen',     emoji: '🐱', count: 10, preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/pusheen/sticker_1.png' },
  { id: 'blobs-gg',      name: 'Blobs',       emoji: '🫧', count: 12, preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/blobs-gg/sticker_1.png' },
  { id: 'among-us-gif',  name: 'Among Us',    emoji: '🚀', count: 8,  preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/among-us-gif/sticker_1.gif' },
  { id: 'baby-yoda',     name: 'Baby Yoda',   emoji: '💚', count: 6,  preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/baby-yoda/sticker_1.png' },
  { id: 'minions',       name: 'Minions',     emoji: '💛', count: 8,  preview: 'https://s3.getstickerpack.com/storage/uploads/sticker-pack/minions/sticker_1.png' },
];

const STICKER_PACK_STICKERS = (packId, count) =>
  Array.from({ length: count }, (_, i) =>
    `/api/sticker-proxy?url=${encodeURIComponent('https://s3.getstickerpack.com/storage/uploads/sticker-pack/' + packId + '/sticker_' + (i + 1) + '.' + (packId.includes('gif') ? 'gif' : 'png'))}`
  );

// User's installed packs (stored in localStorage)
let _installedPacks = JSON.parse(localStorage.getItem('nx_sticker_packs') || '["memes-gif"]');

const getInstalledPackStickers = (packId) => {
  const pack = STICKER_STORE.find(p => p.id === packId);
  if (!pack) return [];
  return STICKER_PACK_STICKERS(packId, pack.count);
};

export function showGifStickerPicker(onSelect) {
  document.getElementById('gif-picker')?.remove();

  const picker = document.createElement('div');
  picker.id = 'gif-picker';
  picker.className = 'gif-picker';

  picker.innerHTML = `
    <div class="gif-picker-header">
      <div class="gif-picker-tabs">
        <button class="gif-tab active" data-tab="gif">GIF</button>
        <button class="gif-tab" data-tab="stickers">Stickers</button>
      </div>
      <button class="icon-btn" id="gif-picker-close" style="width:24px;height:24px;">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="gif-search-wrap">
      <input class="gif-search-input" id="gif-search-input" placeholder="Search GIFs…" type="text" />
    </div>
    <div class="gif-grid" id="gif-grid">
      <div class="gif-loading">Loading…</div>
    </div>
    <div class="gif-sticker-panel" id="sticker-panel" style="display:none;">
      <div class="sticker-pack-tabs" id="sticker-pack-tabs"></div>
      <div class="gif-grid" id="sticker-grid"></div>
      <div class="sticker-footer">
        <label class="sticker-add-btn">
          📎 Custom
          <input type="file" accept="image/*" multiple style="display:none;" id="sticker-upload-input" />
        </label>
        <button class="sticker-add-btn" id="sticker-store-btn">🏪 Get packs</button>
      </div>
    </div>
  `;

  // Position above input area
  const inputArea = document.querySelector('.message-input-area');
  if (inputArea) {
    const rect = inputArea.getBoundingClientRect();
    picker.style.bottom = (window.innerHeight - rect.top + 8) + 'px';
    picker.style.left = '16px';
  }

  document.body.appendChild(picker);

  // Close
  document.getElementById('gif-picker-close')?.addEventListener('click', () => picker.remove());
  setTimeout(() => {
    document.addEventListener('click', (e) => {
      if (!picker.contains(e.target) && e.target.id !== 'gif-btn') picker.remove();
    }, { once: true });
  }, 100);

  // Tab switching
  picker.querySelectorAll('.gif-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      picker.querySelectorAll('.gif-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      const isGif = tab.dataset.tab === 'gif';
      picker.querySelector('.gif-search-wrap').style.display = isGif ? '' : 'none';
      document.getElementById('gif-grid').style.display = isGif ? '' : 'none';
      document.getElementById('sticker-panel').style.display = isGif ? 'none' : '';
      if (!isGif) renderStickers();
    });
  });

  // GIF search
  const searchInput = document.getElementById('gif-search-input');
  let searchTimer;
  const searchGifs = async (q) => {
    const grid = document.getElementById('gif-grid');
    if (!grid) return;
    grid.innerHTML = '<div class="gif-loading">Searching…</div>';
    try {
      const term = encodeURIComponent(q || 'trending');
      const res = await fetch(`https://api.giphy.com/v1/gifs/${q ? 'search' : 'trending'}?api_key=By7uZpcs45N3rje210XbOhrViLRW6791&q=${term}&limit=24&rating=g`);
      const data = await res.json();
      grid.innerHTML = '';
      (data.data || []).forEach(item => {
        const gif = item.images?.original?.url;
        const preview = item.images?.fixed_height?.url || gif;
        if (!gif) return;
        const img = document.createElement('img');
        img.src = preview;
        img.className = 'gif-item';
        img.loading = 'lazy';
        img.addEventListener('click', () => {
          picker.remove();
          onSelect(gif, 'gif');
        });
        grid.appendChild(img);
      });
      if (!grid.children.length) grid.innerHTML = '<div class="gif-loading">No results</div>';
    } catch (err) {
      const grid2 = document.getElementById('gif-grid');
      if (grid2) grid2.innerHTML = '<div class="gif-loading">Failed to load GIFs</div>';
    }
  };

  searchInput?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => searchGifs(searchInput.value.trim()), 400);
  });

  // Load trending on open
  searchGifs('');

  // Sticker upload
  // Wire store button
  document.getElementById('sticker-store-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    showStickerStore((packId) => {
      if (!_installedPacks.includes(packId)) {
        _installedPacks.push(packId);
        localStorage.setItem('nx_sticker_packs', JSON.stringify(_installedPacks));
      }
      renderStickers(packId);
    }, _installedPacks);
  });

  document.getElementById('sticker-upload-input')?.addEventListener('change', (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        _customStickers.push({ url: ev.target.result, name: file.name });
        localStorage.setItem('nx_custom_stickers', JSON.stringify(_customStickers));
        renderStickers();
      };
      reader.readAsDataURL(file);
    });
  });

  let _activePack = _installedPacks[0] || 'memes-gif';

  const renderStickers = (packName) => {
    if (packName) _activePack = packName;
    const grid = document.getElementById('sticker-grid');
    const tabsEl = document.getElementById('sticker-pack-tabs');
    if (!grid) return;

    // Render pack tabs
    if (tabsEl) {
      tabsEl.innerHTML = '';
      const allPacks = [..._installedPacks, 'Custom'];
      allPacks.forEach(name => {
        const tab = document.createElement('button');
        tab.className = 'sticker-pack-tab' + (name === _activePack ? ' active' : '');
        const pack = STICKER_STORE.find(p => p.id === name);
        tab.textContent = pack ? pack.emoji + ' ' + pack.name : name;
        tab.addEventListener('click', () => renderStickers(name));
        tabsEl.appendChild(tab);
      });
    }

    grid.innerHTML = '';

    if (_activePack === 'Custom') {
      if (!_customStickers.length) {
        grid.innerHTML = '<div class="gif-loading" style="width:100%;grid-column:1/-1;">No custom stickers yet</div>';
        return;
      }
      _customStickers.forEach((sticker, i) => {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'position:relative;display:inline-block;';
        const img = document.createElement('img');
        img.src = sticker.url;
        img.className = 'gif-item sticker-item';
        img.addEventListener('click', () => { picker.remove(); onSelect(sticker.url, 'sticker'); });
        const del = document.createElement('button');
        del.textContent = '×';
        del.style.cssText = 'position:absolute;top:2px;right:2px;width:16px;height:16px;background:rgba(0,0,0,0.6);color:white;border:none;border-radius:50%;cursor:pointer;font-size:10px;';
        del.addEventListener('click', (e) => {
          e.stopPropagation();
          _customStickers.splice(i, 1);
          localStorage.setItem('nx_custom_stickers', JSON.stringify(_customStickers));
          renderStickers('Custom');
        });
        wrap.appendChild(img);
        wrap.appendChild(del);
        grid.appendChild(wrap);
      });
      return;
    }

    const stickers = getInstalledPackStickers(_activePack);
    if (!stickers.length) {
      grid.innerHTML = '<div class="gif-loading" style="grid-column:1/-1">Pack not found</div>';
      return;
    }
    stickers.forEach(url => {
      const img = document.createElement('img');
      img.src = url;
      img.className = 'gif-item sticker-item';
      img.loading = 'lazy';
      img.onerror = () => img.style.display = 'none';
      img.addEventListener('click', () => { picker.remove(); onSelect(url, 'sticker'); });
      grid.appendChild(img);
    });
  };

  document.getElementById('gif-search-input')?.focus();
}

/* ──────────────────────────────────────────────
   Sticker Store
─────────────────────────────────────────────── */
function showStickerStore(onInstall, installedPacks) {
  document.getElementById('sticker-store')?.remove();

  const store = document.createElement('div');
  store.id = 'sticker-store';
  store.className = 'sticker-store';

  store.innerHTML = `
    <div class="sticker-store-header">
      <span style="font-size:14px;font-weight:600;">🏪 Sticker Store</span>
      <button class="icon-btn" id="sticker-store-close">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="sticker-store-grid" id="sticker-store-grid"></div>
  `;

  document.body.appendChild(store);
  document.getElementById('sticker-store-close')?.addEventListener('click', () => store.remove());

  const grid = document.getElementById('sticker-store-grid');
  STICKER_STORE.forEach(pack => {
    const isInstalled = installedPacks.includes(pack.id);
    const item = document.createElement('div');
    item.className = 'sticker-store-item';
    item.innerHTML = `
      <img src="${escHtml(pack.preview)}" class="sticker-store-preview" />
      <div class="sticker-store-info">
        <div class="sticker-store-name">${pack.emoji} ${escHtml(pack.name)}</div>
        <div class="sticker-store-count">${pack.count} stickers</div>
      </div>
      <button class="sticker-store-btn ${isInstalled ? 'installed' : ''}" data-pack="${escHtml(pack.id)}">
        ${isInstalled ? '✓ Added' : '+ Add'}
      </button>
    `;
    const btn = item.querySelector('.sticker-store-btn');
    btn?.addEventListener('click', () => {
      if (!installedPacks.includes(pack.id)) {
        onInstall(pack.id);
        btn.textContent = '✓ Added';
        btn.classList.add('installed');
        installedPacks.push(pack.id);
      }
    });
    grid.appendChild(item);
  });

  // Close on outside click
  setTimeout(() => {
    document.addEventListener('click', (e) => {
      if (!store.contains(e.target)) store.remove();
    }, { once: true });
  }, 100);
}
